import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ShoppingBag, Package, ShoppingCart, LogOut, Search, Plus, Bell, Facebook, Twitter, Linkedin, Instagram, Users, Camera, FileText, ChevronLeft, ChevronRight, AlertCircle, AlertTriangle, RefreshCw, Loader2, MapPin, Hammer, Send, Paperclip, X, XCircle, Upload, MessageSquare, ZoomIn, Sparkles, Wand2, CheckCircle, CheckCircle2, Check, TrendingUp, Clock, Activity, Building2, Calendar, DollarSign, Eye, Wrench, Maximize2, Square } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslations } from '../translations';
import { InvitationForm } from '../components/invitations/InvitationForm';
import { InvitationList } from '../components/invitations/InvitationList';
import { SubUserList } from '../components/invitations/SubUserList';
import { useInvitations } from '../hooks/useInvitations';
import { apiClient } from '../lib/apiClient';
import {
  getCart, addToCart as apiAddToCart, addLines as apiAddLines,
  removeFromCart as apiRemoveFromCart,
  updateCartItem as apiUpdateCartItem,
  clearCart as apiClearCart,
  acceptQuote, rejectQuote,
  listSupplierProducts, getSupplierProduct,
} from '../api/customerApi';
import ChatWidget from '../components/ChatWidget';
import CustomerCheckout from './CustomerCheckout';
import QuoteRequestModal from './QuoteRequestModal';
import CustomerOnboarding from '../components/CustomerOnboarding';
import OnboardingWizard from '../components/OnboardingWizard';
import ModularStandsCatalog from '../components/ModularStandsCatalog';
import { ModularStandDetailBody } from './ModularStandDetailPage';
import { compressImage } from '../utils/compressImage';
import BookingWidget from '../components/BookingWidget';
import NotificationBell from '../components/NotificationBell';
import { RichTextEditor } from '../components/RichTextEditor';
import StandLayoutVisualizer from '../components/StandLayoutVisualizer';
import OnboardingModal from '../components/OnboardingModal';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Cart Expiry Countdown ─────────────────────────────────────────────────────
const CartItemCountdown = ({ expiresAt, language }) => {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const diff = new Date(expiresAt) - new Date();
      setRemaining(diff > 0 ? diff : 0);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (remaining === null || !expiresAt) return null;

  if (remaining === 0) {
    return (
      <span className="text-[10px] font-semibold text-red-500 flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse inline-block" />
        {language === 'tr' ? 'Süresi doldu' : 'Expired'}
      </span>
    );
  }

  const totalSec = Math.floor(remaining / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;

  const pad = n => String(n).padStart(2, '0');
  const timeStr = h > 0
    ? `${h}:${pad(m)}:${pad(s)}`
    : `${pad(m)}:${pad(s)}`;

  // Color: red < 1h, amber < 3h, gray otherwise
  const color = h === 0
    ? 'text-red-500'
    : h < 3
      ? 'text-amber-500'
      : 'text-gray-400';

  const label = language === 'tr'
    ? (h > 0 ? `${h}s ${m}d ${s}sn kaldı` : m > 0 ? `${m}d ${s}sn kaldı` : `${s}sn kaldı`)
    : (h > 0 ? `${h}h ${m}m ${s}s left` : m > 0 ? `${m}m ${s}s left` : `${s}s left`);

  return (
    <span className={`text-[10px] font-mono font-semibold flex items-center gap-1 ${color}`}>
      <span className={`h-1.5 w-1.5 rounded-full inline-block ${h === 0 ? 'bg-red-400 animate-pulse' : h < 3 ? 'bg-amber-400 animate-pulse' : 'bg-gray-300'}`} />
      {label}
    </span>
  );
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const t = useTranslations(language);
  // ── Supplier Products (Live API) ──────────────────────────────────────────
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [productsFetched, setProductsFetched] = useState(false);
  // Phase 86C: centered modal shown when user clicks a locked "Customize Booth"
  // button (stand not yet paid). Renders at page level (outside TabsContent).
  const [showStandPaymentLockModal, setShowStandPaymentLockModal] = useState(false);
  // Phase 86E: organizer extra detail modal ("İncele" / "Preview" button)
  const [organizerDetail, setOrganizerDetail] = useState(null); // raw organizer product or null
  // Phase 86F: Set of organizer product_ids that belong to the modular package
  // associated with the customer's PAID stand. Populated only when
  // `hasAnyPaidStand` becomes true. Used to conditionally show the
  // "product is included in your package" banner in the preview modal.
  const [paidPackageProductIds, setPaidPackageProductIds] = useState(new Set());
  // ── Organizer Extras Catalog (/customer/organizer-products) ──────────────
  // Phase 86: Shown under the "Organization" chip in Browse Products tab.
  // These are organizer-curated extras (Rip Carpet, power strips, etc.) that
  // historically lived only inside modular-stand-package items.
  const [organizerExtras, setOrganizerExtras] = useState([]);
  // Phase 86: source filter chip on Browse Products: 'all' | 'organizer' | 'supplier'
  const [productSource, setProductSource] = useState('all');
  // Event config (loaded from backend settings)
  const [eventConfig, setEventConfig] = useState({ event_start: '2026-11-09', event_end: '2026-11-20', event_days: 12 });
  // Product detail state
  const [detailProduct, setDetailProduct] = useState(null); // full detail from API
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedPricingType, setSelectedPricingType] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null); // kept for compat (variant selector moved to BookingWidget)
  const [selectedLines, setSelectedLines] = useState([]); // from BookingWidget (array of line objects)
  const [addingToCart, setAddingToCart] = useState(false);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState(''); // Chairs, Sofas & Lounges
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetail, setShowProductDetail] = useState(false);
  const [lightboxOpen, setLightboxOpen]   = useState(false);
  const [lightboxIdx, setLightboxIdx]     = useState(0);
  const [lightboxImgs, setLightboxImgs]   = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [cart, setCart] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteActionLoading, setQuoteActionLoading] = useState(null);
  // Customize Booth (rich-text custom request attached to cart item)
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [customizeNotes, setCustomizeNotes] = useState('');
  const [customizeSubmitting, setCustomizeSubmitting] = useState(false);
  const [myLayout, setMyLayout] = useState(null);
  const [layoutLoading, setLayoutLoading] = useState(true);
  // Phase 86AZ.5 — Top-level state for the admin-bound modular package's
  // enriched payload (items + product details). Used by the "My Stand" tab
  // to render the package contents inline (chairs, tables, sockets, etc.).
  const [assignedModularPkg, setAssignedModularPkg] = useState(null);
  const [standUnreadCount, setStandUnreadCount] = useState(0); // unread msgs badge for Stand Design tab
  const [standServicesNewCount, setStandServicesNewCount] = useState(0); // unseen new service products
  // Stand Değişiklik Talebi
  const [changeRequests, setChangeRequests]   = useState([]);
  const [changeQuota, setChangeQuota]         = useState(null); // Phase 86N — { max_allowed, used, remaining, can_submit }
  const [showChangeForm, setShowChangeForm]   = useState(false);
  const [wizardStep, setWizardStep]           = useState(1); // Phase 86O — 1: pick categories, 2: fill details, 3: review & submit
  const [changeForm, setChangeForm]           = useState({ change_types: [], requested_area: '', requested_stand_type: '', requested_location: '', notes: '' });
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  const [changeMsg, setChangeMsg]             = useState('');
  const [remindingId, setRemindingId]         = useState(null);
  const [respondingId, setRespondingId]       = useState(null); // alternatif yanıtlama
  const [altDetailModal, setAltDetailModal]   = useState(null); // {alt, idx} | null — büyütülmüş kroki modalı

  // Alan Onayı & Sözleşme
  const [areaApprovalStatus, setAreaApprovalStatus] = useState(null); // null | 'approved'
  const [contractInfo, setContractInfo] = useState(null); // { contract_status, contract_signed_at, ... }
  const [areaApproving, setAreaApproving] = useState(false);
  const [approveAreaModalOpen, setApproveAreaModalOpen] = useState(false);
  
  // Onboarding Modal
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Profile state
  const [profileData, setProfileData] = useState(null);
  const [profileEdit, setProfileEdit] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');

  // Avatar state
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const avatarInputRef = React.useRef(null);

  // Kayıtlı kullanıcı bilgileri (ad, email, ülke)
  const getUserInfo = () => {
    try {
      const raw = localStorage.getItem('cop31_user');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  };

  // Zone theme — her kullanıcı tipi kendi rengiyle
  const userInfo = getUserInfo();
  const zoneTheme = (() => {
    const zone = userInfo.zone || 'blue_zone';
    const role = userInfo.role;
    if (role === 'supplier_admin') return {
      hex: '#D97706', bg: 'bg-amber-600', hover: 'hover:bg-amber-700',
      light: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600',
      ring: 'focus:ring-amber-300', label: 'Supplier Zone', spin: 'border-amber-600',
      gradient: 'from-amber-500 to-orange-600', badge: 'bg-amber-600',
    };
    if (zone === 'green_zone') return {
      hex: '#059669', bg: 'bg-emerald-600', hover: 'hover:bg-emerald-700',
      light: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600',
      ring: 'focus:ring-emerald-300', label: 'Green Zone', spin: 'border-emerald-600',
      gradient: 'from-emerald-500 to-teal-600', badge: 'bg-emerald-600',
    };
    // Default: Blue Zone
    return {
      hex: '#2563EB', bg: 'bg-blue-600', hover: 'hover:bg-blue-700',
      light: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600',
      ring: 'focus:ring-blue-300', label: 'Blue Zone', spin: 'border-blue-600',
      gradient: 'from-blue-600 to-indigo-700', badge: 'bg-blue-600',
    };
  })();

  // Country flags lookup
  const CFLAGS = {
    'türkiye': '🇹🇷', 'turkey': '🇹🇷',
    'belçika': '🇧🇪', 'belgium': '🇧🇪', 'belgique': '🇧🇪',
    'germany': '🇩🇪', 'almanya': '🇩🇪',
    'france': '🇫🇷', 'fransa': '🇫🇷',
    'united states': '🇺🇸', 'usa': '🇺🇸', 'abd': '🇺🇸',
    'united kingdom': '🇬🇧', 'uk': '🇬🇧', 'birleşik krallık': '🇬🇧',
    'italy': '🇮🇹', 'italya': '🇮🇹',
    'japan': '🇯🇵', 'japonya': '🇯🇵',
    'china': '🇨🇳', 'çin': '🇨🇳',
    'brazil': '🇧🇷', 'brezilya': '🇧🇷',
    'india': '🇮🇳', 'hindistan': '🇮🇳',
    'canada': '🇨🇦', 'kanada': '🇨🇦',
    'australia': '🇦🇺', 'avustralya': '🇦🇺',
    'spain': '🇪🇸', 'ispanya': '🇪🇸',
    'netherlands': '🇳🇱', 'hollanda': '🇳🇱',
    'sweden': '🇸🇪', 'isveç': '🇸🇪',
    'norway': '🇳🇴', 'norveç': '🇳🇴',
    'denmark': '🇩🇰', 'danimarka': '🇩🇰',
    'finland': '🇫🇮', 'finlandiya': '🇫🇮',
    'switzerland': '🇨🇭', 'isviçre': '🇨🇭',
    'austria': '🇦🇹', 'avusturya': '🇦🇹',
    'south africa': '🇿🇦', 'güney afrika': '🇿🇦',
    'kenya': '🇰🇪', 'egypt': '🇪🇬', 'mısır': '🇪🇬',
    'uae': '🇦🇪', 'birleşik arap emirlikleri': '🇦🇪',
    'saudi arabia': '🇸🇦', 'suudi arabistan': '🇸🇦',
    'qatar': '🇶🇦', 'katar': '🇶🇦',
    'pakistan': '🇵🇰', 'indonesia': '🇮🇩', 'endonezya': '🇮🇩',
    'mexico': '🇲🇽', 'meksika': '🇲🇽',
    'argentina': '🇦🇷', 'arjantin': '🇦🇷',
    'greece': '🇬🇷', 'yunanistan': '🇬🇷',
    'portugal': '🇵🇹', 'poland': '🇵🇱', 'polonya': '🇵🇱',
    'ukraine': '🇺🇦', 'ukrayna': '🇺🇦',
    'russia': '🇷🇺', 'rusya': '🇷🇺',
    'south korea': '🇰🇷', 'güney kore': '🇰🇷',
    'singapore': '🇸🇬', 'singapur': '🇸🇬',
    'azerbaijan': '🇦🇿', 'azerbaycan': '🇦🇿',
    'israel': '🇮🇱', 'iran': '🇮🇷',
    'nigeria': '🇳🇬', 'nijerya': '🇳🇬',
    'morocco': '🇲🇦', 'fas': '🇲🇦',
    'romania': '🇷🇴', 'romanya': '🇷🇴',
    'hungary': '🇭🇺', 'macaristan': '🇭🇺',
    'czech republic': '🇨🇿', 'czechia': '🇨🇿', 'çekya': '🇨🇿',
    'slovakia': '🇸🇰', 'slovakya': '🇸🇰',
    'croatia': '🇭🇷', 'hırvatistan': '🇭🇷',
    'serbia': '🇷🇸', 'sırbistan': '🇷🇸',
    'bulgaria': '🇧🇬', 'bulgaristan': '🇧🇬',
    'estonia': '🇪🇪', 'estonya': '🇪🇪',
    'latvia': '🇱🇻', 'letonya': '🇱🇻',
    'lithuania': '🇱🇹', 'litvanya': '🇱🇹',
    'luxembourg': '🇱🇺', 'lüksemburg': '🇱🇺',
    'ireland': '🇮🇪', 'irlanda': '🇮🇪',
    'new zealand': '🇳🇿', 'yeni zelanda': '🇳🇿',
    'colombia': '🇨🇴', 'kolombiya': '🇨🇴',
    'chile': '🇨🇱', 'şili': '🇨🇱',
    'peru': '🇵🇪', 'venezuela': '🇻🇪',
    'malaysia': '🇲🇾', 'malezya': '🇲🇾',
    'thailand': '🇹🇭', 'tayland': '🇹🇭',
    'vietnam': '🇻🇳', 'philippines': '🇵🇭', 'filipinler': '🇵🇭',
    'ethiopia': '🇪🇹', 'etiyopya': '🇪🇹',
    'tanzania': '🇹🇿', 'tanzanya': '🇹🇿',
    'ghana': '🇬🇭', 'senegal': '🇸🇳',
    'iraq': '🇮🇶', 'irak': '🇮🇶',
    'jordan': '🇯🇴', 'ürdün': '🇯🇴',
    'lebanon': '🇱🇧', 'lübnan': '🇱🇧',
    'kazakhstan': '🇰🇿', 'kazakistan': '🇰🇿',
    'uzbekistan': '🇺🇿', 'özbekistan': '🇺🇿',
    'georgia': '🇬🇪', 'gürcistan': '🇬🇪',
    'armenia': '🇦🇲', 'ermenistan': '🇦🇲',
  };
  const countryFlag = userInfo.country ? (CFLAGS[userInfo.country.toLowerCase()] || '🌍') : '🌍';

  // Event countdown
  const eventDate = new Date('2026-11-09T00:00:00');
  const daysUntil = Math.max(0, Math.ceil((eventDate - new Date()) / (1000 * 60 * 60 * 24)));

  // Invitation & sub-user management
  const invitationData = useInvitations();
  const [showInviteForm, setShowInviteForm] = useState(false);

  const { slug, section, standId } = useParams();

  // Onboarding
  const onboardingRefresh = useRef(null);
  // null = kontrol ediliyor (loading), true = wizard göster, false = dashboard göster
  const [showWizard, setShowWizard] = useState(null);

  // İlk girişte onboarding durumunu kontrol et — cevap gelene kadar null (loading)
  // Phase 86AZ.17 — Sub-users SHARE the tenant's profile (set up by the
  // main customer_admin during their own onboarding). They should never see
  // the OnboardingWizard. Skip the check entirely for sub_user role.
  useEffect(() => {
    const token = localStorage.getItem('cop31_token');
    if (!token) { setShowWizard(false); return; }
    try {
      const me = JSON.parse(localStorage.getItem('cop31_user') || '{}');
      if (me.role === 'sub_user') { setShowWizard(false); return; }
    } catch { /* fall through to API check */ }

    apiClient.get(`/customer/onboarding`).then(res => {
      const completed = res.data?.completed_steps || [];
      setShowWizard(!completed.includes('profile_complete'));
    }).catch(() => {
      // API hatası — wizard'ı göster
      setShowWizard(true);
    });
  }, []);

  const closeWizard = () => {
    setShowWizard(false);
    if (onboardingRefresh.current) onboardingRefresh.current();
    // Onboarding bitince/atlanınca kullanıcı default sekme olan dashboard'a gitsin —
    // yeni kullanıcı genel görünüm + sıradaki adımları orada görür. URL muhtemelen
    // login akışından gelen /layout idi; bu yüzden açıkça dashboard'a yönlendirelim.
    if (slug) navigate(`/musteri-admin/${slug}/dashboard`);
  };
  const completeOnboardingStep = useCallback(async (stepId) => {
    try {
      await apiClient.post(`/customer/onboarding/complete-step`, { step_id: stepId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('cop31_token')}` } });
      if (onboardingRefresh.current) onboardingRefresh.current();
    } catch { /* ignore */ }
  }, []);

  // URL'den aktif tab türetiliyor (useState yok — URL kaynağı)
  const validTabs = ['dashboard', 'layout', 'modular-stands', 'products', 'orders', 'design', 'profile', 'users'];
  const activeTab = validTabs.includes(section) ? section : 'dashboard';

  // Tab değişince URL'i güncelle + onboarding step tetikle (sadece "görüntüleme" adımları)
  const handleTabChange = (tab) => {
    navigate(`/musteri-admin/${slug}/${tab}`);
    if (tab === 'layout') completeOnboardingStep('layout_viewed');
    if (tab === 'products') completeOnboardingStep('products_explored');
    // cart_added → sepete ekleyince, first_order → sipariş verince, user_invited → davet gönderince
  };

  useEffect(() => {
    const token = localStorage.getItem('cop31_token');
    const userDataStr = localStorage.getItem('cop31_user');
    
    if (!token) {
      navigate('/giris');
      return;
    }
    
    // Ultra admin buraya giremez → direkt ultra-admin paneline
    if (userDataStr) {
      const userData = JSON.parse(userDataStr);
      if (userData.role === 'ultra_admin') {
        navigate('/giris');
        return;
      }
      // Slug kontrolü
      const expectedSlug = userData.tenant_slug || 'default';
      if (slug !== expectedSlug) {
        const currentTab = section || 'dashboard';
        navigate(`/musteri-admin/${expectedSlug}/${currentTab}`);
        return;
      }
    }
    
    fetchMyLayout();
    fetchChangeRequests();
    fetchOrders();
    fetchSupplierProducts();
    // Phase 86: load organizer extras catalog for Browse Products tab
    apiClient.get('/customer/organizer-products')
      .then(r => setOrganizerExtras(Array.isArray(r.data) ? r.data : []))
      .catch(() => { /* silent — tab will just show supplier products */ });
    fetchProfile();
    fetchAvatar();
    fetchCart();
    fetchContractInfo();
    // Fetch event config for BookingWidget
    apiClient.get(`/auth/event-config`)
      .then(r => setEventConfig(r.data))
      .catch(() => {});
  }, [slug, location.pathname]); // eslint-disable-line

  // Phase 86AZ.5 — Whenever myLayout reveals an admin-bound modular package,
  // (re)fetch the full enriched package (items + product details) so the
  // "My Stand" tab can render the contents inline (chairs/tables/sockets…).
  useEffect(() => {
    const pkgId = myLayout?.assigned_modular_package_id;
    if (!pkgId) { setAssignedModularPkg(null); return; }
    (async () => {
      try {
        const pkgRes = await apiClient.get(`/customer/modular-package`);
        setAssignedModularPkg(pkgRes.data?.package || null);
      } catch {
        setAssignedModularPkg(null);
      }
    })();
  }, [myLayout?.assigned_modular_package_id]);

  // ── Faz B/C — Layout & SCR auto-refresh polling ────────────────────────
  // When the user is on the Layout tab AND an SCR is in-flight (pending or
  // awaiting customer choice), poll every 8s so admin-side decisions
  // (approval / rejection / alternative proposals / direct re-assignment)
  // propagate without manual refresh. This is the wiring for items 6, 7 & 8.
  useEffect(() => {
    if (section !== 'layout') return;
    const hasInflightScr = changeRequests.some(r =>
      r.status === 'pending' || r.status === 'awaiting_customer_choice'
    );
    if (!hasInflightScr) return;
    const id = setInterval(() => {
      // Refresh both — myLayout reflects new stand assignment by admin,
      // changeRequests reflects new alternatives or status changes.
      fetchMyLayout();
      fetchChangeRequests();
    }, 8000);
    return () => clearInterval(id);
  }, [section, changeRequests]); // eslint-disable-line

  // ── Dashboard Onboarding Guide ────────────────────────────────────────
  // Show onboarding modal on first visit (check both session and local storage)
  // Phase 86AZ.17 — Sub-users inherit the tenant's onboarding state; they
  // should NOT see the tour as a "new tenant" walkthrough. The tour is only
  // for the main customer_admin (first-time setup).
  useEffect(() => {
    if (!userInfo) return;
    if (userInfo.role === 'sub_user') return;
    const sessionCompleted = sessionStorage.getItem('customer_dashboard_onboarding');
    const permanentlyHidden = localStorage.getItem('customer_dashboard_onboarding');

    if (!sessionCompleted && !permanentlyHidden) {
      // Delay 1 second after dashboard loads
      const timer = setTimeout(() => {
        setShowOnboarding(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [userInfo]);

  // Onboarding steps (bilingual + context-aware) — Phase 86AZ.4
  // The tour adapts to what was already assigned to the customer:
  //   • hasModular  → admin bound a Modular Stand Package; tour walks them
  //                   to "View your assigned package" and HIDES the
  //                   "Custom Design" path.
  //   • !hasModular → admin chose Space Only / Custom; tour explains the
  //                   "Custom Stand Request" path and HIDES the Modular
  //                   Stand catalog as a selectable option.
  const hasModular = !!myLayout?.assigned_modular_package_id;
  const onboardingSteps = language === 'tr' ? [
    {
      title: 'Hoş Geldiniz! 🎉',
      icon: '👋',
      description: 'COP 31 Customer Dashboard\'a hoş geldiniz! Bu kısa tura size platformun nasıl kullanılacağını göstereceğiz.',
      checklist: [
        'Profilinizi tamamlayın',
        hasModular ? 'Atanan modüler paketinizi inceleyin' : 'Stand alanınızı inceleyin',
        'Ürün ve hizmetlere göz atın',
        'Siparişlerinizi takip edin'
      ]
    },
    {
      title: 'Profil Bilgileri',
      icon: '👤',
      description: 'Sol üst köşedeki profilinize tıklayarak bilgilerinizi güncelleyebilirsiniz. Şirket bilgileri, iletişim detayları ve daha fazlası.',
    },
    hasModular ? {
      title: 'Atanan Modüler Paketiniz 📦',
      icon: '📦',
      description: 'Organizasyon ekibi size özel bir Modüler Stand Paketi atadı. "Stand & Layout" sekmesindeki "Yolunu Seç" kartından veya "Modüler Standlar" sekmesinden paketinizin detaylarını inceleyebilirsiniz. Özel tasarım yolu bu hesapta kapalıdır.',
      checklist: [
        'Stand & Layout sekmesinde paketinizi görüntüleyin',
        'Modüler Standlar sekmesinden detayları açın',
        'Ek ürünleri sepete ekleyin (varsa)',
        'Onaylayıp sözleşme adımına geçin'
      ]
    } : {
      title: 'Stand Alanı ve Özel Tasarım 🏗️',
      icon: '🏗️',
      description: 'Stand alanınız organizasyon tarafından atandı. "Stand & Layout" sekmesinden alanınızı görüntüleyebilir; alan onaylandıktan sonra "Özel Standlar" sekmesinden tasarımcılarımızla özel stand talebi başlatabilirsiniz. Modüler hazır stand bu hesapta seçilebilir değildir.',
      checklist: [
        'Stand & Layout sekmesinde alanınızı kontrol edin',
        'Alan onayını verin',
        'Özel Standlar sekmesinden tasarım talebi başlatın',
        'Tasarım dosyalarınızı yükleyin'
      ]
    },
    {
      title: 'Ürünlere Göz Atın',
      icon: '🛒',
      description: '"Ürünler" sekmesinden organizasyon ve tedarikçi ürünlerine erişin. Mobilya, AV ekipmanları, catering ve daha fazlası.',
    },
    {
      title: 'Sipariş Takibi',
      icon: '📦',
      description: '"Siparişlerim" sekmesinden tüm siparişlerinizi takip edebilir, faturaları indirebilir ve hizmet durumlarını görebilirsiniz.',
    },
    {
      title: 'Hazırsınız! 🚀',
      icon: '✅',
      description: 'Artık platformu kullanmaya hazırsınız! Sol menüden istediğiniz sekmeye geçebilir, sağ üstteki bildirim ikonundan güncellemeleri takip edebilirsiniz.',
      checklist: [
        'Profilinizi tamamlayın',
        hasModular ? 'Atanan paketinizi onaylayın' : 'Stand alan onayınızı verin',
        'İhtiyacınız olan ürünleri sepete ekleyin',
        'Herhangi bir sorunuz olursa chat butonunu kullanın'
      ]
    }
  ] : [
    {
      title: 'Welcome! 🎉',
      icon: '👋',
      description: 'Welcome to the COP 31 Customer Dashboard! This quick tour will show you how to use the platform effectively.',
      checklist: [
        'Complete your profile',
        hasModular ? 'Review your assigned modular package' : 'Review your assigned stand area',
        'Browse products and services',
        'Track your orders'
      ]
    },
    {
      title: 'Profile Information',
      icon: '👤',
      description: 'Click on your profile in the top left corner to update your information. Company details, contact information, and more.',
    },
    hasModular ? {
      title: 'Your Assigned Modular Package 📦',
      icon: '📦',
      description: 'The organisation has assigned a Modular Stand Package specifically to you. View your package via the "Choose Your Way" card in the "Stand & Layout" tab, or open the "Modular Stands" tab for full details. The Custom Design path is locked for this account.',
      checklist: [
        'View your package in the Stand & Layout tab',
        'Open details in the Modular Stands tab',
        'Add extra items to the cart (if any)',
        'Confirm and proceed to the contract step'
      ]
    } : {
      title: 'Stand Area & Custom Design 🏗️',
      icon: '🏗️',
      description: 'Your stand area has been assigned by the organisation. Review it in the "Stand & Layout" tab; once the area is approved you can start a custom design request from the "Custom Stands" tab. Pre-built modular stands are not selectable on this account.',
      checklist: [
        'Review your area in Stand & Layout',
        'Approve your stand area',
        'Start a request in the Custom Stands tab',
        'Upload your design files'
      ]
    },
    {
      title: 'Browse Products',
      icon: '🛒',
      description: 'Access organization and supplier products from the "Products" tab. Furniture, AV equipment, catering, and more.',
    },
    {
      title: 'Order Tracking',
      icon: '📦',
      description: 'Track all your orders from "My Orders" tab. Download invoices and view service statuses.',
    },
    {
      title: 'You\'re Ready! 🚀',
      icon: '✅',
      description: 'You\'re all set to use the platform! Use the left menu to navigate between sections and the notification bell for updates.',
      checklist: [
        'Complete your profile',
        hasModular ? 'Confirm your assigned package' : 'Approve your stand area',
        'Add required products to cart',
        'Use the chat button if you have any questions'
      ]
    }
  ];

  const fetchOrders = async () => {
    const token = localStorage.getItem('cop31_token');
    if (!token) return;
    setOrdersLoading(true);
    try {
      const response = await apiClient.get(`/customer/orders`);
      setOrders(response.data || []);
    } catch (error) {
      console.error('[FETCH-ORDERS] Error:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  // ── Supplier Products ──────────────────────────────────────────────────────
  const fetchSupplierProducts = useCallback(async (force = false) => {
    if (!force && (productsLoading || productsFetched)) return;
    setProductsLoading(true);
    setProductsError(null);
    try {
      const data = await listSupplierProducts();
      setSupplierProducts(Array.isArray(data) ? data : []);
      setProductsFetched(true);
    } catch (e) {
      setProductsError(language === 'tr' ? 'Ürünler yüklenemedi. Tekrar deneyin.' : 'Failed to load products.');
    } finally {
      setProductsLoading(false);
    }
  }, [productsLoading, productsFetched, language]); // eslint-disable-line

  // Detay için ürünü API'den çek (variants + full pricing)
  const openProductDetail = useCallback(async (product) => {
    setSelectedProduct(product);
    setCurrentImageIndex(0);
    setSelectedPricingType(null);
    setSelectedVariant(null);
    setSelectedLines([]);
    setDetailProduct(null);
    setShowProductDetail(true);
    setDetailLoading(true);
    try {
      const full = await getSupplierProduct(product.id, 1, null);
      setDetailProduct(full);
      // Eğer bu sırada bir varyant seçildiyse, zenginleştirilmiş halini güncelle
      setSelectedVariant(prev => {
        if (!prev) return null;
        const enriched = (full.variants || []).find(v => v.id === prev.id);
        return enriched || prev;
      });
    } catch {
      setDetailProduct(product);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Pricing helpers (merkezi)
  const getEffectivePrice = useCallback((product, pricingType = null) => {
    const pricing = (product && product.pricing) || {};
    if (pricingType && pricing[pricingType] != null) return pricing[pricingType];
    for (const key of ['event_duration', 'one_time', 'daily', 'weekly', 'monthly']) {
      if (pricing[key] != null) return pricing[key];
    }
    return product?.price || 0;
  }, []);

  const getPricingSummary = useCallback((product) => {
    const pricing = (product && product.pricing) || {};
    const labels = { daily: 'Günlük', weekly: 'Haftalık', monthly: 'Aylık', one_time: 'Tek Seferlik', event_duration: 'Etkinlik' };
    return Object.entries(pricing)
      .filter(([, v]) => v != null)
      .map(([k, v]) => `${labels[k] || k}: ${product.currency || 'USD'} ${v}`)
      .join(' · ');
  }, []);

  // ── Quote helpers ───────────────────────────────────────────────────────────
  const isQuoteExpired = (order) => {
    const vo = order?.quote_offer?.validity_date;
    if (!vo) return false;
    try { return new Date(vo) < new Date(); } catch { return false; }
  };

  const handleAcceptQuote = async (orderId) => {
    setQuoteActionLoading(orderId + '-accept');
    try {
      await acceptQuote(orderId);
      toast.success(language === 'tr' ? 'Teklif kabul edildi. Siparişiniz onaylandı.' : 'Quote accepted. Order confirmed.');
      await fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'tr' ? 'Teklif kabul edilemedi.' : 'Failed to accept quote.'));
    } finally { setQuoteActionLoading(null); }
  };

  const handleRejectQuote = async (orderId) => {
    setQuoteActionLoading(orderId + '-reject');
    try {
      await rejectQuote(orderId);
      toast.success(language === 'tr' ? 'Teklif reddedildi.' : 'Quote rejected.');
      await fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'tr' ? 'Teklif reddedilemedi.' : 'Failed to reject quote.'));
    } finally { setQuoteActionLoading(null); }
  };

  const fetchCart = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    const token = localStorage.getItem('cop31_token');
    if (!token) return;
    if (!silent) setCartLoading(true);
    try {
      const data = await getCart();
      setCart(data.items || []);
    } catch { /* cart stays empty */ }
    finally { if (!silent) setCartLoading(false); }
  }, []);

  // Global cart-change listener — child components (modular catalog, stand
  // services, supplier products, etc.) dispatch `cop31:cart-changed` after a
  // successful add/remove. We silently re-pull the cart so badges, totals
  // and the drawer all stay in sync without any visible loading flicker.
  useEffect(() => {
    const handler = () => fetchCart({ silent: true });
    window.addEventListener('cop31:cart-changed', handler);
    return () => window.removeEventListener('cop31:cart-changed', handler);
  }, [fetchCart]);

  // Stand Services unseen-count — only real new products since last visit.
  // Returns 0 for unallocated users; badge stays hidden in that case.
  const fetchStandServicesBadge = useCallback(async () => {
    try {
      const res = await apiClient.get('/stand-services/unseen-count');
      setStandServicesNewCount(res.data?.count || 0);
    } catch { /* badge stays at 0 on error */ }
  }, []);
  useEffect(() => { fetchStandServicesBadge(); }, [fetchStandServicesBadge]);

  // ── Line total helper (mirrors backend compute_line_total) ───────────────────
  const computeLineTotal = (calcType, mode, unitPrice, qty, people, days) => {
    const q = Math.max(parseInt(qty) || 1, 1);
    const p = Math.max(parseInt(people) || 0, 0);
    const d = Math.max(parseInt(days) || 1, 1);
    switch (calcType) {
      case 'flat':             return unitPrice;
      case 'quantity_based':
      case 'carton_based':     return q * unitPrice;
      case 'person_based':     return p * unitPrice;
      case 'quantity_and_days':
        return mode === 'event' ? q * unitPrice : q * d * unitPrice;
      case 'person_and_days':
        return mode === 'event' ? p * unitPrice : p * d * unitPrice;
      default:                 return unitPrice;
    }
  };

  const handleAddLinesToCart = async (product, lines) => {
    if (!lines?.length) return;
    setAddingToCart(true);
    const coverImg = product.cover_url || product.image_objects?.[0]?.url || product.images?.[0] || null;

    // Varyant görsellerini hazırla — variant_id varsa o varyantın görselini kullan
    const variantImgMap = {};
    for (const v of (product.variants || [])) {
      if (v.id && v.images?.length > 0) {
        variantImgMap[v.id] = v.images[0].url || v.images[0].thumb_url || null;
      }
    }

    // Compute stock limits to store on each cart item
    const calcType = product.calculation_type || 'flat';
    const isCartonBased = calcType === 'carton_based';
    // For carton_based: stock_quantity IS already in cartons (same unit as cart qty)
    const minQty = Math.max(1, product.min_order_quantity || 1);
    const stockPolicy = product.stock_policy;
    const stockQty = product.stock_quantity;
    const stockMax = (stockPolicy && stockPolicy !== 'untracked' && stockQty != null)
      ? stockQty  // stock_quantity is always in the same unit as cart quantity (cartons for carton_based)
      : null;
    const maxQty = product.max_order_quantity
      ? (stockMax !== null ? Math.min(product.max_order_quantity, stockMax) : product.max_order_quantity)
      : stockMax;

    const cartLines = lines.map(l => ({
      product_id:            product.id,
      product_name:          product.name || product.title,
      product_image:         (l.variant_id && variantImgMap[l.variant_id]) ? variantImgMap[l.variant_id] : coverImg,
      supplier_tenant_id:    product.tenant_id || 'quattro-stand',
      currency:              product.currency || 'USD',
      calculation_type:      l.calculation_type || 'flat',
      unit_price:            l.unit_price,
      line_total:            l.line_total,
      quantity:              l.quantity  || 1,
      number_of_people:      l.number_of_people  || null,
      number_of_days:        l.number_of_days    || null,
      start_date:            l.start_date        || null,
      end_date:              l.end_date          || null,
      pricing_mode_selected: l.pricing_mode_selected || null,
      variant_id:            l.variant_id        || null,
      variant_name:          l.variant_name      || null,
      slot_date:             l.slot_date         || null,
      slot_hour:             l.slot_hour         != null ? l.slot_hour : null,
      label:                 l.label             || null,
      min_qty:               minQty,
      max_qty:               maxQty,
      customization_notes:   l.customization_notes || null,
      is_customized:         l.is_customized       || false,
    }));
    try {
      const data = await apiAddLines(cartLines);
      setCart(data.items || []);
      fetchSupplierProducts(true); // stok badge'ini güncelle
      window.dispatchEvent(new CustomEvent('cop31:cart-changed'));
      completeOnboardingStep('cart_added');
    } catch { fetchCart(); }
    setAddingToCart(false);
  };

  const handleRemoveFromCart = async (itemId) => {
    setCart(prev => prev.filter(i => i.id !== itemId));
    try {
      const data = await apiRemoveFromCart(itemId);
      setCart(data.items || []);
      fetchSupplierProducts(true); // stok badge'ini güncelle
      window.dispatchEvent(new CustomEvent('cop31:cart-changed'));
    }
    catch { fetchCart(); }
  };

  // Forklift grubu: tüm saatleri tek seferde sil
  const handleRemoveForkliftGroup = async (groupItems) => {
    const ids = groupItems.map(i => i.id);
    setCart(prev => prev.filter(i => !ids.includes(i.id)));
    try {
      let lastData;
      for (const id of ids) {
        lastData = await apiRemoveFromCart(id);
      }
      setCart(lastData?.items || []);
      fetchSupplierProducts(true);
      window.dispatchEvent(new CustomEvent('cop31:cart-changed'));
    } catch { fetchCart(); }
  };

  // Forklift saatlerini tek görsel satıra grupla
  const groupCartForDisplay = (items) => {
    const fmt = h => String(h).padStart(2, '0') + ':00';
    const groups = {};
    const result = [];
    items.forEach(item => {
      // Forklift item: slot_hour mevcut VEYA pricing_mode 'hourly' (eski kayıtlar için fallback)
      const isForklift = item.slot_hour != null || item.pricing_mode_selected === 'hourly';
      if (isForklift) {
        const key = `${item.product_id}__${item.slot_date || 'nodate'}`;
        if (!groups[key]) {
          groups[key] = { _isForkliftGroup: true, key, product_id: item.product_id, product_name: item.product_name, slot_date: item.slot_date, product_image: item.product_image, currency: item.currency || 'USD', expires_at: item.expires_at, items: [] };
        }
        groups[key].items.push(item);
      } else {
        result.push(item);
      }
    });
    Object.values(groups).forEach(g => {
      // slot_hour varsa sırala, yoksa eklenme sırasını koru
      if (g.items.some(i => i.slot_hour != null)) {
        g.items.sort((a, b) => (a.slot_hour || 0) - (b.slot_hour || 0));
        const minH = g.items[0].slot_hour;
        const maxH = g.items[g.items.length - 1].slot_hour;
        g.start_time = fmt(minH);
        g.end_time = fmt(maxH + 1);
      } else {
        // Eski kayıtlar: label'dan saat bilgisi çıkar (örn: "2026-11-09 · 09:00 - 10:00")
        const firstLabel = g.items[0]?.label || '';
        const timeMatch = firstLabel.match(/(\d{2}:\d{2})/);
        g.start_time = timeMatch ? timeMatch[1] : '??:00';
        const lastLabel = g.items[g.items.length - 1]?.label || '';
        const lastTimeMatch = lastLabel.match(/(\d{2}:\d{2}) - (\d{2}:\d{2})/);
        g.end_time = lastTimeMatch ? lastTimeMatch[2] : '??:00';
      }
      g.total_hours = g.items.length;
      g.total_price = g.items.reduce((s, i) => s + (i.line_total || i.unit_price || 0), 0);
      g.unit_price = g.items[0]?.unit_price || 0;
      result.push(g);
    });
    return result;
  };

  // Retroactively look up product limits for old cart items that lack min_qty/max_qty
  const getCartItemLimits = (item) => {
    if (item.max_qty != null || item.min_qty != null) {
      return { minQ: item.min_qty || 1, maxQ: item.max_qty };
    }
    const product = supplierProducts.find(p => p.id === item.product_id);
    if (!product) return { minQ: 1, maxQ: null };
    const calcType = item.calculation_type || 'flat';
    const isCarton = calcType === 'carton_based';
    const ipc = product.items_per_carton || 1;
    const minQ = Math.max(1, product.min_order_quantity || 1);
    const sp = product.stock_policy;
    const sq = product.stock_quantity;
    // stock_quantity is in the same unit as cart quantity (cartons for carton_based)
    const stockMax = (sp && sp !== 'untracked' && sq != null) ? sq : null;
    const maxQ = product.max_order_quantity
      ? (stockMax !== null ? Math.min(product.max_order_quantity, stockMax) : product.max_order_quantity)
      : stockMax;
    return { minQ, maxQ };
  };

  const handleUpdateCartQty = async (item, newQty) => {
    const { minQ, maxQ } = getCartItemLimits(item);
    let qty = Math.max(minQ, newQty);
    if (maxQ !== null) qty = Math.min(qty, maxQ);

    if (qty <= 0) { handleRemoveFromCart(item.id); return; }

    const newLineTotal = computeLineTotal(
      item.calculation_type || 'flat',
      item.pricing_mode_selected || 'event',
      item.unit_price,
      qty,
      item.number_of_people || 0,
      item.number_of_days || 1,
    );
    setCart(prev => prev.map(i =>
      i.id === item.id ? { ...i, quantity: qty, line_total: newLineTotal } : i
    ));
    try {
      const data = await apiUpdateCartItem(item.id, qty);
      setCart(data.items || []);
      fetchSupplierProducts(true); // stok badge'ini güncelle
      window.dispatchEvent(new CustomEvent('cop31:cart-changed'));
    }
    catch { fetchCart(); }
  };

  const fetchProfile = async () => {
    const token = localStorage.getItem('cop31_token');
    if (!token) return;
    try {
      const res = await apiClient.get(`/customer/profile`);
      const data = res.data || {};
      setProfileData(data);
      setProfileForm(data);
      // localStorage'daki ismi profil API'siyle senkronize et (UNFCCC kullanıcıları için)
      // Phase 86AZ.17 — Sub-users have their OWN full_name; the tenant
      // profile carries the main user's name. Don't overwrite the sub-user's
      // localStorage identity with the main user's name.
      const me = (() => { try { return JSON.parse(localStorage.getItem('cop31_user') || '{}'); } catch { return {}; } })();
      if (data.full_name && me.role !== 'sub_user') {
        try {
          if (me.full_name !== data.full_name) {
            me.full_name = data.full_name;
            localStorage.setItem('cop31_user', JSON.stringify(me));
          }
        } catch { /* ignore */ }
      }
    } catch (e) {
      setProfileData({});
      setProfileForm({});
    }
  };

  const fetchAvatar = async () => {
    const token = localStorage.getItem('cop31_token');
    const userInfo = getUserInfo();
    const userId = userInfo.user_id || userInfo.id;
    if (!token || !userId) return;
    try {
      const res = await apiClient.get(`/customer/avatar/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(res.data);
      setAvatarUrl(blobUrl);
    } catch {
      setAvatarUrl(null);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const token = localStorage.getItem('cop31_token');
    setAvatarLoading(true);
    const formData = new FormData();
    formData.append('file', await compressImage(file, { maxSizeMB: 0.3, maxWidthOrHeight: 512 }));
    try {
      await apiClient.post(`/customer/avatar`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      // Eski blob URL'ini temizle
      if (avatarUrl) URL.revokeObjectURL(avatarUrl);
      await fetchAvatar();
    } catch (err) {
      alert(err.response?.data?.detail || (language === 'tr' ? 'Yükleme başarısız' : 'Upload failed'));
    } finally {
      setAvatarLoading(false);
      e.target.value = '';
    }
  };

  const handleAvatarDelete = async () => {
    if (!window.confirm(language === 'tr' ? 'Profil resmini silmek istiyor musunuz?' : 'Delete your profile picture?')) return;
    const token = localStorage.getItem('cop31_token');
    setAvatarLoading(true);
    try {
      await apiClient.delete(`/customer/avatar`);
      if (avatarUrl) URL.revokeObjectURL(avatarUrl);
      setAvatarUrl(null);
    } catch {
      alert(language === 'tr' ? 'Silme başarısız' : 'Delete failed');
    } finally {
      setAvatarLoading(false);
    }
  };

  const saveProfile = async () => {
    const token = localStorage.getItem('cop31_token');
    setProfileSaving(true);
    setProfileMsg('');
    try {
      await apiClient.put(`/customer/profile`, profileForm);
      setProfileData(profileForm);
      setProfileEdit(false);
      setProfileMsg(language === 'tr' ? 'Profil kaydedildi.' : 'Profile saved.');
      setTimeout(() => setProfileMsg(''), 3000);
      completeOnboardingStep('profile_complete');
    } catch (e) {
      setProfileMsg(language === 'tr' ? 'Hata: ' + (e.response?.data?.detail || 'Kayıt başarısız') : 'Error: ' + (e.response?.data?.detail || 'Save failed'));
    } finally {
      setProfileSaving(false);
    }
  };

  // Normalize grid_cells: backend {x,y} dict formatını veya {col,row} formatını [[x,y]] array formatına çevirir
  const normalizeCells = (cells) => {
    if (!Array.isArray(cells)) return [];
    return cells.map(c => {
      if (Array.isArray(c)) return c;
      // Hall Editor formatı: {col, row}
      if (c.col != null && c.row != null) return [c.col, c.row];
      // Eski Blue Zone formatı: {x, y}
      if (c.x != null && c.y != null) return [c.x, c.y];
      return [0, 0];
    });
  };

  const fetchMyLayout = async () => {
    const token = localStorage.getItem('cop31_token');
    
    try {
      const response = await apiClient.get(`/customer/my-layout`);
      
      // Backend participant object dönüyor, biz layout kısmını istiyoruz
      if (response.data && response.data.layout) {
        const layout = response.data.layout;
        layout.grid_cells = normalizeCells(layout.grid_cells);
        // Üst seviye participant bilgilerini layout'a taşı
        layout.stand_no  = response.data.stand_no  || '';
        layout.hall_name = response.data.hall       || '';
        layout.country   = response.data.country    || '';
        // Phase 86AU — admin-assigned stand_type for the Modüler/Space Only badge.
        layout.stand_type = response.data.stand_type || layout.stand_type || '';
        // Phase 86AZ.2 — propagate the admin-bound modular package binding
        // so the dashboard can hide Custom Design and filter Modular Stands
        // to ONLY the assigned package.
        layout.assigned_modular_package_id = response.data.assigned_modular_package_id || '';
        layout.assigned_modular_package    = response.data.assigned_modular_package    || null;
        setMyLayout(layout);
      } else if (response.data && response.data.base_width) {
        const layout = response.data;
        layout.grid_cells = normalizeCells(layout.grid_cells);
        setMyLayout(layout);
      } else {
        setMyLayout(null);
      }
    } catch (error) {
      console.error('[FETCH-LAYOUT] Error:', error);
      setMyLayout(null);
    } finally {
      setLayoutLoading(false);
    }
  };

  const fetchChangeRequests = async () => {
    const token = localStorage.getItem('cop31_token');
    try {
      const res = await apiClient.get(`/customer/stand-change-requests`);
      setChangeRequests(res.data || []);
    } catch (e) { console.error(e); }
    // Phase 86N — also fetch quota so we can disable "+ New Request" when full
    try {
      const q = await apiClient.get(`/customer/stand-change-requests/quota`);
      setChangeQuota(q.data || null);
    } catch { /* non-blocking */ }
  };

  const fetchContractInfo = async () => {
    const token = localStorage.getItem('cop31_token');
    if (!token) return;
    try {
      const res = await apiClient.get(`/customer/my-layout/contract`);
      setContractInfo(res.data || null);
      setAreaApprovalStatus(res.data?.area_approval_status || null);
    } catch { /* ignore */ }
  };

  const handleApproveArea = async () => {
    setAreaApproving(true);
    try {
      await apiClient.post(`/customer/my-layout/approve-area`, {});
      setAreaApprovalStatus('approved');
      toast.success(language === 'tr' ? 'Stand alanı onaylandı! Sözleşme en kısa sürede gönderilecektir.' : 'Stand area approved! Contract will be sent shortly.');
      fetchContractInfo();
      // Phase 86AA — refresh SCR list so the "Yeni Alan Atandı" badge flips
      // to "Tamamlandı" instantly without requiring a full page reload.
      fetchChangeRequests();
      setApproveAreaModalOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'tr' ? 'Onay başarısız.' : 'Approval failed.'));
    } finally {
      setAreaApproving(false);
    }
  };

  const submitChangeRequest = async () => {
    if (!changeForm.change_types.length) {
      setChangeMsg(language === 'tr' ? 'En az bir değişiklik türü seçin.' : 'Select at least one change type.');
      return;
    }
    setChangeSubmitting(true);
    setChangeMsg('');
    const token = localStorage.getItem('cop31_token');
    try {
      await apiClient.post(`/customer/stand-change-requests`, {
        change_types:         changeForm.change_types,
        requested_area:       changeForm.requested_area ? Number(changeForm.requested_area) : null,
        requested_stand_type: changeForm.requested_stand_type,
        requested_location:   changeForm.requested_location,
        notes:                changeForm.notes,
      });
      setChangeMsg(language === 'tr' ? 'Talebiniz başarıyla iletildi.' : 'Your request has been submitted.');
      setShowChangeForm(false);
      setWizardStep(1); // Phase 86O — reset wizard for next time
      setChangeForm({ change_types: [], requested_area: '', requested_stand_type: '', requested_location: '', notes: '' });
      fetchChangeRequests();
    } catch (e) {
      const backendMsg = e.response?.data?.detail || '';
      if (e.response?.status === 409) {
        // Zaten bekleyen talep var — formu kapat ve listeyi yenile
        setChangeMsg('');
        setShowChangeForm(false);
        fetchChangeRequests();
        toast.error(language === 'tr'
          ? (backendMsg || 'Zaten bekleyen bir talebiniz var.')
          : 'You already have a pending request.');
      } else {
        setChangeMsg(language === 'tr'
          ? (backendMsg || 'Hata oluştu, tekrar deneyin.')
          : (backendMsg || 'Error occurred, try again.'));
      }
    } finally {
      setChangeSubmitting(false);
    }
  };

  const remindChangeRequest = async (requestId) => {
    setRemindingId(requestId);
    const token = localStorage.getItem('cop31_token');
    try {
      await apiClient.post(`/customer/stand-change-requests/${requestId}/remind`, {});
      toast.success(language === 'tr' ? 'Hatırlatma gönderildi! Admin en kısa sürede yanıt verecektir.' : 'Reminder sent! Admin will respond shortly.');
      fetchChangeRequests();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 429) {
        toast.error(language === 'tr' ? 'Son hatırlatmadan bu yana 24 saat geçmemiş.' : 'Please wait 24h before sending another reminder.');
      } else {
        toast.error(language === 'tr' ? 'Hatırlatma gönderilemedi.' : 'Could not send reminder.');
      }
    } finally {
      setRemindingId(null);
    }
  };

  const respondToAlternative = async (requestId, action, alternativeId = null) => {
    setRespondingId(requestId + '_' + action + (alternativeId || ''));
    const token = localStorage.getItem('cop31_token');
    try {
      await apiClient.post(`/customer/stand-change-requests/${requestId}/respond`, {
        action,
        alternative_id: alternativeId,
      });
      if (action === 'accept') {
        toast.success(language === 'tr' ? 'Alternatif kabul edildi! Stand talebiniz onaylandı.' : 'Alternative accepted! Your stand request is approved.');
      } else {
        toast.success(language === 'tr' ? 'Tüm alternatifler reddedildi.' : 'All alternatives declined.');
      }
      fetchChangeRequests();
    } catch (e) {
      toast.error(language === 'tr' ? 'Hata oluştu, tekrar deneyin.' : 'Error occurred, please try again.');
    } finally {
      setRespondingId(null);
    }
  };

  // Helper: Edge segments
  const getEdgeSegments = (cells, baseW, baseH, side) => {
    if (!cells || !cells.length) return [];
    const segments = [];
    
    if (side === 'north') {
      for (let x = 0; x < Math.max(baseW, 30); x++) {
        const colCells = cells.filter(([cx]) => cx === x);
        if (colCells.length > 0) {
          const minY = Math.min(...colCells.map(([, cy]) => cy));
          segments.push({ x1: x, y1: minY, x2: x + 1, y2: minY });
        }
      }
    } else if (side === 'south') {
      for (let x = 0; x < Math.max(baseW, 30); x++) {
        const colCells = cells.filter(([cx]) => cx === x);
        if (colCells.length > 0) {
          const maxY = Math.max(...colCells.map(([, cy]) => cy));
          segments.push({ x1: x, y1: maxY + 1, x2: x + 1, y2: maxY + 1 });
        }
      }
    } else if (side === 'east') {
      for (let y = 0; y < Math.max(baseH, 25); y++) {
        const rowCells = cells.filter(([, cy]) => cy === y);
        if (rowCells.length > 0) {
          const maxX = Math.max(...rowCells.map(([cx]) => cx));
          segments.push({ x1: maxX + 1, y1: y, x2: maxX + 1, y2: y + 1 });
        }
      }
    } else if (side === 'west') {
      for (let y = 0; y < Math.max(baseH, 25); y++) {
        const rowCells = cells.filter(([, cy]) => cy === y);
        if (rowCells.length > 0) {
          const minX = Math.min(...rowCells.map(([cx]) => cx));
          segments.push({ x1: minX, y1: y, x2: minX, y2: y + 1 });
        }
      }
    }
    return segments;
  };

  const getSegmentColor = (cells, baseW, baseH, openSides, side, x, y) => {
    let checkX = x, checkY = y;
    if (side === 'north') checkY = y;
    else if (side === 'south') checkY = y - 1;
    else if (side === 'east') checkX = x - 1;
    else if (side === 'west') checkX = x;
    
    const isSelected = cells.some(([cx, cy]) => cx === checkX && cy === checkY);
    const isExtrusion = isSelected && (checkX >= baseW || checkY >= baseH);
    if (isExtrusion) return '#22c55e';
    return (openSides || []).includes(side) ? '#22c55e' : '#ef4444';
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/giris');
  };

  // Dynamic categories from loaded products
  const productCategories = useMemo(() => {
    const cats = new Set(supplierProducts.map(p => p.category || 'Diğer').filter(Boolean));
    return ['Tümü', ...Array.from(cats).sort()];
  }, [supplierProducts]);

  // Phase 86: Source pools for the two tabs.
  // Modular Standlar tab  → supplier_products WHERE category == 'Organization'
  // Browse Products tab   → organizer extras + non-Organization supplier_products
  const isModularStandRow = (p) => {
    const c = (p?.category || '').toLowerCase();
    return c === 'organization' || c.startsWith('organization ') || c.includes('stand constructor');
  };

  // Normalize organizer_products into a shape compatible with the product grid.
  // Clicking such a card is a no-op for now (detail page works via supplier API
  // only); we mark it with `_source: 'organizer'` so the renderer can skip the
  // detail-modal click path.
  const organizerAsProducts = useMemo(() => {
    return (organizerExtras || []).map(o => {
      const title    = (language === 'tr' ? (o.name_tr || o.name_en) : (o.name_en || o.name_tr)) || o.name || '';
      const category = (language === 'tr' ? (o.category_tr || o.category_en) : (o.category_en || o.category_tr)) || 'Organization';
      return {
        id:              o.id,
        _source:         'organizer',
        title,
        title_tr:        o.name_tr,
        title_en:        o.name_en,
        name:            title,
        category,                                  // display-only; grouping uses _source
        description:     '',
        cover_url:       o.cover_url || null,
        cover_thumb_url: o.thumb_url || null,
        image_objects:   o.cover_url ? [{ id: 'o-'+o.id, url: o.cover_url, thumb_url: o.thumb_url, is_cover: true }] : [],
        effective_price: Number(o.extra_price || 0),
        currency:        o.currency || 'EUR',
        unit:            o.unit || 'adet',
        availability:    { status: o.track_stock ? (o.stock_qty > 0 ? 'in_stock' : 'out_of_stock') : 'untracked' },
      };
    });
  }, [organizerExtras, language]);

  // Pool shown on Modular Stands tab
  const modularStandProducts = useMemo(() => {
    return (supplierProducts || []).filter(isModularStandRow);
  }, [supplierProducts]);

  // Pool shown on Browse Products tab (applies source/search/category filters)
  const browsePoolAll = useMemo(() => {
    const nonModularSupplier = (supplierProducts || []).filter(p => !isModularStandRow(p));
    return [
      ...organizerAsProducts,
      ...nonModularSupplier,
    ];
  }, [supplierProducts, organizerAsProducts]);

  const filteredProducts = useMemo(() => {
    // Extract "NN m²" from product title/variants — used to sort modular stands smallest → largest
    const extractM2 = (p) => {
      const src = `${p.title || ''} ${p.title_tr || ''} ${p.title_en || ''}`;
      const m = src.match(/(\d{1,4})\s*m²/i);
      return m ? parseInt(m[1], 10) : null;
    };
    // Apply source filter chip (Phase 86). Only affects Browse Products view.
    const sourceFiltered = (() => {
      if (productSource === 'organizer') return browsePoolAll.filter(p => p._source === 'organizer');
      if (productSource === 'supplier')  return browsePoolAll.filter(p => p._source !== 'organizer');
      return browsePoolAll;
    })();
    return sourceFiltered
      .filter(p => {
        const name = (p.title || p.name || '').toLowerCase();
        const desc = (p.description || '').toLowerCase();
        const cat = (p.category || 'Diğer').toLowerCase();
        const matchesSearch = !searchTerm || name.includes(searchTerm.toLowerCase()) || desc.includes(searchTerm.toLowerCase());
        const matchesCategory = !selectedCategory || selectedCategory === 'Tümü' ||
          cat === selectedCategory.toLowerCase() ||
          cat.includes(selectedCategory.toLowerCase());
        return matchesSearch && matchesCategory;
      })
      .sort((a, b) => {
        const am2 = extractM2(a);
        const bm2 = extractM2(b);
        // Ürünlerden m² taşıyanlar önce, artan sırayla
        if (am2 != null && bm2 != null) return am2 - bm2;
        if (am2 != null) return -1;
        if (bm2 != null) return 1;
        return (a.title || '').localeCompare(b.title || '');
      });
  }, [browsePoolAll, productSource, searchTerm, selectedCategory]);

  // ── Recommended Tier (Organization/Stand Constructor) based on customer's stand area ──
  // Picks smallest Tier whose max m² >= customer's area; falls back to largest Tier if none big enough.
  // Phase 86: uses modularStandProducts (Modular Stands tab source) instead of filteredProducts.
  const recommendedProductId = useMemo(() => {
    const userArea = parseFloat(myLayout?.total_area || 0);
    if (!userArea || userArea <= 0) return null;
    const tierCandidates = modularStandProducts
      .map(p => {
        const src = `${p.title || ''} ${p.title_tr || ''} ${p.title_en || ''}`;
        const m = src.match(/(\d{1,4})\s*m²/i);
        return m ? { id: p.id, area: parseInt(m[1], 10) } : null;
      })
      .filter(Boolean);
    if (!tierCandidates.length) return null;
    const fit = tierCandidates
      .filter(x => x.area >= userArea)
      .sort((a, b) => a.area - b.area);
    if (fit.length) return fit[0].id;
    // fallback: user area bigger than all tiers → suggest the largest available
    const largest = tierCandidates.sort((a, b) => b.area - a.area)[0];
    return largest?.id || null;
  }, [modularStandProducts, myLayout?.total_area]);

  const categories = productCategories;

  // ── Fiyat formatlama: TR → 40.000,00 | EN → 40,000.00 ──────────
  const fmt = (val, decimals = 2) => {
    const num = parseFloat(val) || 0;
    return num.toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // Mirror of `requestStatus` from StandLayoutSection so the dashboard tab can
  // surface the same state without duplicating the (deeper) child component's
  // logic. Returns: 'allocated' | 'under_review' | 'pending' | 'none'.
  const requestStatusForDashboard = (prof) => {
    if (myLayout?.stand_no) return 'allocated';
    if (!prof?.stand_request_submitted_at) return 'none';
    return prof.stand_request_status || 'pending';
  };
  const translateCategory = (cat) => {
    if (!cat) return cat;
    if (language === 'tr') {
      const map = {
        'Organization':         'Organizatör',
        'Stand Constructor':    'Stand Yapımcısı',
        'Design Agency':        'Tasarım Ajansı',
        'Handling & Equipment': 'Taşıma & Ekipman',
        'Travel & Accommodation': 'Seyahat & Konaklama',
        'Furniture':            'Mobilya',
        'Catering':             'Catering',
        'Tümü':                 'Tümü',
      };
      return map[cat] || cat;
    }
    const mapEn = { 'Tümü': 'All' };
    return mapEn[cat] || cat;
  };
  const furnitureSubCategories = [];
  const technicSubCategories = [];

  const addToCart = (product) => {
    // Tüm ürünler için detay modal aç — canlı API verisi yükle
    openProductDetail(product);
  };

  // Phase 86D — Global stand-payment gate.
  // Customer must purchase AND pay for at least one modular stand product
  // before they can buy any other (non-stand) product. Viewing/details are
  // unrestricted; only add-to-cart is blocked. A modular stand's own
  // add-to-cart (so they CAN buy the stand) is always allowed.
  // Phase 86AZ.9 — Green Zone customers get their stand ALLOCATED by Ultra
  // Admin (no self-checkout). Allocation == effectively paid for the gate's
  // purposes, so we unlock the rest of the catalog as soon as the customer
  // has an admin-bound modular package on their layout.
  const hasAnyPaidStand = useMemo(() => {
    // Admin-allocated stand (Green Zone) → unlock immediately.
    if (myLayout?.assigned_modular_package_id) return true;
    const ids = new Set((modularStandProducts || []).map(p => p.id));
    return (orders || []).some(o => {
      const isPaid = (o.payment_status === 'paid' || o.status === 'paid' || o.status === 'completed');
      if (!isPaid) return false;
      return (o.items || []).some(it => ids.has(it.product_id));
    });
  }, [orders, modularStandProducts, myLayout?.assigned_modular_package_id]);

  // Returns true when the given product is a platform modular stand (the only
  // thing a customer is allowed to buy before a stand is paid).
  const isModularStandProduct = useCallback((product) => {
    if (!product) return false;
    const pid = product.id;
    return (modularStandProducts || []).some(p => p.id === pid);
  }, [modularStandProducts]);

  // Central gate: returns true when add-to-cart is allowed for this product.
  // Side-effect: when blocked, opens the centered stand-payment lock modal.
  const gateOrShowModal = useCallback((product) => {
    if (isModularStandProduct(product)) return true;
    if (hasAnyPaidStand)                 return true;
    setShowStandPaymentLockModal(true);
    return false;
  }, [hasAnyPaidStand, isModularStandProduct]);

  // Phase 86F — When the customer has paid for a stand, fetch the modular
  // package associated with their allocated stand area and cache the set of
  // organizer product_ids. This drives the conditional "included in your
  // package" banner in the preview modal.
  useEffect(() => {
    if (!hasAnyPaidStand) {
      setPaidPackageProductIds(new Set());
      return;
    }
    apiClient.get('/customer/modular-package')
      .then(r => {
        const items = r.data?.package?.items || [];
        setPaidPackageProductIds(new Set(items.map(it => it.product_id).filter(Boolean)));
      })
      .catch(() => setPaidPackageProductIds(new Set()));
  }, [hasAnyPaidStand]);

  // Phase 86: add an organizer extra (standalone purchase outside modular
  // package). These products can ALSO be bought additionally even though
  // they come free inside a modular stand package.
  const [addingOrganizerId, setAddingOrganizerId] = useState(null);
  // Phase 86AZ.10 — Per-card quantity state for "Add to Cart". Lets the
  // customer pick how many of each Organizer Extra they want before a
  // single add-to-cart call (instead of clicking the button N times).
  const [organizerQty, setOrganizerQty] = useState({}); // { productId: qty }
  const setQtyFor = (pid, fn) => setOrganizerQty(prev => {
    const cur = Number(prev[pid] ?? 1);
    const next = Math.max(1, Math.min(99, Number(fn(cur))));
    return { ...prev, [pid]: next };
  });
  const handleAddOrganizerExtra = useCallback(async (rawOrganizer, card, qty = 1) => {
    if (addingOrganizerId) return;
    // Phase 86D: non-stand product — require paid stand first
    if (!hasAnyPaidStand) { setShowStandPaymentLockModal(true); return; }
    const q = Math.max(1, Math.min(99, Number(qty) || 1));
    setAddingOrganizerId(rawOrganizer.id);
    try {
      const name  = (language === 'tr' ? (rawOrganizer.name_tr || rawOrganizer.name_en) : (rawOrganizer.name_en || rawOrganizer.name_tr)) || rawOrganizer.name || '';
      const price = Number(rawOrganizer.extra_price || 0);
      await apiAddToCart({
        product_id:          `organizer-extra-${rawOrganizer.id}`,
        product_name:        name,
        product_image:       card?.cover_thumb_url || rawOrganizer.cover_url || null,
        calculation_type:    'flat',
        unit_price:          price,
        line_total:          price * q,
        currency:            rawOrganizer.currency || 'EUR',
        quantity:            q,
        supplier_tenant_id:  'organizer',
        notes:               language === 'tr'
          ? 'Modüler paket dışı ilave alım (Organizer Ekstra)'
          : 'Additional purchase outside modular package (Organizer Extra)',
      });
      window.dispatchEvent(new CustomEvent('cop31:cart-changed'));
      toast.success(language === 'tr' ? `${q} adet sepete eklendi` : `${q} added to cart`);
      // Reset the per-card qty back to 1 after a successful add.
      setOrganizerQty(prev => ({ ...prev, [rawOrganizer.id]: 1 }));
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'tr' ? 'Sepete eklenemedi.' : 'Failed to add to cart.'));
    } finally {
      setAddingOrganizerId(null);
    }
  }, [addingOrganizerId, language, hasAnyPaidStand]);

  const getStatusColor = (status) => {
    const map = {
      pending:              'bg-yellow-100 text-yellow-700',
      pending_confirmation: 'bg-orange-100 text-orange-700',
      quote_pending:        'bg-purple-100 text-purple-700',
      quoted:               'bg-sky-100 text-sky-700',
      confirmed:            'bg-blue-100 text-blue-700',
      in_progress:          'bg-cyan-100 text-cyan-700',
      completed:            'bg-emerald-100 text-emerald-700',
      cancelled:            'bg-red-100 text-red-700',
      // Stand Services (Phase 75) statuses
      submitted:            'bg-amber-100 text-amber-700',
      approved:             'bg-emerald-100 text-emerald-700',
      rejected:             'bg-red-100 text-red-700',
      paid:                 'bg-blue-100 text-blue-700',
      delivered:            'bg-violet-100 text-violet-700',
      refunded:             'bg-orange-100 text-orange-700',
    };
    return map[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status, lang = 'tr') => {
    const trMap = {
      pending:              'Bekliyor',
      pending_confirmation: 'Onay Bekliyor',
      quote_pending:        'Teklif Bekleniyor',
      quoted:               'Teklif Verildi',
      confirmed:            'Onaylandı',
      in_progress:          'İşlemde',
      completed:            'Tamamlandı',
      cancelled:            'İptal',
      submitted:            'Onay Bekliyor',
      approved:             'Onaylandı',
      rejected:             'Reddedildi',
      paid:                 'Ödendi',
      delivered:            'Teslim Edildi',
      refunded:             'İade Edildi',
    };
    const enMap = {
      pending:              'Pending',
      pending_confirmation: 'Awaiting Confirmation',
      quote_pending:        'Quote Pending',
      quoted:               'Quoted',
      confirmed:            'Confirmed',
      in_progress:          'In Progress',
      completed:            'Completed',
      cancelled:            'Cancelled',
      submitted:            'Pending',
      approved:             'Approved',
      rejected:             'Rejected',
      paid:                 'Paid',
      delivered:            'Delivered',
      refunded:             'Refunded',
    };
    return (lang === 'tr' ? trMap : enMap)[status] || status;
  };

  // ── Phase 86M — Product Detail View extracted to a single computed JSX
  // node. This lets us render the same detail surface from BOTH the
  // "products" tab AND the "modular-stands" tab without duplicating the
  // ~260-line component, so clicking a Modular Stand card no longer forces
  // the user to switch tabs (Phase 86M UX requirement). Evaluated once per
  // render via the IIFE — when there's nothing to show it's null and the
  // tabs fall back to their default grid content.
  const productDetailJSX = (showProductDetail && selectedProduct) ? (() => {
    const product = detailProduct || selectedProduct;
    const enrichedVariant = selectedVariant && detailProduct
      ? (detailProduct.variants || []).find(v => v.id === selectedVariant.id)
      : null;
    const variantImgs = enrichedVariant?.images?.length > 0 ? enrichedVariant.images : null;
    const baseImgs = selectedProduct?.image_objects?.length > 0 ? selectedProduct.image_objects : [];
    const imgObjs = variantImgs || (product?.image_objects?.length > 0 ? product.image_objects : baseImgs);
    const mainImg = imgObjs[currentImageIndex]?.url || product?.cover_url || null;
    const openLightbox = (idx) => { setLightboxImgs(imgObjs); setLightboxIdx(idx); setLightboxOpen(true); };
    return (
      <div className="space-y-5">
        {/* Back button — navigates back to whichever tab the user opened from */}
        <button
          onClick={() => { setShowProductDetail(false); setDetailProduct(null); setCurrentImageIndex(0); setSelectedVariant(null); }}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors group"
          data-testid="product-detail-back-btn"
        >
          <ChevronLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
          {language === 'tr' ? 'Geri' : 'Back'}
        </button>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left — Resimler */}
          <div className="space-y-3">
            {/* Ana resim — tıklayınca lightbox */}
            <div
              className="relative bg-gray-100 rounded-2xl overflow-hidden cursor-zoom-in"
              style={{ aspectRatio: '4/3' }}
              onClick={() => mainImg && openLightbox(currentImageIndex)}
            >
              {mainImg
                ? <img
                    src={mainImg}
                    alt=""
                    className="w-full h-full object-contain transition-opacity duration-300"
                    data-testid="product-detail-main-image"
                    onError={(e) => {
                      // Phase 86J — Display s3_key may be missing
                      // from S3 (half-orphan); fall back to thumb.
                      const fallback = imgObjs[currentImageIndex]?.thumb_url;
                      if (fallback && e.target.src !== fallback && !e.target.dataset.fallbackTried) {
                        e.target.dataset.fallbackTried = '1';
                        e.target.src = fallback;
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                  />
                : <div className="w-full h-full flex items-center justify-center"><Package className="h-20 w-20 text-gray-300" /></div>
              }
              {mainImg && (
                <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/20">
                  <div className="bg-white/90 rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 shadow">
                    <ZoomIn className="h-3.5 w-3.5" />
                    {language === 'tr' ? 'Büyüt' : 'Zoom'}
                  </div>
                </div>
              )}
              {imgObjs.length > 1 && (
                <>
                  <button onClick={e => { e.stopPropagation(); setCurrentImageIndex(i => Math.max(0, i - 1)); }}
                    disabled={currentImageIndex === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1.5 disabled:opacity-30 shadow hover:bg-white transition-colors">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); setCurrentImageIndex(i => Math.min(imgObjs.length - 1, i + 1)); }}
                    disabled={currentImageIndex === imgObjs.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-1.5 disabled:opacity-30 shadow hover:bg-white transition-colors">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}
              {detailLoading && (
                <div className="absolute top-2 right-2 bg-white/80 rounded-full p-1">
                  <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin" />
                </div>
              )}
            </div>
            {/* Küçük resim şeridi */}
            {imgObjs.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbar">
                {imgObjs.map((img, idx) => (
                  <img key={idx} src={img.thumb_url || img.url} alt=""
                    className={`w-16 h-16 shrink-0 object-cover rounded-lg cursor-pointer border-2 transition-all ${idx === currentImageIndex ? 'border-emerald-500 shadow scale-105' : 'border-gray-200 opacity-70 hover:opacity-100'}`}
                    onClick={() => setCurrentImageIndex(idx)} />
                ))}
              </div>
            )}

            {/* Stok durumu */}
            {(() => {
              const p2 = detailProduct || selectedProduct;
              const unit = p2?.calculation_type === 'carton_based' ? (language === 'tr' ? 'koli' : 'Pack') : (language === 'tr' ? 'adet' : 'pcs');
              if (selectedVariant?.stock != null) {
                const vStock = selectedVariant.stock;
                const vStatus = vStock === 0 ? 'out_of_stock' : vStock <= 5 ? 'low_stock' : 'in_stock';
                const vColors = { in_stock: 'bg-emerald-50 text-emerald-700 border-emerald-200', low_stock: 'bg-amber-50 text-amber-700 border-amber-200', out_of_stock: 'bg-red-50 text-red-700 border-red-200' };
                const vMsg = { in_stock: language === 'tr' ? 'Stokta mevcut' : 'In stock', low_stock: language === 'tr' ? 'Az stok' : 'Low stock', out_of_stock: language === 'tr' ? 'Stokta yok' : 'Out of stock' };
                return <div className={`rounded-xl border px-3 py-2 text-sm flex items-center gap-2 ${vColors[vStatus]}`}><div className="h-2 w-2 rounded-full bg-current shrink-0" /><span>{vMsg[vStatus]}</span><span className="text-[10px] text-current/60 ml-0.5">({selectedVariant.name})</span><span className="ml-auto font-semibold">{vStock} {unit}</span></div>;
              }
              const avail = p2?.availability;
              if (!avail || avail.status === 'untracked') return null;
              const colors = { in_stock: 'bg-emerald-50 text-emerald-700 border-emerald-200', low_stock: 'bg-amber-50 text-amber-700 border-amber-200', out_of_stock: 'bg-red-50 text-red-700 border-red-200', can_supply_on_request: 'bg-blue-50 text-blue-700 border-blue-200', partial_available: 'bg-orange-50 text-orange-700 border-orange-200' };
              return <div className={`rounded-xl border px-3 py-2 text-sm flex items-center gap-2 ${colors[avail.status] || ''}`}><div className="h-2 w-2 rounded-full bg-current shrink-0" />{avail.message}{avail.available_qty != null && <span className="ml-auto font-semibold">{avail.available_qty} {unit}</span>}</div>;
            })()}

            {/* Description */}
            {(() => {
              const p = detailProduct || selectedProduct;
              const desc = language === 'tr'
                ? (p?.description_tr || p?.description_en || p?.description)
                : (p?.description_en || p?.description_tr || p?.description);
              return desc ? (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 uppercase mb-1.5">{language === 'tr' ? 'Açıklama' : 'Description'}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{desc}</p>
                </div>
              ) : null;
            })()}
          </div>

          {/* Right — Fiyat, varyantlar, sepet */}
          <div className="space-y-4">
            {/* Başlık */}
            <div>
              <div className="flex items-start gap-2 flex-wrap mb-1">
                {(detailProduct?.product_code || selectedProduct.product_code) && (
                  <span className="text-xs font-mono text-gray-400">{detailProduct?.product_code || selectedProduct.product_code}</span>
                )}
                {product.category && <Badge variant="secondary" className="text-[10px]">{translateCategory(product.category)}</Badge>}
              </div>
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                {language === 'tr'
                  ? (product.title_tr || product.title_en || product.title)
                  : (product.title_en || product.title_tr || product.title)}
              </h1>
            </div>

            {/* Fiyat */}
            {(() => {
              const p = detailProduct || selectedProduct;
              const displayPrice = parseFloat(selectedVariant
                ? (selectedVariant.price_override_daily ?? selectedVariant.price_override_event ?? selectedVariant.price ?? p.daily_price ?? p.event_price ?? 0)
                : (p.daily_price || p.event_price || p.effective_price || p.price || 0));
              const total = selectedLines?.reduce((s, l) => s + (l.line_total || 0), 0);
              return (
                <div className="bg-emerald-50 rounded-2xl p-4">
                  <p className="text-2xl font-extrabold text-emerald-700 mb-1">
                    {p.currency || 'USD'} {total > 0
                      ? total.toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US', { minimumFractionDigits: 0 })
                      : fmt(displayPrice, 0)}
                    {p.unit && !total && <span className="text-sm font-normal text-emerald-500 ml-1">/ {p.unit}</span>}
                    {total > 0 && <span className="text-sm font-normal text-emerald-500 ml-2">({selectedLines.length} {language === 'tr' ? 'satır' : 'lines'})</span>}
                  </p>
                  {p.min_rental_period_days && !selectedLines?.length && (
                    <p className="text-xs text-emerald-600">Min. {p.min_rental_period_days} {language === 'tr' ? 'gün kiralama' : 'days rental'}</p>
                  )}
                </div>
              );
            })()}

            {/* Booking Widget */}
            <BookingWidget
              product={detailProduct || selectedProduct}
              language={language}
              onChange={setSelectedLines}
              onVariantChange={v => {
                setSelectedVariant(v);
                setCurrentImageIndex(0);
                if (v && detailProduct) {
                  const enriched = (detailProduct.variants || []).find(dv => dv.id === v.id);
                  if (enriched) setSelectedVariant(enriched);
                }
              }}
              addingToCart={addingToCart}
              onAddToCart={async (lines) => {
                // Phase 86D — non-stand products require paid stand first
                const _p = detailProduct || selectedProduct;
                if (!gateOrShowModal(_p)) return;
                setAddingToCart(true);
                await handleAddLinesToCart(_p, lines);
                setAddingToCart(false);
                setShowProductDetail(false);
                toast.success(language === 'tr' ? `${lines.length} ürün sepete eklendi!` : `${lines.length} item(s) added to cart!`);
              }}
              eventConfig={eventConfig}
            />

            {/* Sepete Ekle + Customize Booth */}
            <div className="flex gap-3 pt-2">
              {selectedLines?.some(l => l._key === 'auto') && (selectedLines[0]?.line_total || 0) > 0 && (
                <Button
                  data-testid="add-to-cart-btn"
                  disabled={addingToCart}
                  onClick={async () => {
                    // Phase 86D — gate non-stand products
                    const _p = detailProduct || selectedProduct;
                    if (!gateOrShowModal(_p)) return;
                    setAddingToCart(true);
                    await handleAddLinesToCart(detailProduct || selectedProduct, selectedLines);
                    setAddingToCart(false);
                    setShowProductDetail(false);
                    toast.success(language === 'tr' ? 'Sepete eklendi!' : 'Added to cart!');
                  }}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full py-5 text-sm font-semibold gap-2 flex items-center justify-center">
                  <ShoppingCart className="h-4 w-4" />
                  {addingToCart ? (language === 'tr' ? 'Ekleniyor...' : 'Adding...') : (language === 'tr' ? 'Sepete Ekle' : 'Add to Cart')}
                </Button>
              )}
              {/* Platform Tier stand product (ultra admin) → "Customize Booth"
                  Everything else → "Note for Supplier".
                  Phase 86C: "Customize Booth" is LOCKED until the
                  customer has a paid order containing this exact
                  modular stand product. Clicking while locked shows
                  a toast explaining the requirement. */}
              {(() => {
                const _p = detailProduct || selectedProduct;
                const isPlatformProduct =
                  (_p?.tenant_id === 'summitflow-platform') ||
                  (_p?.supplier_id === 'summitflow-platform');
                const labelTr = isPlatformProduct ? 'Standı Özelleştir' : 'Tedarikçiye Not Bırak';
                const labelEn = isPlatformProduct ? 'Customize Booth'    : 'Note for Supplier';
                // Payment gate only applies to Customize Booth on platform products
                const standPaid = isPlatformProduct
                  ? (orders || []).some(o =>
                      (o.payment_status === 'paid' || o.status === 'paid' || o.status === 'completed') &&
                      (o.items || []).some(it => it.product_id === _p?.id)
                    )
                  : true;
                const isLocked = isPlatformProduct && !standPaid;
                return (
                  <Button
                    data-testid={isPlatformProduct ? 'customize-booth-btn' : 'note-for-supplier-btn'}
                    onClick={() => {
                      if (isLocked) {
                        setShowStandPaymentLockModal(true);
                        return;
                      }
                      setCustomizeNotes('');
                      setShowCustomizeModal(true);
                    }}
                    variant="outline"
                    title={isLocked
                      ? (language === 'tr' ? 'Stand ödemesi yapıldıktan sonra aktif olacaktır' : 'Will be active after stand payment is made')
                      : undefined}
                    className={`flex-1 rounded-full py-5 text-sm font-semibold gap-2 ${
                      isLocked
                        ? 'border-gray-300 text-gray-400 bg-gray-50 opacity-60 cursor-not-allowed hover:bg-gray-50'
                        : 'border-amber-500 text-amber-700 hover:bg-amber-50'
                    }`}
                    data-locked={isLocked ? 'true' : 'false'}>
                    <Wand2 className="h-4 w-4" />
                    {language === 'tr' ? labelTr : labelEn}
                  </Button>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  })() : null;

  return (
    <div className="min-h-screen bg-gray-50 zone-dashboard">
      {/* Onboarding Wizard — full screen, zorunlu — profile_complete olmadan kapatılamaz */}
      {showWizard === null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50">
          <div className="flex flex-col items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-blue-600 flex items-center justify-center animate-pulse">
              <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
        </div>
      )}
      {showWizard === true && (
        <OnboardingWizard
          onClose={closeWizard}
          onSkip={closeWizard}
          userData={(() => {
            try { return JSON.parse(localStorage.getItem('cop31_user') || '{}'); } catch { return {}; }
          })()}
        />
      )}

      {/* Chat Widget — floating bottom right */}
      <ChatWidget
        token={localStorage.getItem('cop31_token')}
        userName={(() => { try { return JSON.parse(localStorage.getItem('cop31_user') || '{}')?.full_name || ''; } catch { return ''; } })()}
      />

      {/* Dynamic zone color overrides — applies to ALL emerald tailwind classes */}
      <style>{`
        .zone-dashboard .bg-emerald-600, .zone-dashboard .bg-emerald-500 { background-color: ${zoneTheme.hex} !important; }
        .zone-dashboard .hover\\:bg-emerald-700:hover, .zone-dashboard .hover\\:bg-emerald-600:hover { background-color: ${zoneTheme.hex}dd !important; }
        .zone-dashboard .text-emerald-600, .zone-dashboard .text-emerald-700 { color: ${zoneTheme.hex} !important; }
        .zone-dashboard .border-emerald-200, .zone-dashboard .border-emerald-300 { border-color: ${zoneTheme.hex}40 !important; }
        .zone-dashboard .border-emerald-600 { border-color: ${zoneTheme.hex} !important; }
        .zone-dashboard .bg-emerald-50 { background-color: ${zoneTheme.hex}0d !important; }
        .zone-dashboard .bg-emerald-100 { background-color: ${zoneTheme.hex}18 !important; }
        .zone-dashboard .ring-emerald-300, .zone-dashboard .focus\\:ring-emerald-300:focus { --tw-ring-color: ${zoneTheme.hex}60 !important; }
      `}</style>

      {/* Onboarding widget — fixed bottom-left, visible until 100% */}
      <CustomerOnboarding
        tr={language === 'tr'}
        onNavigate={handleTabChange}
        onStepComplete={onboardingRefresh}
        onOpenWizard={() => setShowWizard(true)}
      />
      <nav className="sticky top-0 z-40 border-b border-white/10" style={{background: `linear-gradient(135deg, ${zoneTheme.hex} 0%, ${zoneTheme.hex}dd 100%)`}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo + Zone Identity */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 rounded-xl bg-white/20 flex flex-col items-center justify-center">
                  <span className="text-white font-black text-[9px] leading-none tracking-tight">COP</span>
                  <span className="text-white font-black text-sm leading-none">31</span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm">COP 31 Antalya</span>
                    <span className="bg-white/20 text-white text-[9px] font-bold px-2 py-0.5 rounded-full tracking-widest">{zoneTheme.label.toUpperCase()}</span>
                  </div>
                  <p className="text-white/60 text-[10px]">9-20 Kasım 2026</p>
                </div>
              </div>
              {/* Country flag + name */}
              {userInfo.country && (
                <div className="hidden md:flex items-center gap-2 bg-white/10 rounded-full px-3 py-1">
                  <span className="text-lg">{countryFlag}</span>
                  <span className="text-white text-xs font-semibold">{userInfo.country}</span>
                </div>
              )}
            </div>
            {/* Right actions */}
            <div className="flex items-center gap-3">
              <NotificationBell
                language={language}
                onNavigate={(link) => {
                  // Route hints: /musteri-admin/orders/:id → switch to Orders tab
                  if (link && link.includes('/musteri-admin/orders/')) handleTabChange('orders');
                }}
              />
              <Button variant="ghost" size="sm" className="rounded-full relative text-white hover:bg-white/10" onClick={() => setShowCart(true)}>
                <ShoppingCart className="h-5 w-5" />
                {cart.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-white text-xs rounded-full flex items-center justify-center font-bold" style={{color: zoneTheme.hex}}>
                    {cart.length}
                  </span>
                )}
              </Button>
              <LanguageToggle variant="admin" />
              <Button onClick={handleLogout} variant="ghost" className="rounded-full text-white hover:bg-white/10 text-sm gap-2">
                <LogOut className="h-4 w-4" />
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Hero Banner ── */}
      <div className={`bg-gradient-to-r ${zoneTheme.gradient} text-white`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Welcome */}
            <div className="flex items-center gap-4">
              <div className="text-5xl">{countryFlag}</div>
              <div>
                <p className="text-white/70 text-xs font-semibold tracking-widest uppercase mb-0.5">{zoneTheme.label}</p>
                <h2 className="text-2xl font-black text-white leading-tight">
                  {language === 'tr' ? 'Hoş Geldiniz' : 'Welcome'},&nbsp;
                  {/* Phase 86AZ.17 — Sub-users see THEIR own first name in
                      the greeting (not the tenant's main user). For the
                      main customer_admin, profileData wins (since it carries
                      authorized_person details that may differ from login). */}
                  {((userInfo.role === 'sub_user' ? userInfo.full_name : (profileData?.full_name || userInfo.full_name)) || '').split(' ')[0] || (language === 'tr' ? 'Katılımcı' : 'Participant')}
                  {/* Green Zone: Show organization, Blue Zone: Show country */}
                  {userInfo.zone === 'green-zone' && userInfo.organization && (
                    <span className="text-white/60 font-normal text-lg"> · {userInfo.organization}</span>
                  )}
                  {userInfo.zone === 'blue-zone' && userInfo.country && (
                    <span className="text-white/60 font-normal text-lg"> · {userInfo.country}</span>
                  )}
                </h2>
              </div>
            </div>
            {/* Stats */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="bg-white/15 backdrop-blur rounded-2xl px-4 py-3 text-center min-w-[80px]">
                <p className="text-2xl font-black text-white">{daysUntil}</p>
                <p className="text-white/70 text-[10px] font-semibold uppercase tracking-wide">{language === 'tr' ? 'Gün Kaldı' : 'Days Left'}</p>
              </div>
              <div className="bg-white/15 backdrop-blur rounded-2xl px-4 py-3 text-center min-w-[80px]">
                <p className="text-2xl font-black text-white">{(myLayout && myLayout.base_width) ? `${myLayout.total_area}m²` : '—'}</p>
                <p className="text-white/70 text-[10px] font-semibold uppercase tracking-wide">{language === 'tr' ? 'Stand Alanı' : 'Stand Area'}</p>
              </div>
              <div className="bg-white/15 backdrop-blur rounded-2xl px-4 py-3 text-center min-w-[80px]">
                <p className="text-2xl font-black text-white">{orders.length}</p>
                <p className="text-white/70 text-[10px] font-semibold uppercase tracking-wide">{language === 'tr' ? 'Sipariş' : 'Orders'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {standId ? (
          <ModularStandDetailBody standId={standId} />
        ) : (
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          {(() => {
            // Phase 86AZ.2 — When admin bound a specific Modular Stand
            // Package to this customer at assignment time, the "Custom
            // Stands" tab is locked out (modular package + custom design
            // are mutually exclusive). We render 7 tabs instead of 8.
            const hasAssignedModular = !!myLayout?.assigned_modular_package_id;
            const tabsColsClass = hasAssignedModular ? 'grid-cols-7' : 'grid-cols-8';
            return (
          <TabsList className={`grid w-full ${tabsColsClass}`}>
            <TabsTrigger value="dashboard">{language === 'tr' ? 'Dashboard' : 'Dashboard'}</TabsTrigger>
            <TabsTrigger value="layout">{t('my_layout')}</TabsTrigger>
            <TabsTrigger value="modular-stands" data-testid="modular-stands-tab-trigger">
              {hasAssignedModular
                ? (language === 'tr' ? 'Standım' : 'My Stand')
                : (language === 'tr' ? 'Modüler Standlar' : 'Modular Stands')}
            </TabsTrigger>
            <TabsTrigger value="products">{t('browse_products')}</TabsTrigger>
            <TabsTrigger value="orders">{t('my_orders')}</TabsTrigger>
            {!hasAssignedModular && (
              <TabsTrigger value="design" data-testid="design-tab-trigger" className="relative">
                {language === 'tr' ? 'Özel Standlar' : 'Custom Stands'}
                {standUnreadCount > 0 && (
                  <span className="absolute -top-1 -right-0.5 h-4.5 min-w-[18px] px-1 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center leading-none animate-pulse" style={{fontSize:10,minWidth:18,height:18}}>
                    {standUnreadCount}
                  </span>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger value="users">
              <Users className="h-4 w-4 mr-1.5" />
              {language === 'tr' ? 'Kullanıcılar' : 'Users'}
            </TabsTrigger>
            <TabsTrigger value="profile">{t('profile')}</TabsTrigger>
          </TabsList>
            );
          })()}

          {/* ── DASHBOARD TAB — comprehensive info dashboard ── */}
          <TabsContent value="dashboard" className="space-y-5">
            {(() => { const tr = language === 'tr'; return (<>
            {/* ── Action Items strip — color-coded alerts surfacing what
                 the customer needs to act on. Hidden when nothing pending. */}
            {(() => {
              const standAllocated = !!myLayout?.stand_no;
              const standSubmittedAt = profileData?.stand_request_submitted_at;
              const standUnderReview = !standAllocated && profileData?.stand_request_status === 'under_review';
              const standPending     = !standAllocated && !standSubmittedAt;
              const totalProfileFields = ['full_name','email','company','phone','about','industry','website','address','tax_id'];
              const filledFields = totalProfileFields.filter(k => profileData && (profileData[k] || '').toString().trim()).length;
              const profilePct = Math.round((filledFields / totalProfileFields.length) * 100);
              const items = [];
              if (standPending) items.push({
                kind: 'attention',
                icon: AlertCircle,
                title: tr ? 'Stand Alanı Talebi Gönderin' : 'Submit Your Stand Area Request',
                msg:   tr ? 'Henüz bir stand alanı talebiniz yok. Hemen gönderin.' : 'You haven\'t submitted a stand area request yet.',
                cta:   tr ? 'Şimdi Gönder' : 'Submit Now',
                // Phase 86AV — Stand area request form lives in the Custom
                // Stands ("design") tab, not in My Stand Layout.
                onClick: () => handleTabChange('design'),
              });
              if (standUnderReview) items.push({
                kind: 'info',
                icon: Clock,
                title: tr ? 'Talebiniz İnceleniyor' : 'Your Request is Under Review',
                msg:   tr ? 'Yönetici talebinize en kısa sürede dönecek.' : 'An admin will respond to your request shortly.',
                cta:   tr ? 'Detayları Gör' : 'View Details',
                onClick: () => handleTabChange('layout'),
              });
              // Phase 86AX — When customer has explicitly approved the stand
              // area the dashboard hero pivots from "Allocated" → "Approved".
              const standApproved = standAllocated && (myLayout?.area_approval_status === 'approved');
              if (standAllocated) items.push({
                kind: 'success',
                icon: CheckCircle2,
                title: standApproved
                  ? (tr ? `✓ Stand Onaylandı: ${myLayout.stand_no}` : `✓ Stand Approved: ${myLayout.stand_no}`)
                  : (tr ? `🎉 Stand Atandı: ${myLayout.stand_no}` : `🎉 Stand Allocated: ${myLayout.stand_no}`),
                msg:   tr ? `${myLayout.total_area || 0} m² · Stand düzeninizi hazırlayın` : `${myLayout.total_area || 0} m² · prepare your stand layout`,
                cta:   tr ? 'Düzeni Aç' : 'Open Layout',
                onClick: () => handleTabChange('layout'),
              });
              if (profilePct < 100 && profileData) items.push({
                kind: 'attention',
                icon: AlertCircle,
                title: tr ? `Profil Tamamlama: %${profilePct}` : `Profile Completion: ${profilePct}%`,
                msg:   tr ? `${totalProfileFields.length - filledFields} alan boş. Profilinizi tamamlayın.` : `${totalProfileFields.length - filledFields} fields are empty. Complete your profile.`,
                cta:   tr ? 'Profile Git' : 'Go to Profile',
                onClick: () => handleTabChange('profile'),
              });
              if (items.length === 0) return null;
              const styleByKind = {
                attention: 'bg-amber-50 border-amber-200 text-amber-900',
                info:      'bg-blue-50  border-blue-200  text-blue-900',
                success:   'bg-emerald-50 border-emerald-200 text-emerald-900',
              };
              const iconByKind = {
                attention: 'text-amber-600',
                info:      'text-blue-600',
                success:   'text-emerald-600',
              };
              return (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((it, i) => {
                    const Icon = it.icon;
                    return (
                      <div key={i} className={`rounded-2xl border ${styleByKind[it.kind]} p-4 flex items-start gap-3`}>
                        <div className={`flex-shrink-0 ${iconByKind[it.kind]}`}><Icon className="h-5 w-5" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold leading-tight">{it.title}</p>
                          <p className="text-xs opacity-80 mt-0.5 leading-snug">{it.msg}</p>
                          <button onClick={it.onClick}
                            className="text-[11px] font-bold inline-flex items-center gap-1 mt-2 hover:underline">
                            {it.cta} <ChevronRight className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ── Hero metric row — 4 high-density KPI cards.
                 Each card uses a colored accent stripe and a mini contextual
                 sub-line so the whole dashboard reads at a glance. */}
            {(() => {
              const totalSpent = (orders || []).reduce((sum, o) => sum + (o.total_amount || 0), 0);
              const standArea  = myLayout?.total_area || 0;
              const teamSize   = (invitationData?.subUsers?.length || 0) + 1;
              const cards = [
                {
                  label:  tr ? 'Etkinliğe Kalan' : 'Days to Event',
                  value:  daysUntil,
                  unit:   tr ? 'gün' : 'days',
                  Icon:   Calendar,
                  accent: '#3b82f6',
                  bg:     'from-blue-50 to-blue-100',
                  sub:    '9 Nov 2026',
                  pct:    Math.min(100, Math.max(0, ((500 - daysUntil) / 500) * 100)),
                },
                {
                  label:  tr ? 'Stand Alanı' : 'Stand Area',
                  value:  standArea || '—',
                  unit:   standArea ? 'm²' : '',
                  Icon:   Building2,
                  accent: zoneTheme.hex,
                  bg:     `from-${zoneTheme.tone}-50 to-${zoneTheme.tone}-100`,
                  sub:    myLayout?.stand_no
                    ? `${tr ? 'Stand' : 'Stand'} ${myLayout.stand_no}`
                    : (tr ? 'Henüz atanmadı' : 'Not assigned yet'),
                  badge:  myLayout?.stand_no ? (tr ? 'Atandı' : 'Allocated') : null,
                },
                {
                  label:  tr ? 'Toplam Harcama' : 'Total Spent',
                  value:  totalSpent ? fmt(totalSpent) : '—',
                  unit:   totalSpent ? 'USD' : '',
                  Icon:   DollarSign,
                  accent: '#16a34a',
                  bg:     'from-emerald-50 to-emerald-100',
                  sub:    `${(orders || []).length} ${tr ? 'sipariş' : 'orders'}`,
                },
                {
                  label:  tr ? 'Ekip Üyeleri' : 'Team Members',
                  value:  teamSize,
                  unit:   '',
                  Icon:   Users,
                  accent: '#a855f7',
                  bg:     'from-violet-50 to-violet-100',
                  sub:    tr ? 'Davet etmek için tıklayın' : 'Click to invite more',
                  onClick: () => handleTabChange('users'),
                },
              ];
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {cards.map((c, i) => {
                    const Icon = c.Icon;
                    return (
                      <div
                        key={i}
                        onClick={c.onClick}
                        className={`relative bg-gradient-to-br ${c.bg} rounded-2xl border border-white/60 shadow-sm overflow-hidden ${c.onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                        data-testid={`dashboard-kpi-${i}`}>
                        <div className="absolute top-0 left-0 h-full w-1" style={{background: c.accent}} />
                        <div className="px-4 py-4 flex items-start gap-3">
                          <div className="h-10 w-10 rounded-xl bg-white/70 flex items-center justify-center flex-shrink-0 shadow-sm">
                            <Icon className="h-5 w-5" style={{color: c.accent}} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-wide font-bold text-gray-500">{c.label}</p>
                            <div className="flex items-baseline gap-1 mt-0.5">
                              <span className="text-2xl font-black text-gray-900 leading-none">{c.value}</span>
                              {c.unit && <span className="text-xs font-semibold text-gray-500">{c.unit}</span>}
                              {c.badge && <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500 text-white">{c.badge}</span>}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-1 truncate">{c.sub}</p>
                            {c.pct !== undefined && (
                              <div className="mt-2 h-1 bg-white/60 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{width: `${c.pct}%`, background: c.accent}} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ── Three-column layout: Quick Actions · Stand Detail · Event Card */}
            <div className="grid lg:grid-cols-3 gap-4">
              {/* ── LEFT col (lg:span-1): Quick Actions ── */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 lg:col-span-1">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="h-4 w-4 text-violet-500" />
                  <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wide">{tr ? 'Hızlı Erişim' : 'Quick Access'}</h3>
                </div>
                <div className="space-y-1.5">
                  {[
                    { label: tr ? 'Stand Düzenim' : 'My Stand Layout', tab: 'layout', icon: '🗺️', color: 'bg-blue-50 text-blue-600', desc: myLayout?.stand_no ? `${myLayout.total_area}m² · ${myLayout.stand_no}` : (tr ? 'Hazırlanıyor' : 'Pending') },
                    { label: tr ? 'Ürünleri Keşfet' : 'Browse Products', tab: 'products', icon: '🛍️', color: 'bg-amber-50 text-amber-600', desc: tr ? 'Stand ekipmanları' : 'Stand equipment' },
                    { label: tr ? 'Stand Servisleri' : 'Stand Services', href: `/musteri-admin/${slug}/stand-services`, icon: '⚡', color: 'bg-cyan-50 text-cyan-600', desc: tr ? 'Elektrik, internet, temizlik' : 'Electricity, internet, cleaning', badge: standServicesNewCount },
                    { label: tr ? 'Siparişlerim' : 'My Orders', tab: 'orders', icon: '📋', color: 'bg-emerald-50 text-emerald-600', desc: `${(orders||[]).length} ${tr ? 'sipariş' : 'orders'}` },
                    { label: tr ? 'Ekibimi Yönet' : 'Manage Team', tab: 'users', icon: '👥', color: 'bg-violet-50 text-violet-600', desc: tr ? 'Kullanıcı ekle' : 'Add users' },
                  ].map((item, i) => (
                    <button
                      key={i}
                      onClick={() => item.href ? navigate(item.href) : handleTabChange(item.tab)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left group">
                      <span className={`h-9 w-9 rounded-xl ${item.color} flex items-center justify-center text-base flex-shrink-0`}>{item.icon}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-800 group-hover:text-blue-600 transition-colors">{item.label}</p>
                          {item.badge > 0 && (
                            <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none" data-testid={`quick-badge-${i}`}>
                              {item.badge} {tr ? 'yeni' : 'new'}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-gray-400">{item.desc}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-blue-400 transition-colors flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>

              {/* ── MIDDLE col (lg:span-1): Stand Status detail ── */}
              <div className={`rounded-2xl shadow-sm overflow-hidden lg:col-span-1 ${myLayout?.stand_no ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white' : 'bg-white border border-gray-100'}`}>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <MapPin className={`h-4 w-4 ${myLayout?.stand_no ? 'text-white/80' : 'text-gray-400'}`} />
                      <h3 className={`text-xs uppercase tracking-wide font-bold ${myLayout?.stand_no ? 'text-white/80' : 'text-gray-500'}`}>{tr ? 'Stand Durumu' : 'Stand Status'}</h3>
                    </div>
                    {myLayout?.stand_no && (
                      <span className="bg-white/20 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                        {myLayout.area_approval_status === 'approved'
                          ? (tr ? 'ONAYLANDI' : 'APPROVED')
                          : (tr ? 'TAHSİS' : 'ALLOCATED')}
                      </span>
                    )}
                  </div>
                  {myLayout?.stand_no ? (
                    <>
                      <p className="text-3xl font-black mb-1">{myLayout.stand_no}</p>
                      <p className="text-white/80 text-sm mb-3">{myLayout.hall_name || ''}</p>
                      {/* Phase 86AU — Stand Type badge (admin-assigned).
                          Shows the customer "Modular" or "Space Only" upfront
                          so they understand the type before ordering.
                          Phase 86AZ.5 — When a Modular Package is bound to
                          the stand, surface its name right under the badge
                          so the customer sees exactly what they got. */}
                      {(myLayout.stand_type === 'modular' || myLayout.stand_type === 'space_only') && (
                        <div className="mb-3 space-y-1.5" data-testid="customer-stand-type-badge">
                          {myLayout.stand_type === 'modular' ? (
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/95 text-emerald-700 shadow-sm">
                              <Package className="w-3 h-3" />
                              {tr ? 'Modüler Stand' : 'Modular Stand'}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-white/95 text-sky-700 shadow-sm">
                              <Square className="w-3 h-3" />
                              {tr ? 'Sadece Alan' : 'Space Only'}
                            </span>
                          )}
                          {myLayout.stand_type === 'modular' && myLayout.assigned_modular_package && (() => {
                            const pkg = myLayout.assigned_modular_package;
                            const pkgName = (tr ? (pkg.name_tr || pkg.name) : (pkg.name_en || pkg.name)) || '';
                            if (!pkgName) return null;
                            return (
                              <div
                                className="text-[11px] text-white/95 leading-snug flex items-start gap-1.5"
                                data-testid="customer-assigned-package-name"
                                title={pkgName}
                              >
                                <span className="inline-block w-1 h-1 rounded-full bg-white/80 flex-shrink-0 mt-1.5" />
                                <span className="font-semibold break-words">{pkgName}</span>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2 mb-4">
                        <div className="bg-white/15 rounded-xl px-3 py-2">
                          <p className="text-[10px] text-white/70 uppercase font-bold">{tr ? 'Alan' : 'Area'}</p>
                          <p className="text-base font-bold">{myLayout.total_area || 0}m²</p>
                        </div>
                        <div className="bg-white/15 rounded-xl px-3 py-2">
                          <p className="text-[10px] text-white/70 uppercase font-bold">{tr ? 'Açık Cephe' : 'Open Sides'}</p>
                          <p className="text-base font-bold">{(myLayout.open_sides || []).length}</p>
                        </div>
                      </div>
                      <button onClick={() => handleTabChange('layout')}
                        className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white text-emerald-700 text-sm font-bold hover:bg-white/90 transition-colors">
                        <Eye className="h-4 w-4" /> {tr ? 'Stand\'ı Görüntüle' : 'View Stand'}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="text-center py-3">
                        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                          <Clock className="h-7 w-7 text-gray-400" />
                        </div>
                        <p className="text-sm font-bold text-gray-700 mb-1">
                          {requestStatusForDashboard(profileData) === 'pending'   && (tr ? 'Talep Bekliyor' : 'Request Pending')}
                          {requestStatusForDashboard(profileData) === 'under_review' && (tr ? 'İnceleme Aşamasında' : 'Under Review')}
                          {requestStatusForDashboard(profileData) === 'none'      && (tr ? 'Henüz Talep Yok' : 'No Request Yet')}
                        </p>
                        <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                          {requestStatusForDashboard(profileData) === 'none'
                            ? (tr ? 'Stand alanı için talebinizi gönderin.' : 'Submit your stand area request.')
                            : (tr ? 'Yönetici talebinizi değerlendiriyor.' : 'An admin is reviewing your request.')}
                        </p>
                      </div>
                      <button onClick={() => handleTabChange(requestStatusForDashboard(profileData) === 'none' ? 'design' : 'layout')}
                        className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-bold transition-colors"
                        style={{background: zoneTheme.hex}}>
                        {requestStatusForDashboard(profileData) === 'none'
                          ? (tr ? 'Talebi Gönder' : 'Submit Request')
                          : (tr ? 'Detayları Aç' : 'Open Details')}
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* ── RIGHT col (lg:span-1): Event Card ── */}
              <div className={`bg-gradient-to-br ${zoneTheme.gradient} rounded-2xl p-5 text-white relative overflow-hidden lg:col-span-1`}>
                <div className="absolute top-0 right-0 h-32 w-32 bg-white/10 rounded-full -translate-y-12 translate-x-12 blur-2xl" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-white/20 text-white text-[10px] font-black px-2 py-0.5 rounded-full tracking-widest">COP 31</span>
                    <span className="bg-white/15 text-white/90 text-[10px] font-semibold px-2 py-0.5 rounded-full">{(zoneTheme.label || '').toUpperCase()}</span>
                  </div>
                  <h4 className="font-black text-2xl leading-tight mb-1">Antalya 2026</h4>
                  <p className="text-white/70 text-xs mb-4">9–20 Kasım · Nov 9-20</p>
                  <div className="flex items-baseline gap-2 mb-3">
                    <p className="text-5xl font-black">{daysUntil}</p>
                    <p className="text-white/80 text-sm font-bold">{tr ? 'gün kaldı' : 'days left'}</p>
                  </div>
                  <div className="h-1.5 bg-white/20 rounded-full overflow-hidden mb-4">
                    <div className="h-full bg-white rounded-full transition-all" style={{width: `${Math.min(100, Math.max(0, 100 - (daysUntil / 500) * 100))}%`}} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white/15 rounded-xl px-3 py-2">
                      <p className="text-white/70 text-[10px] uppercase font-bold">{tr ? 'Konum' : 'Venue'}</p>
                      <p className="font-bold">Antalya, TR</p>
                    </div>
                    <div className="bg-white/15 rounded-xl px-3 py-2">
                      <p className="text-white/70 text-[10px] uppercase font-bold">{tr ? 'Süre' : 'Duration'}</p>
                      <p className="font-bold">12 {tr ? 'gün' : 'days'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Recent activity row: Orders + Notifications side by side ── */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Recent Orders — always visible (empty state if none) */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-emerald-500" />
                    <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wide">{tr ? 'Son Siparişler' : 'Recent Orders'}</h3>
                  </div>
                  {(orders || []).length > 0 && (
                    <button onClick={() => handleTabChange('orders')} className="text-xs font-semibold flex items-center gap-1 hover:underline" style={{color: zoneTheme.hex}}>
                      {tr ? 'Tümünü Gör' : 'View All'} <ChevronRight className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {(orders || []).length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingBag className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">{tr ? 'Henüz sipariş yok' : 'No orders yet'}</p>
                    <button onClick={() => handleTabChange('products')}
                      className="text-xs font-semibold mt-3 hover:underline" style={{color: zoneTheme.hex}}>
                      {tr ? 'Alışverişe Başla →' : 'Start Shopping →'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {orders.slice(0, 4).map((order, i) => (
                      <div key={i} className="flex items-center justify-between py-2 px-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                            <Package className="h-4 w-4 text-emerald-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate">#{order.id?.slice(-8)?.toUpperCase()}</p>
                            <p className="text-[11px] text-gray-400">{(order.items||[]).length} {tr ? 'ürün' : 'items'}</p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-bold" style={{color: zoneTheme.hex}}>{order.currency || 'USD'} {fmt(order.total_amount || 0)}</p>
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${getStatusColor(order.status)}`}>{getStatusLabel(order.status, language)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Activity Timeline — synthesized from orders + stand events */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Activity className="h-4 w-4 text-blue-500" />
                  <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wide">{tr ? 'Son Hareketler' : 'Recent Activity'}</h3>
                </div>
                {(() => {
                  const events = [];
                  if (myLayout?.stand_no) events.push({
                    icon: '🎉', text: tr ? `${myLayout.stand_no} numaralı stand size atandı` : `Stand ${myLayout.stand_no} assigned to you`,
                    when: profileData?.stand_request_decided_at,
                  });
                  if (profileData?.stand_request_reviewed_at && !myLayout?.stand_no)  events.push({
                    icon: '🔍', text: tr ? 'Stand talebi inceleniyor' : 'Stand request under review',
                    when: profileData.stand_request_reviewed_at,
                  });
                  if (profileData?.stand_request_submitted_at) events.push({
                    icon: '📤', text: tr ? 'Stand alanı talebi gönderildi' : 'Stand area request submitted',
                    when: profileData.stand_request_submitted_at,
                  });
                  (orders || []).slice(0, 3).forEach(o => events.push({
                    icon: '🛒', text: tr ? `Sipariş #${o.id?.slice(-6).toUpperCase()} oluşturuldu` : `Order #${o.id?.slice(-6).toUpperCase()} placed`,
                    when: o.created_at,
                  }));
                  events.sort((a, b) => new Date(b.when || 0) - new Date(a.when || 0));
                  if (events.length === 0) {
                    return (
                      <div className="text-center py-8">
                        <Activity className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-400">{tr ? 'Henüz hareket yok' : 'No activity yet'}</p>
                      </div>
                    );
                  }
                  return (
                    <ol className="space-y-3">
                      {events.slice(0, 5).map((e, i) => {
                        const when = e.when ? new Date(e.when) : null;
                        const whenStr = when ? when.toLocaleString(tr ? 'tr-TR' : 'en-US', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
                        return (
                          <li key={i} className="flex items-start gap-3 relative">
                            <span className="text-lg flex-shrink-0">{e.icon}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-700 leading-snug">{e.text}</p>
                              <p className="text-[11px] text-gray-400 mt-0.5">{whenStr}</p>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  );
                })()}
              </div>
            </div>
            </>); })()}
          </TabsContent>

          <TabsContent value="layout" className="space-y-6">
            <Card className="border-0 shadow-lg rounded-3xl">
              <CardHeader>
                <CardTitle className="text-2xl">{t('my_layout')}</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Faz B — SCR status banner: ALWAYS keeps the underlying
                    stand layout visible. We render this above the layout
                    grid so the user sees both their current stand AND the
                    request status simultaneously. */}
                {(() => {
                  if (!myLayout || !myLayout.stand_no) return null;
                  // Find the most relevant SCR (priority: awaiting_customer_choice > pending > rejected)
                  const scrs = changeRequests || [];
                  const awaiting = scrs.find(r => r.status === 'awaiting_customer_choice');
                  const pending  = scrs.find(r => r.status === 'pending');
                  const rejected = scrs
                    .filter(r => r.status === 'rejected')
                    .sort((a, b) => (b.reviewed_at || '').localeCompare(a.reviewed_at || ''))[0];
                  const scr = awaiting || pending || rejected;
                  if (!scr) return null;

                  const isAwaiting = scr.status === 'awaiting_customer_choice';
                  const isPending  = scr.status === 'pending';
                  const isRejected = scr.status === 'rejected';

                  const tone = isAwaiting
                    ? { bg: 'bg-blue-50',  border: 'border-blue-200',  fg: 'text-blue-800',  pillBg: 'bg-blue-600',  emoji: '✨' }
                    : isPending
                      ? { bg: 'bg-amber-50', border: 'border-amber-200', fg: 'text-amber-800', pillBg: 'bg-amber-500', emoji: '⏳' }
                      : { bg: 'bg-red-50',   border: 'border-red-200',   fg: 'text-red-800',   pillBg: 'bg-red-500',   emoji: '⚠' };

                  return (
                    <div data-testid={`scr-status-banner-${scr.status}`}
                         className={`mb-5 rounded-2xl border ${tone.bg} ${tone.border} px-5 py-4`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-bold text-white ${tone.pillBg}`}>
                              <span>{tone.emoji}</span>
                              {language === 'tr'
                                ? (isAwaiting ? 'Alternatifler Hazır' : isPending ? 'Talep Beklemede' : 'Talep Reddedildi')
                                : (isAwaiting ? 'Alternatives Ready' : isPending ? 'Request Pending' : 'Request Rejected')}
                            </span>
                            <p className={`font-bold ${tone.fg}`}>
                              {language === 'tr'
                                ? (isAwaiting ? 'Yeni stand seçenekleri sunuldu'
                                  : isPending ? 'Stand değişiklik talebiniz inceleniyor'
                                  : 'Stand değişiklik talebiniz onaylanmadı')
                                : (isAwaiting ? 'New stand alternatives proposed'
                                  : isPending ? 'Your stand change request is under review'
                                  : 'Your stand change request was not approved')}
                            </p>
                          </div>
                          <p className={`text-xs mt-1.5 ${tone.fg} opacity-80`}>
                            {language === 'tr'
                              ? (isAwaiting ? 'Mevcut standınız aşağıda görünüyor. Alternatifleri inceleyip kabul ettiğinizde yeni alanınıza geçiş yapılır.'
                                : isPending ? 'Mevcut standınız aşağıda görünüyor. Organizasyon ekibimiz talebinizi değerlendirmektedir.'
                                : 'Mevcut standınız aşağıda görünüyor. Yeni bir talep oluşturabilirsiniz.')
                              : (isAwaiting ? 'Your current stand stays below. Reviewing & accepting an alternative will move you to a new spot.'
                                : isPending ? 'Your current stand stays below. The organization team is reviewing your request.'
                                : 'Your current stand stays below. You can submit a new request.')}
                          </p>
                          {scr.admin_note && isRejected && (
                            <p className="text-xs mt-2 italic text-red-600 bg-white/60 px-3 py-1.5 rounded-lg border border-red-100">
                              {language === 'tr' ? 'Admin notu: ' : 'Admin note: '}{scr.admin_note}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {layoutLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin h-12 w-12 border-4 border-t-transparent rounded-full mx-auto mb-4" style={{borderColor: `${zoneTheme.hex}40`, borderTopColor: 'transparent', borderLeftColor: zoneTheme.hex}}></div>
                    <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
                  </div>
                ) : !myLayout || !myLayout.base_width || !myLayout.stand_no ? (
                  <div className="relative rounded-2xl overflow-hidden bg-gray-50 border border-gray-100" style={{height: '320px'}}>
                    {/* SVG placeholder floor plan */}
                    <svg viewBox="0 0 500 320" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full opacity-20 select-none pointer-events-none" xmlns="http://www.w3.org/2000/svg">
                      {/* Grid lines */}
                      {Array.from({length: 26}).map((_, i) => (
                        <line key={`v${i}`} x1={i * 20} y1={0} x2={i * 20} y2={320} stroke="#94a3b8" strokeWidth="0.5"/>
                      ))}
                      {Array.from({length: 17}).map((_, i) => (
                        <line key={`h${i}`} x1={0} y1={i * 20} x2={500} y2={i * 20} stroke="#94a3b8" strokeWidth="0.5"/>
                      ))}
                      {/* Stand shapes */}
                      <rect x="20" y="20" width="100" height="80" rx="4" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
                      <rect x="140" y="20" width="120" height="60" rx="4" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
                      <rect x="280" y="20" width="80" height="100" rx="4" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
                      <rect x="380" y="20" width="100" height="60" rx="4" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
                      <rect x="20" y="140" width="140" height="100" rx="4" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
                      <rect x="180" y="120" width="100" height="80" rx="4" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
                      <rect x="300" y="140" width="80" height="60" rx="4" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
                      <rect x="400" y="120" width="80" height="80" rx="4" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
                      <rect x="20" y="260" width="200" height="40" rx="4" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
                      <rect x="240" y="240" width="140" height="60" rx="4" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
                      <rect x="400" y="220" width="80" height="80" rx="4" fill="#bfdbfe" stroke="#3b82f6" strokeWidth="1.5"/>
                      {/* Main aisle */}
                      <rect x="140" y="100" width="120" height="20" rx="2" fill="#e0f2fe" stroke="#7dd3fc" strokeWidth="1"/>
                      <rect x="0" y="220" width="500" height="20" rx="2" fill="#e0f2fe" stroke="#7dd3fc" strokeWidth="1"/>
                    </svg>
                    {/* Overlay */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                      {profileData?.stand_request_submitted_at ? (
                        // Phase 86V — Request submitted but not yet allocated.
                        // Show a summary of what was requested + a promise
                        // that the customer will be pinged the moment an
                        // admin allocates a stand. Avoids the previous empty
                        // "Team working" loading state.
                        <div className="bg-white/95 backdrop-blur-sm rounded-3xl px-7 py-6 shadow-sm border border-gray-100 max-w-md w-full text-left" data-testid="stand-request-summary-card">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{background: `${zoneTheme.hex}15`}}>
                              <MapPin className="h-5 w-5" style={{color: zoneTheme.hex}}/>
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-bold text-gray-900 text-base leading-tight">
                                {language === 'tr' ? 'Stand Talebiniz Alındı' : 'Your Stand Request Received'}
                              </h3>
                              <p className="text-[11px] text-gray-500 mt-0.5">
                                {language === 'tr'
                                  ? 'Organizasyon ekibi değerlendirme aşamasında'
                                  : 'Organizer team is reviewing your request'}
                              </p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 mb-4">
                            <div className="bg-gray-50 rounded-xl px-3 py-2">
                              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{language === 'tr' ? 'Alan' : 'Area'}</p>
                              <p className="text-sm font-bold text-gray-800 mt-0.5">
                                {profileData?.stand_area ? `${profileData.stand_area} m²` : '—'}
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-xl px-3 py-2">
                              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{language === 'tr' ? 'Tasarım' : 'Design'}</p>
                              <p className="text-sm font-bold text-gray-800 mt-0.5 capitalize">
                                {profileData?.stand_design_type === 'custom'
                                  ? (language === 'tr' ? 'Özel' : 'Custom')
                                  : profileData?.stand_design_type === 'modular'
                                    ? (language === 'tr' ? 'Modüler' : 'Modular')
                                    : '—'}
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-xl px-3 py-2">
                              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{language === 'tr' ? 'Yerleşim' : 'Layout'}</p>
                              <p className="text-sm font-bold text-gray-800 mt-0.5 capitalize">
                                {profileData?.stand_layout_type || '—'}
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-xl px-3 py-2">
                              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{language === 'tr' ? 'Talep' : 'Submitted'}</p>
                              <p className="text-sm font-bold text-gray-800 mt-0.5">
                                {new Date(profileData.stand_request_submitted_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                          {profileData?.stand_request_notes && (
                            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg px-3 py-2 mb-4">
                              <p className="text-[9px] font-bold text-amber-700 uppercase tracking-wider mb-0.5">
                                {language === 'tr' ? 'Notunuz' : 'Your Note'}
                              </p>
                              <p className="text-xs text-amber-900 leading-relaxed line-clamp-3">{profileData.stand_request_notes}</p>
                            </div>
                          )}
                          <div className="flex items-start gap-2 bg-blue-50 rounded-xl px-3 py-2.5">
                            <Bell className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-blue-900 leading-relaxed">
                              {language === 'tr'
                                ? 'Stand ataması tamamlandığında e-posta ve bildirim çanıyla (sağ üst) haberdar edileceksiniz. Layout bu sayfada otomatik görünecek.'
                                : 'You will be notified via email and the bell icon (top-right) the moment your stand is assigned. The layout will appear here automatically.'}
                            </p>
                          </div>
                          <div className="mt-3 inline-flex items-center gap-2 text-[11px] font-semibold rounded-full px-3 py-1.5"
                               style={{background: `${zoneTheme.hex}15`, color: zoneTheme.hex}}>
                            <span className="animate-pulse h-1.5 w-1.5 rounded-full" style={{background: zoneTheme.hex}}></span>
                            {(() => {
                              const st = profileData?.stand_request_status || 'pending';
                              if (st === 'under_review') return language === 'tr' ? 'İnceleniyor' : 'Under Review';
                              return language === 'tr' ? 'Atama Bekleniyor' : 'Awaiting Allocation';
                            })()}
                          </div>
                        </div>
                      ) : (
                        <div className="bg-white/90 backdrop-blur-sm rounded-3xl px-8 py-6 shadow-sm border border-gray-100 max-w-sm">
                          <div className="h-14 w-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{background: `${zoneTheme.hex}15`}}>
                            <MapPin className="h-7 w-7" style={{color: zoneTheme.hex}}/>
                          </div>
                          <h3 className="font-bold text-gray-800 text-base mb-2">
                            {language === 'tr' ? 'Stand Düzeniniz Hazırlanıyor' : 'Your Stand Layout is Being Prepared'}
                          </h3>
                          <p className="text-gray-500 text-sm leading-relaxed">
                            {language === 'tr'
                              ? 'Stand düzeniniz en kısa sürede bu alanda görüntülenecektir. Hazır olduğunda bildirim alacaksınız.'
                              : 'Your stand layout will be displayed here shortly. You will receive a notification when it is ready.'}
                          </p>
                          <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1.5" style={{background: `${zoneTheme.hex}15`, color: zoneTheme.hex}}>
                            <span className="animate-pulse h-1.5 w-1.5 rounded-full" style={{background: zoneTheme.hex}}></span>
                            {language === 'tr' ? 'Organizasyon Ekibi Çalışıyor' : 'Organization Team Working'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="grid lg:grid-cols-5 gap-6">

                    {/* ── Sol kolon: Stand Bilgileri + Kenar Durumu ── */}
                    <div className="lg:col-span-2 flex flex-col gap-4">

                      {/* Stand Bilgileri */}
                      <div className="rounded-2xl p-5" style={{background: `${zoneTheme.hex}0d`, border: `1.5px solid ${zoneTheme.hex}30`}}>
                        <h3 className="font-bold text-base mb-4 flex items-center gap-2" style={{color: zoneTheme.hex}}>
                          <span className="h-4 w-1 rounded-full inline-block" style={{background: zoneTheme.hex}}></span>
                          {language === 'tr' ? 'Stand Bilgileri' : 'Stand Information'}
                        </h3>
                        <div className="space-y-3">
                          {/* Zone tipi */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 font-medium">{language === 'tr' ? 'Bölge' : 'Zone'}</span>
                            <span className="text-xs px-2.5 py-1 rounded-full font-bold text-white"
                              style={{background: zoneTheme.hex}}>
                              {(userInfo.zone === 'green_zone' || myLayout.zone_type === 'green_zone')
                                ? (language === 'tr' ? 'Yeşil Bölge' : 'Green Zone')
                                : (language === 'tr' ? 'Mavi Bölge' : 'Blue Zone')}
                            </span>
                          </div>
                          {/* Hall adı */}
                          {myLayout.hall_name && (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 font-medium">{language === 'tr' ? 'Hall' : 'Hall'}</span>
                              <span className="text-xs font-semibold text-gray-800 text-right max-w-32 truncate">{myLayout.hall_name}</span>
                            </div>
                          )}
                          {/* Stand No */}
                          {myLayout.stand_no && (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 font-medium">{language === 'tr' ? 'Stand No' : 'Stand No'}</span>
                              <span className="text-xs font-bold text-gray-900 font-mono bg-gray-100 px-2 py-0.5 rounded">{myLayout.stand_no}</span>
                            </div>
                          )}
                          {/* Boyutlar */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 font-medium">{language === 'tr' ? 'En × Boy' : 'W × H'}</span>
                            <span className="text-xs font-semibold text-gray-800">{myLayout.base_width}m × {myLayout.base_height}m</span>
                          </div>
                          {/* Alan */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 font-medium">{t('total_area')}</span>
                            <span className="text-sm font-black" style={{color: zoneTheme.hex}}>{myLayout.total_area}m²</span>
                          </div>
                          {/* Açık Kenarlar — Phase 86AZ: A,B,C,D olarak göster (north/south/east/west yerine) */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 font-medium">{language === 'tr' ? 'Açık Kenarlar' : 'Open Sides'}</span>
                            <span className="text-xs font-semibold text-gray-800">
                              {myLayout.open_sides && myLayout.open_sides.length > 0
                                ? myLayout.open_sides
                                    .map(s => ({north:'A', east:'B', south:'C', west:'D'}[s]))
                                    .filter(Boolean).sort().join(', ')
                                : '—'}
                            </span>
                          </div>
                          {/* Oluşturulma tarihi */}
                          {myLayout.created_at && (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 font-medium">{language === 'tr' ? 'Oluşturulma' : 'Created'}</span>
                              <span className="text-xs font-semibold text-gray-700">
                                {new Date(myLayout.created_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {day:'2-digit', month:'short', year:'numeric'})}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* ── Alan Onayı & Sözleşme Durumu ──
                          Phase 86AZ — Green Zone customers do NOT go through
                          the area-approval lifecycle. When Ultra Admin assigns
                          a Green Zone stand it's auto-approved server-side, so
                          hide the entire approval card for green_zone users. */}
                      {(userInfo.zone !== 'green_zone' && myLayout.zone_type !== 'green_zone') && (
                      <div className="rounded-2xl p-5 border-2" style={{
                        background: areaApprovalStatus === 'approved'
                          ? (contractInfo?.contract_status === 'signed' ? '#f0fdf4' : '#eff6ff')
                          : '#fffbeb',
                        borderColor: areaApprovalStatus === 'approved'
                          ? (contractInfo?.contract_status === 'signed' ? '#86efac' : '#93c5fd')
                          : '#fcd34d'
                      }}>
                        <h3 className="font-bold text-base mb-3 flex items-center gap-2" style={{
                          color: areaApprovalStatus === 'approved'
                            ? (contractInfo?.contract_status === 'signed' ? '#166534' : '#1d4ed8')
                            : '#92400e'
                        }}>
                          <span className="text-lg">
                            {contractInfo?.contract_status === 'signed' ? '✅' : areaApprovalStatus === 'approved' ? '📋' : '📌'}
                          </span>
                          {contractInfo?.contract_status === 'signed'
                            ? (language === 'tr' ? 'Sözleşme İmzalandı' : 'Contract Signed')
                            : areaApprovalStatus === 'approved'
                              ? (contractInfo?.contract_status === 'sent'
                                ? (language === 'tr' ? 'Sözleşme Gönderildi' : 'Contract Sent')
                                : (language === 'tr' ? 'Alan Onaylandı' : 'Area Approved'))
                              : (language === 'tr' ? 'Stand Alan Onayı' : 'Stand Area Approval')}
                        </h3>

                        {contractInfo?.contract_status === 'signed' ? (
                          <div className="space-y-2">
                            <p className="text-xs text-emerald-700 font-medium">
                              {language === 'tr' ? 'Sözleşmeniz dijital olarak imzalandı.' : 'Your contract has been digitally signed.'}
                            </p>
                            <div className="text-xs text-gray-500 space-y-1">
                              {contractInfo.contract_signer_name && (
                                <p><span className="font-semibold">{language === 'tr' ? 'İmzalayan:' : 'Signed by:'}</span> {contractInfo.contract_signer_name}</p>
                              )}
                              {contractInfo.contract_signed_at && (
                                <p><span className="font-semibold">{language === 'tr' ? 'İmza tarihi:' : 'Signed at:'}</span> {new Date(contractInfo.contract_signed_at).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US')}</p>
                              )}
                              {contractInfo.contract_total_amount && (
                                <p><span className="font-semibold">{language === 'tr' ? 'Toplam Tutar:' : 'Total Amount:'}</span> {contractInfo.contract_currency || 'USD'} {Number(contractInfo.contract_total_amount).toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                        ) : contractInfo?.contract_status === 'sent' ? (
                          <div className="space-y-2">
                            <p className="text-xs text-blue-700 font-medium">
                              {language === 'tr' ? 'Sözleşmeniz email adresinize gönderildi. Lütfen imzalayın.' : 'Your contract was sent to your email. Please sign it.'}
                            </p>
                            <div className="text-xs text-gray-500">
                              {contractInfo.contract_total_amount && (
                                <p><span className="font-semibold">{language === 'tr' ? 'Toplam Tutar:' : 'Total:'}</span> {contractInfo.contract_currency || 'USD'} {Number(contractInfo.contract_total_amount).toLocaleString()}</p>
                              )}
                            </div>
                            <p className="text-xs text-blue-500">{language === 'tr' ? 'Email\'inizdeki imzalama linkine tıklayın.' : 'Click the signing link in your email.'}</p>
                          </div>
                        ) : areaApprovalStatus === 'approved' ? (
                          <p className="text-xs text-blue-700">
                            {language === 'tr' ? 'Stand alanınızı onayladınız. Organizasyon ekibi sözleşmenizi hazırlıyor.' : 'You approved your stand area. The organization team is preparing your contract.'}
                          </p>
                        ) : (
                          <>
                            <p className="text-xs text-amber-700 mb-3 leading-relaxed">
                              {language === 'tr'
                                ? 'Stand alanınız atandı. Alanı onaylayarak sözleşme sürecini başlatın.'
                                : 'Your stand area has been assigned. Approve it to start the contract process.'}
                            </p>
                            <button
                              onClick={() => setApproveAreaModalOpen(true)}
                              disabled={areaApproving}
                              className="w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                              style={{background: 'linear-gradient(135deg, #d97706, #b45309)'}}
                              data-testid="approve-area-btn"
                            >
                              {areaApproving ? (
                                <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {language === 'tr' ? 'Onaylanıyor...' : 'Approving...'}</>
                              ) : (
                                <>{language === 'tr' ? '✓ Stand Alanımı Onaylıyorum' : '✓ I Approve My Stand Area'}</>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                      )}

                      {/* Phase 86AC — Post-Approval "Choose Your Way" card.
                          Surfaces the next-step decision the moment the
                          stand area is sold (customer-approved). Two paths:
                          • Modüler Stand → /modular-stands tab (browse and
                            buy a pre-built stand package).
                          • Özel Tasarım → /design tab (Stand Talebi page
                            for custom design requests / supplier handoff).
                          Both deep-link via the same setActiveTab/navigate
                          combo the rest of the dashboard uses.
                          Phase 86AZ.2 — Hide "Custom Design" path when the
                          admin already bound a specific Modular Package to
                          this customer (mutually exclusive paths). */}
                      {areaApprovalStatus === 'approved' && (() => {
                        const hasAssignedModular = !!myLayout?.assigned_modular_package_id;
                        return (
                        <div className="rounded-2xl p-5 border-2 border-purple-200 bg-gradient-to-br from-purple-50 via-fuchsia-50 to-rose-50" data-testid="choose-your-way-card">
                          <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="h-5 w-5 text-purple-600" />
                            <h3 className="font-bold text-base text-purple-900">
                              {language === 'tr' ? 'Yolunu Seç' : 'Choose Your Way'}
                            </h3>
                          </div>
                          <p className="text-xs text-purple-700/80 mb-4 leading-relaxed">
                            {hasAssignedModular
                              ? (language === 'tr'
                                  ? 'Stand alanınız onaylandı! Size atanan modüler paketi inceleyin.'
                                  : 'Your stand area is sold! Review your assigned modular package.')
                              : (language === 'tr'
                                  ? 'Stand alanınız onaylandı! Şimdi standınızı nasıl kuracağınızı seçin.'
                                  : 'Your stand area is sold! Now pick how you would like to build your stand.')}
                          </p>
                          <div className={`grid ${hasAssignedModular ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                            <button
                              onClick={() => {
                                handleTabChange('modular-stands');
                              }}
                              data-testid="choose-modular-btn"
                              className="group rounded-2xl bg-white border border-purple-200 hover:border-purple-400 hover:shadow-md transition-all p-4 text-left"
                            >
                              <div className="h-10 w-10 rounded-xl bg-indigo-100 flex items-center justify-center mb-3 group-hover:bg-indigo-200 transition-colors">
                                <Package className="h-5 w-5 text-indigo-600" />
                              </div>
                              <p className="font-bold text-sm text-gray-900 mb-0.5">
                                {hasAssignedModular
                                  ? (language === 'tr' ? 'Atanan Modüler Paket' : 'Assigned Modular Package')
                                  : (language === 'tr' ? 'Modüler Stand' : 'Modular Stand')}
                              </p>
                              <p className="text-[11px] text-gray-500 leading-relaxed">
                                {hasAssignedModular
                                  ? (language === 'tr'
                                      ? 'Size özel atanmış paketi görüntüleyin.'
                                      : 'View the package assigned to you.')
                                  : (language === 'tr'
                                      ? 'Hazır paketlerden seç, anında kur.'
                                      : 'Pick a ready package, set up instantly.')}
                              </p>
                            </button>
                            {!hasAssignedModular && (
                              <button
                                onClick={() => {
                                  handleTabChange('design');
                                }}
                                data-testid="choose-custom-btn"
                                className="group rounded-2xl bg-white border border-purple-200 hover:border-purple-400 hover:shadow-md transition-all p-4 text-left"
                              >
                                <div className="h-10 w-10 rounded-xl bg-fuchsia-100 flex items-center justify-center mb-3 group-hover:bg-fuchsia-200 transition-colors">
                                  <Wand2 className="h-5 w-5 text-fuchsia-600" />
                                </div>
                                <p className="font-bold text-sm text-gray-900 mb-0.5">
                                  {language === 'tr' ? 'Özel Tasarım' : 'Custom Design'}
                                </p>
                                <p className="text-[11px] text-gray-500 leading-relaxed">
                                  {language === 'tr'
                                    ? 'Tasarımcılarımızla özel stand kur.'
                                    : 'Build a unique stand with our designers.'}
                                </p>
                              </button>
                            )}
                          </div>
                        </div>
                        );
                      })()}


                      {/* Kenar Durumu */}
                      <div className="rounded-2xl p-5 bg-gray-50 border border-gray-200">
                        <h3 className="font-bold text-base mb-4 flex items-center gap-2 text-gray-700">
                          <span className="h-4 w-1 rounded-full inline-block bg-gray-400"></span>
                          {language === 'tr' ? 'Kenar Durumu' : 'Side Status'}
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { side: 'north', label: 'A', color: '#ef4444' },
                            { side: 'east',  label: 'B', color: '#f59e0b' },
                            { side: 'south', label: 'C', color: '#8b5cf6' },
                            { side: 'west',  label: 'D', color: '#10b981' }
                          ].map(({side, label, color}) => {
                            const isOpen = myLayout.open_sides && myLayout.open_sides.includes(side);
                            return (
                              <div key={side} className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-gray-100">
                                <div className="h-6 w-6 rounded-full text-white flex items-center justify-center text-xs font-bold shrink-0" style={{backgroundColor: color}}>
                                  {label}
                                </div>
                                <div className={`h-2.5 w-2.5 rounded-full shrink-0 ${isOpen ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                <span className={`text-xs font-medium ${isOpen ? 'text-green-700' : 'text-red-600'}`}>
                                  {isOpen ? (language === 'tr' ? 'Açık' : 'Open') : (language === 'tr' ? 'Kapalı' : 'Closed')}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {/* Legend */}
                        <div className="flex gap-4 mt-3 text-xs text-gray-400">
                          <span className="flex items-center gap-1.5">
                            <span className="inline-block w-4 h-1.5 rounded-full bg-green-500"></span>
                            {language === 'tr' ? 'Açık Kenar' : 'Open Side'}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <span className="inline-block w-4 h-1.5 rounded-full bg-gray-800"></span>
                            {language === 'tr' ? 'Kapalı Kenar' : 'Closed Side'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* ── Sağ kolon: Stand Krokisi (büyük) ── */}
                    <div className="lg:col-span-3 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-base text-gray-800">{language === 'tr' ? 'Stand Krokisi' : 'Stand View'}</h3>
                          <p className="text-xs text-gray-400 mt-0.5">{language === 'tr' ? 'Her kare = 1m² · Ultra Admin görünümüyle senkronize' : 'Each square = 1m² · Synced with Ultra Admin view'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {myLayout.hall_name && (
                            <span className="text-xs px-3 py-1 rounded-full font-medium" style={{background: `${myLayout.hall_stand_color || '#f59e0b'}22`, color: myLayout.hall_stand_color || '#f59e0b'}}>
                              {myLayout.hall_name}
                            </span>
                          )}
                          {myLayout.hall_stand_status && (
                            <span className="text-xs px-3 py-1 rounded-full font-bold text-white"
                              style={{background: myLayout.hall_stand_color || '#f59e0b'}}>
                              {myLayout.hall_stand_status === 'reserved'    ? (language === 'tr' ? 'Rezerve'   : 'Reserved')    :
                               myLayout.hall_stand_status === 'unavailable' ? (language === 'tr' ? 'Satıldı'   : 'Sold')        :
                                                                               (language === 'tr' ? 'Müsait'    : 'Available')}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex-1 flex items-center justify-center">
                        {(() => {
                          const w = myLayout.base_width  || 1;
                          const h = myLayout.base_height || 1;

                          const hexToRgba = (hex, alpha) => {
                            if (!hex || hex.length < 7) return `rgba(148,163,184,${alpha})`;
                            const r = parseInt(hex.slice(1,3),16);
                            const g = parseInt(hex.slice(3,5),16);
                            const b = parseInt(hex.slice(5,7),16);
                            return `rgba(${r},${g},${b},${alpha})`;
                          };

                          const rawColor     = myLayout.hall_stand_color || '#f59e0b';
                          const transparency = (myLayout.hall_stand_transparency ?? 70) / 100;
                          const statusColor  = hexToRgba(rawColor, transparency);

                          // Komşu standlar (gri)
                          const adjStands = myLayout.adjacent_stands || [];

                          // Toplam bounding box: ana stand + tüm komşular
                          // Ana stand: (0,0) → (w, h) göreli koordinatlarda
                          let totalMinX = 0, totalMinY = 0;
                          let totalMaxX = w, totalMaxY = h;
                          adjStands.forEach(adj => {
                            totalMinX = Math.min(totalMinX, adj.rel_x);
                            totalMinY = Math.min(totalMinY, adj.rel_y);
                            totalMaxX = Math.max(totalMaxX, adj.rel_x + adj.width);
                            totalMaxY = Math.max(totalMaxY, adj.rel_y + adj.height);
                          });
                          const totalCols = totalMaxX - totalMinX;
                          const totalRows = totalMaxY - totalMinY;

                          // Ölçekli cellSize: tüm standlar sığsın, max 500×580
                          const maxW = 500, maxH = 580;
                          const cellSize = Math.min(
                            Math.floor(maxW / totalCols),
                            Math.floor(maxH / totalRows),
                            56
                          );

                          const PAD = 64;
                          // Offset: negatif koordinatları pozitife çevirmek için
                          const offsetX = -totalMinX;  // negatif rel_x kadar sağa kaydır
                          const offsetY = -totalMinY;  // negatif rel_y kadar aşağı kaydır
                          const svgW = totalCols * cellSize + PAD * 2;
                          const svgH = totalRows * cellSize + PAD * 2;
                          // Ana standin SVG içindeki başlangıç noktası
                          const standX = PAD + offsetX * cellSize;
                          const standY = PAD + offsetY * cellSize;
                          const openSides = myLayout.open_sides || [];

                          const standFontSz = Math.max(10, cellSize * 0.55);
                          const nameFontSz  = Math.max(13, standFontSz * 1.4);
                          const HALO_CLR    = 'rgba(255,255,255,0.9)';
                          const FONT_CLR    = '#0f172a';
                          const CLOSED_W = 9;
                          const OPEN_W   = 3;

                          return (
                            <svg viewBox={`0 0 ${svgW} ${svgH}`} style={{width: '100%', maxHeight: '620px', shapeRendering: 'crispEdges'}}>
                              {/* ── Komşu standlar (gri, tam boyutlu, ölçekli) ── */}
                              {adjStands.map((adj, i) => {
                                const ax = PAD + (offsetX + adj.rel_x) * cellSize;
                                const ay = PAD + (offsetY + adj.rel_y) * cellSize;
                                const aw = adj.width  * cellSize;
                                const ah = adj.height * cellSize;
                                const adjFontSz = Math.max(9, cellSize * 0.38);
                                return (
                                  <g key={`adj-${i}`}>
                                    <rect x={ax} y={ay} width={aw} height={ah} fill="#e2e8f0" />
                                    <rect x={ax} y={ay} width={aw} height={ah} fill="rgba(148,163,184,0.25)" />
                                    {Array.from({length: adj.width + 1}).map((_, ci) => (
                                      <line key={`acv-${i}-${ci}`} x1={ax+ci*cellSize} y1={ay} x2={ax+ci*cellSize} y2={ay+ah} stroke="#cbd5e1" strokeWidth="0.5"/>
                                    ))}
                                    {Array.from({length: adj.height + 1}).map((_, ri) => (
                                      <line key={`ach-${i}-${ri}`} x1={ax} y1={ay+ri*cellSize} x2={ax+aw} y2={ay+ri*cellSize} stroke="#cbd5e1" strokeWidth="0.5"/>
                                    ))}
                                    <rect x={ax} y={ay} width={aw} height={ah} fill="none" stroke="#94a3b8" strokeWidth="1"/>
                                    {adj.stand_no && (
                                      <text x={ax+aw/2} y={ay+ah/2} textAnchor="middle" dominantBaseline="middle"
                                        fontSize={adjFontSz} fontWeight="600" fill="#64748b" fontFamily="monospace"
                                        stroke="rgba(255,255,255,0.85)" strokeWidth="2.5" paintOrder="stroke">
                                        {adj.stand_no}
                                      </text>
                                    )}
                                  </g>
                                );
                              })}

                              {/* ── Ana stand ── */}
                              <rect x={standX} y={standY} width={w*cellSize} height={h*cellSize} fill="#f8fafc" />
                              <rect x={standX} y={standY} width={w*cellSize} height={h*cellSize} fill={statusColor} />

                              {Array.from({length: w+1}).map((_, i) => (
                                <line key={`vg-${i}`} x1={standX+i*cellSize} y1={standY} x2={standX+i*cellSize} y2={standY+h*cellSize} stroke="#cbd5e1" strokeWidth="0.5"/>
                              ))}
                              {Array.from({length: h+1}).map((_, i) => (
                                <line key={`hg-${i}`} x1={standX} y1={standY+i*cellSize} x2={standX+w*cellSize} y2={standY+i*cellSize} stroke="#cbd5e1" strokeWidth="0.5"/>
                              ))}

                              <rect x={standX} y={standY} width={w*cellSize} height={h*cellSize} fill="none" stroke="#94a3b8" strokeWidth="1.5"/>

                              <line x1={standX} y1={standY} x2={standX+w*cellSize} y2={standY}
                                stroke={openSides.includes('north') ? '#22c55e' : '#111827'} strokeWidth={openSides.includes('north') ? OPEN_W : CLOSED_W}/>
                              <line x1={standX+w*cellSize} y1={standY} x2={standX+w*cellSize} y2={standY+h*cellSize}
                                stroke={openSides.includes('east') ? '#22c55e' : '#111827'} strokeWidth={openSides.includes('east') ? OPEN_W : CLOSED_W}/>
                              <line x1={standX} y1={standY+h*cellSize} x2={standX+w*cellSize} y2={standY+h*cellSize}
                                stroke={openSides.includes('south') ? '#22c55e' : '#111827'} strokeWidth={openSides.includes('south') ? OPEN_W : CLOSED_W}/>
                              <line x1={standX} y1={standY} x2={standX} y2={standY+h*cellSize}
                                stroke={openSides.includes('west') ? '#22c55e' : '#111827'} strokeWidth={openSides.includes('west') ? OPEN_W : CLOSED_W}/>

                              {myLayout.stand_no && (
                                <text x={standX+w*cellSize-5} y={standY+standFontSz+3}
                                  textAnchor="end" fontSize={standFontSz} fontWeight="700" fill={FONT_CLR}
                                  fontFamily="monospace" stroke={HALO_CLR} strokeWidth={standFontSz*0.2}
                                  paintOrder="stroke" opacity="0.95">{myLayout.stand_no}</text>
                              )}
                              {myLayout.country && (() => {
                                // Phase 86AZ — Uzun firma adı stand kutusundan
                                // taşmasın diye otomatik 2 satıra böl. Kelime
                                // sayısı ≥ 2 ve toplam karakter sayısı 12+ ise
                                // satır ortasından parçala.
                                const txt = String(myLayout.country || '');
                                const words = txt.trim().split(/\s+/);
                                let line1 = txt, line2 = '';
                                if (words.length >= 2 && txt.length > 12) {
                                  // Kelimeleri ortadan böl
                                  const mid = Math.ceil(words.length / 2);
                                  line1 = words.slice(0, mid).join(' ');
                                  line2 = words.slice(mid).join(' ');
                                }
                                // İki satır varsa baseline'ı yukarı kaydır
                                const cx = standX+w*cellSize/2;
                                const cy = standY+h*cellSize/2;
                                if (line2) {
                                  return (
                                    <text x={cx} y={cy}
                                      textAnchor="middle" dominantBaseline="middle"
                                      fontSize={nameFontSz*0.85} fontWeight="800" fill={FONT_CLR}
                                      fontFamily="sans-serif" stroke={HALO_CLR} strokeWidth={nameFontSz*0.18}
                                      paintOrder="stroke">
                                      <tspan x={cx} dy={`-0.55em`}>{line1}</tspan>
                                      <tspan x={cx} dy={`1.1em`}>{line2}</tspan>
                                    </text>
                                  );
                                }
                                return (
                                  <text x={cx} y={cy}
                                    textAnchor="middle" dominantBaseline="middle"
                                    fontSize={nameFontSz} fontWeight="800" fill={FONT_CLR}
                                    fontFamily="sans-serif" stroke={HALO_CLR} strokeWidth={nameFontSz*0.2}
                                    paintOrder="stroke">{line1}</text>
                                );
                              })()}
                              <text x={standX+w*cellSize/2} y={standY+h*cellSize-6}
                                textAnchor="middle" fontSize={standFontSz*0.85}
                                fill={FONT_CLR} fontFamily="sans-serif" opacity="0.75"
                                stroke={HALO_CLR} strokeWidth={standFontSz*0.16} paintOrder="stroke">
                                {myLayout.total_area}m²
                              </text>

                              {/* A/B/C/D etiketleri */}
                              {[
                                {label:'A', x: standX+w*cellSize/2,    y: standY-26,           fill:'#ef4444'},
                                {label:'B', x: standX+w*cellSize+26,   y: standY+h*cellSize/2, fill:'#f59e0b'},
                                {label:'C', x: standX+w*cellSize/2,    y: standY+h*cellSize+26,fill:'#8b5cf6'},
                                {label:'D', x: standX-26,              y: standY+h*cellSize/2, fill:'#10b981'},
                              ].map(({label, x, y, fill}) => (
                                <g key={label}>
                                  <circle cx={x} cy={y} r="18" fill={fill}/>
                                  <text x={x} y={y+5} textAnchor="middle" dominantBaseline="middle" fontSize="14" fontWeight="bold" fill="white">{label}</text>
                                </g>
                              ))}
                            </svg>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Stand Değişiklik Talebi — sadece layout tanımlanmışsa göster ──
                    Phase 86AY — Green Zone customers don't have a "change
                    stand" capability (stand is assigned directly by admin,
                    no resize/relocate lifecycle). Hide the whole section
                    when the user belongs to green_zone (either via JWT
                    `userInfo.zone` or the participant doc's `zone_type`). */}
                {myLayout && myLayout.base_width
                  && userInfo.zone !== 'green_zone'
                  && myLayout.zone_type !== 'green_zone' && (
                <div className="mt-6 rounded-2xl border border-dashed border-amber-300 bg-amber-50 p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <h3 className="font-bold text-base text-amber-800 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        {language === 'tr' ? 'Stand Değişiklik Talebi' : 'Stand Change Request'}
                      </h3>
                      <p className="text-xs text-amber-600 mt-1">
                        {language === 'tr'
                          ? 'Alan büyüklüğü, konum veya açık kenar değişikliği talep edebilirsiniz.'
                          : 'You can request area, location or open sides change.'}
                      </p>
                    </div>
                    {(() => {
                      const hasPending = changeRequests.some(r => r.status === 'pending' || r.status === 'awaiting_customer_choice');
                      // Phase 86N — quota lock independent of pending state
                      const quotaExhausted = changeQuota && !changeQuota.can_submit && !hasPending;
                      const isLocked = hasPending || quotaExhausted;
                      const lockedReason = hasPending
                        ? (language === 'tr' ? 'Bekleyen talebiniz sonuçlanana kadar yeni talep açılamaz.' : 'Wait for pending request to resolve.')
                        : (language === 'tr'
                            ? `Stand değişiklik hakkınız doldu (${changeQuota?.used || 0}/${changeQuota?.max_allowed || 1}). Daha fazla bilgi için organizasyon ekibiyle iletişime geçin.`
                            : `Stand change limit reached (${changeQuota?.used || 0}/${changeQuota?.max_allowed || 1}). Contact the organisation team for more info.`);
                      if (!showChangeForm) {
                        return (
                          <div className="flex flex-col items-end gap-1">
                            <button
                              onClick={() => { if (!isLocked) { setShowChangeForm(true); setWizardStep(1); setChangeMsg(''); } }}
                              disabled={isLocked}
                              className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors shrink-0 ${
                                isLocked
                                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : 'bg-amber-500 hover:bg-amber-600 text-white'
                              }`}
                              data-testid="open-change-request-form"
                              title={isLocked ? lockedReason : ''}
                            >
                              {language === 'tr' ? '+ Yeni Talep' : '+ New Request'}
                            </button>
                            {/* Quota counter — always visible when quota is loaded */}
                            {changeQuota && changeQuota.max_allowed > 0 && (
                              <p className="text-[11px] text-amber-600/80 font-mono" data-testid="change-quota-counter">
                                {changeQuota.used}/{changeQuota.max_allowed} {language === 'tr' ? 'kullanıldı' : 'used'}
                              </p>
                            )}
                            {isLocked && (
                              <p className="text-xs text-amber-600 text-right max-w-xs">
                                {lockedReason}
                              </p>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  {/* ── Phase 86O — Step-by-Step Wizard ─────────────────────
                       Müşteri eski tek-ekranlı formu kafa karıştırıcı buluyordu.
                       Şimdi 3 adımda ilerliyor: 
                       (1) Ne değiştirmek istiyorsun? (büyük tıklanabilir kartlar)
                       (2) Detayları doldur (sadece seçilen kategoriler)
                       (3) Özet + Onay
                       Her adımda "Geri / İleri" butonları var, her seçim
                       görsel olarak (CheckCircle + renk) belirgin. */}
                  {showChangeForm && (
                    <div className="mt-4 bg-white rounded-xl border border-amber-200 overflow-hidden" data-testid="change-request-wizard">
                      {/* Step Indicator */}
                      <div className="px-4 sm:px-6 py-3 bg-amber-50/50 border-b border-amber-100">
                        <div className="flex items-center justify-between max-w-md mx-auto">
                          {[
                            { n: 1, label: language === 'tr' ? 'Ne değişsin?' : 'What?' },
                            { n: 2, label: language === 'tr' ? 'Detaylar'    : 'Details' },
                            { n: 3, label: language === 'tr' ? 'Onay'        : 'Confirm' },
                          ].map((s, idx, arr) => {
                            const isActive   = wizardStep === s.n;
                            const isComplete = wizardStep > s.n;
                            return (
                              <React.Fragment key={s.n}>
                                <div className="flex flex-col items-center gap-1 flex-shrink-0" data-testid={`wizard-step-indicator-${s.n}`}>
                                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                                    isComplete ? 'bg-amber-500 text-white' :
                                    isActive   ? 'bg-amber-500 text-white ring-4 ring-amber-200' :
                                                 'bg-gray-200 text-gray-500'
                                  }`}>
                                    {isComplete ? <Check className="h-3.5 w-3.5" /> : s.n}
                                  </div>
                                  <span className={`text-[10px] font-semibold ${isActive ? 'text-amber-700' : 'text-gray-400'}`}>{s.label}</span>
                                </div>
                                {idx < arr.length - 1 && (
                                  <div className={`flex-1 h-0.5 mx-1 -mt-4 transition-colors ${wizardStep > s.n ? 'bg-amber-500' : 'bg-gray-200'}`} />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>

                      <div className="p-4 sm:p-5 space-y-4">

                      {/* ─── STEP 1 — Pick categories ─────────────────── */}
                      {wizardStep === 1 && (
                        <div className="space-y-3" data-testid="wizard-step-1">
                          <div>
                            <h4 className="text-sm font-bold text-gray-900">
                              {language === 'tr' ? 'Ne değiştirmek istiyorsunuz?' : 'What do you want to change?'}
                            </h4>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {language === 'tr' ? 'Birden fazla seçebilirsiniz. Karta tıklayın.' : 'Pick one or more. Click a card to select.'}
                            </p>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {[
                              { key: 'area',       Icon: Maximize2, title: language === 'tr' ? 'Alan' : 'Area',
                                desc: language === 'tr' ? 'Stand boyutunu (m²) değiştirmek istiyorum' : 'I want to change the stand size (m²)' },
                              { key: 'location',   Icon: MapPin,    title: language === 'tr' ? 'Konum' : 'Location',
                                desc: language === 'tr' ? 'Stand yerini başka bir bölgeye taşımak istiyorum' : 'I want to move the stand to another spot' },
                              { key: 'open_sides', Icon: Square,    title: language === 'tr' ? 'Stand Tipi' : 'Stand Type',
                                desc: language === 'tr' ? 'Köşe / Sıra / Yarımada / Ada tercihimi değiştirmek istiyorum' : 'Switch between Corner / Liner / Peninsula / Island' },
                            ].map(({ key, Icon, title, desc }) => {
                              const sel = changeForm.change_types.includes(key);
                              return (
                                <button key={key}
                                  type="button"
                                  data-testid={`wizard-cat-${key}`}
                                  onClick={() => setChangeForm(f => ({
                                    ...f,
                                    change_types: sel ? f.change_types.filter(t => t !== key) : [...f.change_types, key]
                                  }))}
                                  className={`relative text-left p-4 rounded-2xl border-2 transition-all hover:scale-[1.02] ${
                                    sel
                                      ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                                      : 'border-gray-200 bg-white hover:border-amber-300'
                                  }`}
                                >
                                  {sel && (
                                    <CheckCircle2 className="absolute top-2 right-2 h-5 w-5 text-amber-500 fill-amber-500" />
                                  )}
                                  <Icon className={`h-7 w-7 mb-2 ${sel ? 'text-amber-600' : 'text-gray-400'}`} />
                                  <p className={`text-sm font-bold mb-1 ${sel ? 'text-amber-900' : 'text-gray-900'}`}>{title}</p>
                                  <p className="text-xs text-gray-500 leading-snug">{desc}</p>
                                </button>
                              );
                            })}
                          </div>
                          {changeForm.change_types.length === 0 && (
                            <p className="text-xs text-gray-400 text-center pt-1">
                              {language === 'tr' ? 'Devam etmek için en az 1 kart seçin.' : 'Pick at least 1 card to continue.'}
                            </p>
                          )}
                        </div>
                      )}

                      {/* ─── STEP 2 — Fill details ────────────────────── */}
                      {wizardStep === 2 && (
                        <div className="space-y-5" data-testid="wizard-step-2">
                          <div>
                            <h4 className="text-sm font-bold text-gray-900">
                              {language === 'tr' ? 'Detayları girin' : 'Fill in the details'}
                            </h4>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {language === 'tr' ? 'Sadece seçtiğiniz kategoriler için bilgi gerekir.' : 'Only fields for your selected categories.'}
                            </p>
                          </div>

                          {/* Talep edilen alan */}
                          {changeForm.change_types.includes('area') && (
                            <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                              <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5 mb-2">
                                <Maximize2 className="h-3.5 w-3.5 text-amber-600" />
                                {language === 'tr' ? 'Talep Edilen Alan (m²)' : 'Requested Area (m²)'}
                              </label>
                              <input
                                type="number" min="1" max="500"
                                value={changeForm.requested_area}
                                onChange={e => setChangeForm(f => ({...f, requested_area: e.target.value}))}
                                placeholder={`${language === 'tr' ? 'Mevcut' : 'Current'}: ${myLayout?.total_area || '—'}m²`}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                                data-testid="wizard-input-area"
                              />
                            </div>
                          )}

                          {/* Konum */}
                          {changeForm.change_types.includes('location') && (
                            <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                              <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5 mb-2">
                                <MapPin className="h-3.5 w-3.5 text-amber-600" />
                                {language === 'tr' ? 'Konum Tercihi' : 'Location Preference'}
                              </label>
                              <input
                                type="text"
                                value={changeForm.requested_location}
                                onChange={e => setChangeForm(f => ({...f, requested_location: e.target.value}))}
                                placeholder={language === 'tr' ? 'Örn: Koridora yakın, ana giriş yakını...' : 'E.g. Near corridor, near main entrance...'}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                                data-testid="wizard-input-location"
                              />
                            </div>
                          )}

                          {/* Stand Tipi */}
                          {changeForm.change_types.includes('open_sides') && (
                            <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                              <label className="text-xs font-bold text-gray-700 flex items-center gap-1.5 mb-2">
                                <Square className="h-3.5 w-3.5 text-amber-600" />
                                {language === 'tr' ? 'Stand Tipi' : 'Stand Type'}
                              </label>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {[
                                  { key: 'corner',    label: 'Corner',    tr: 'Köşe',     desc: language === 'tr' ? '2 açık kenar' : '2 open sides', icon: '↗' },
                                  { key: 'liner',     label: 'Liner',     tr: 'Sıra',     desc: language === 'tr' ? '1 açık kenar' : '1 open side',  icon: '→' },
                                  { key: 'peninsula', label: 'Peninsula', tr: 'Yarımada', desc: language === 'tr' ? '3 açık kenar' : '3 open sides', icon: '⊓' },
                                  { key: 'island',    label: 'Island',    tr: 'Ada',      desc: language === 'tr' ? '4 açık kenar' : '4 open sides', icon: '□' },
                                ].map(({ key, label, tr: trl, desc, icon }) => {
                                  const sel = changeForm.requested_stand_type === key;
                                  return (
                                    <button key={key}
                                      type="button"
                                      onClick={() => setChangeForm(f => ({ ...f, requested_stand_type: sel ? '' : key }))}
                                      data-testid={`wizard-stand-type-${key}`}
                                      className={`flex flex-col items-center gap-1 px-3 py-3 rounded-xl border-2 text-center transition-all ${
                                        sel ? 'border-amber-500 bg-amber-100 text-amber-800 ring-2 ring-amber-200' : 'border-gray-200 bg-white text-gray-600 hover:border-amber-300'
                                      }`}
                                    >
                                      <span className="text-xl font-bold leading-none">{icon}</span>
                                      <span className="text-xs font-bold leading-tight">{language === 'tr' ? `${trl}` : label}</span>
                                      <span className="text-[10px] text-gray-400">{desc}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Genel not */}
                          <div>
                            <label className="text-xs font-bold text-gray-700 block mb-1">
                              {language === 'tr' ? 'Ek Notlar (İsteğe Bağlı)' : 'Additional Notes (Optional)'}
                            </label>
                            <textarea
                              rows="3"
                              value={changeForm.notes}
                              onChange={e => setChangeForm(f => ({...f, notes: e.target.value}))}
                              placeholder={language === 'tr' ? 'Talebinizle ilgili ek bilgi...' : 'Additional information...'}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                              data-testid="wizard-input-notes"
                            />
                          </div>
                        </div>
                      )}

                      {/* ─── STEP 3 — Review & submit ─────────────────── */}
                      {wizardStep === 3 && (
                        <div className="space-y-3" data-testid="wizard-step-3">
                          <div>
                            <h4 className="text-sm font-bold text-gray-900">
                              {language === 'tr' ? 'Talebinizi gözden geçirin' : 'Review your request'}
                            </h4>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {language === 'tr' ? 'Onaylamadan önce kontrol edin.' : 'Please verify before submitting.'}
                            </p>
                          </div>

                          <div className="rounded-xl border border-amber-200 bg-amber-50/40 divide-y divide-amber-100">
                            {/* Categories */}
                            <div className="px-4 py-3">
                              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1.5">
                                {language === 'tr' ? 'Değişiklik kategorileri' : 'Change categories'}
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {changeForm.change_types.map(t => (
                                  <span key={t} className="bg-white border border-amber-300 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                                    {t === 'area'       ? (language === 'tr' ? 'Alan'       : 'Area') :
                                     t === 'location'   ? (language === 'tr' ? 'Konum'      : 'Location') :
                                     t === 'open_sides' ? (language === 'tr' ? 'Stand Tipi' : 'Stand Type') : t}
                                  </span>
                                ))}
                              </div>
                            </div>
                            {/* Area — before → after comparison */}
                            {changeForm.change_types.includes('area') && (
                              <div className="px-4 py-3 grid grid-cols-[auto_1fr_auto_1fr] items-center gap-2 sm:gap-3">
                                <span className="text-xs font-semibold text-gray-600 flex items-center gap-1.5"><Maximize2 className="h-3.5 w-3.5 text-amber-600" />{language === 'tr' ? 'Alan' : 'Area'}</span>
                                <span className="text-sm text-gray-400 line-through font-medium text-right" data-testid="review-area-current">
                                  {myLayout?.total_area ? `${myLayout.total_area} m²` : '—'}
                                </span>
                                <ChevronRight className="h-3.5 w-3.5 text-amber-500 mx-auto" />
                                <span className="text-sm font-bold text-amber-800 text-right" data-testid="review-area-new">
                                  {changeForm.requested_area ? `${changeForm.requested_area} m²` : '—'}
                                </span>
                              </div>
                            )}
                            {/* Location — before → after comparison */}
                            {changeForm.change_types.includes('location') && (
                              <div className="px-4 py-3 grid grid-cols-[auto_1fr_auto_1fr] items-start gap-2 sm:gap-3">
                                <span className="text-xs font-semibold text-gray-600 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-amber-600" />{language === 'tr' ? 'Konum' : 'Location'}</span>
                                <span className="text-sm text-gray-400 line-through font-medium text-right break-words" data-testid="review-location-current">
                                  {myLayout?.stand_no ? `${myLayout.hall || ''} · ${myLayout.stand_no}`.trim().replace(/^·\s*/, '') : '—'}
                                </span>
                                <ChevronRight className="h-3.5 w-3.5 text-amber-500 mx-auto mt-1" />
                                <span className="text-sm font-bold text-amber-800 text-right break-words" data-testid="review-location-new">
                                  {changeForm.requested_location || '—'}
                                </span>
                              </div>
                            )}
                            {/* Stand Type — before → after comparison */}
                            {changeForm.change_types.includes('open_sides') && (
                              <div className="px-4 py-3 grid grid-cols-[auto_1fr_auto_1fr] items-center gap-2 sm:gap-3">
                                <span className="text-xs font-semibold text-gray-600 flex items-center gap-1.5"><Square className="h-3.5 w-3.5 text-amber-600" />{language === 'tr' ? 'Stand tipi' : 'Stand type'}</span>
                                <span className="text-sm text-gray-400 line-through font-medium capitalize text-right" data-testid="review-stand-type-current">
                                  {myLayout?.stand_type || '—'}
                                </span>
                                <ChevronRight className="h-3.5 w-3.5 text-amber-500 mx-auto" />
                                <span className="text-sm font-bold text-amber-800 capitalize text-right" data-testid="review-stand-type-new">
                                  {changeForm.requested_stand_type || '—'}
                                </span>
                              </div>
                            )}
                            {/* Notes */}
                            {changeForm.notes && (
                              <div className="px-4 py-3">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">
                                  {language === 'tr' ? 'Ek not' : 'Notes'}
                                </p>
                                <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{changeForm.notes}</p>
                              </div>
                            )}
                          </div>

                          {/* Legend */}
                          <div className="flex items-center justify-center gap-4 text-[11px] pt-1">
                            <span className="flex items-center gap-1.5 text-gray-400">
                              <span className="inline-block w-3 h-0.5 bg-gray-400" />
                              {language === 'tr' ? 'Mevcut' : 'Current'}
                            </span>
                            <span className="flex items-center gap-1.5 text-amber-700 font-semibold">
                              <span className="inline-block w-3 h-0.5 bg-amber-700" />
                              {language === 'tr' ? 'Yeni talep' : 'New request'}
                            </span>
                          </div>

                          {/* Quota reminder */}
                          {changeQuota && (
                            <div className="text-[11px] text-gray-500 text-center">
                              {language === 'tr'
                                ? `Bu talep ${changeQuota.used + 1}/${changeQuota.max_allowed} hakkınızı kullanacak.`
                                : `Submitting will use ${changeQuota.used + 1}/${changeQuota.max_allowed} of your slots.`}
                            </div>
                          )}
                        </div>
                      )}

                      {changeMsg && (
                        <p className={`text-xs font-semibold ${changeMsg.includes('başarı') || changeMsg.includes('submitted') ? 'text-green-600' : 'text-red-500'}`}>{changeMsg}</p>
                      )}

                      {/* ─── Wizard Nav Buttons ──────────────────────── */}
                      <div className="flex gap-2 justify-between pt-2 border-t border-amber-100">
                        <button
                          onClick={() => {
                            if (wizardStep === 1) {
                              setShowChangeForm(false); setChangeMsg('');
                            } else {
                              setWizardStep(s => Math.max(1, s - 1));
                            }
                          }}
                          className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold transition-colors flex items-center gap-1.5"
                          data-testid="wizard-back-btn"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          {wizardStep === 1
                            ? (language === 'tr' ? 'İptal' : 'Cancel')
                            : (language === 'tr' ? 'Geri'  : 'Back')}
                        </button>

                        {wizardStep < 3 ? (
                          <button
                            onClick={() => {
                              // Step 1 → 2: ensure at least one category
                              if (wizardStep === 1 && changeForm.change_types.length === 0) {
                                setChangeMsg(language === 'tr' ? 'En az bir kategori seçmelisiniz.' : 'Please select at least one category.');
                                return;
                              }
                              setChangeMsg('');
                              setWizardStep(s => Math.min(3, s + 1));
                            }}
                            disabled={wizardStep === 1 && changeForm.change_types.length === 0}
                            className="px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold transition-colors flex items-center gap-1.5"
                            data-testid="wizard-next-btn"
                          >
                            {language === 'tr' ? 'İleri' : 'Next'}
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        ) : (
                          <button
                            onClick={submitChangeRequest}
                            disabled={changeSubmitting}
                            data-testid="submit-change-request"
                            className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold transition-colors flex items-center gap-1.5"
                          >
                            <Send className="h-4 w-4" />
                            {changeSubmitting ? '...' : (language === 'tr' ? 'Talebi Gönder' : 'Submit Request')}
                          </button>
                        )}
                      </div>
                      </div>
                    </div>
                  )}

                  {/* Geçmiş talepler */}
                  {changeRequests.length > 0 && (
                    <div className="mt-4 space-y-3">
                      <p className="text-xs font-semibold text-amber-700">{language === 'tr' ? 'Taleplerim' : 'My Requests'}</p>
                      {changeRequests.map(req => (
                        <div key={req.id} className={`rounded-xl border ${
                          req.status === 'awaiting_customer_choice' ? 'border-blue-200 bg-blue-50' :
                          req.status === 'approved' ? 'border-green-200 bg-green-50' :
                          req.status === 'declined_by_customer' ? 'border-orange-200 bg-orange-50' :
                          req.status === 'rejected' ? 'border-red-100 bg-red-50' :
                          'bg-white border-amber-100'
                        } px-4 py-3`}>

                          {/* Talep başlığı ve status */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-gray-700">
                                {req.change_types?.map(t =>
                                  t === 'area' ? (language === 'tr' ? 'Alan' : 'Area') :
                                  t === 'location' ? (language === 'tr' ? 'Konum' : 'Location') :
                                  (language === 'tr' ? 'Stand Tipi' : 'Stand Type')
                                ).join(', ')}
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(req.created_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', {day:'2-digit', month:'short', year:'numeric'})}
                              </p>
                              {req.admin_note && <p className="text-xs text-gray-500 mt-1 italic">{req.admin_note}</p>}
                              {req.status === 'pending' && req.remind_count > 0 && (
                                <p className="text-xs text-amber-500 mt-1">
                                  {language === 'tr' ? `${req.remind_count} hatırlatma gönderildi` : `${req.remind_count} reminder(s) sent`}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                              {/* Phase 86Z — Same lifecycle-aware status as
                                  the admin side: an "approved" SCR where
                                  the customer hasn't yet hit "Approve My
                                  Stand Area" stays in a "New Location
                                  Assigned" state until they confirm. */}
                              {(() => {
                                const isApproved = req.status === 'approved';
                                // Customer'ın area onayını iki kaynaktan alıyoruz:
                                // (a) backend dump (sayfa açılırken),
                                // (b) component state (`areaApprovalStatus` —
                                //     `handleApproveArea` çağrısından sonra
                                //     anında güncellenir, refetch öncesi bile).
                                // OR mantığı sayesinde Approve tıklandığı an
                                // SCR kart status'u "Tamamlandı"ya geçer.
                                const customerOk = (
                                  req.customer_area_approval_status === 'approved' ||
                                  areaApprovalStatus === 'approved'
                                );
                                const awaitingAck = isApproved && !customerOk;
                                let cls, txt;
                                if (req.status === 'pending') {
                                  cls = 'bg-yellow-100 text-yellow-700';
                                  txt = language === 'tr' ? 'Beklemede' : 'Pending';
                                } else if (req.status === 'awaiting_customer_choice') {
                                  cls = 'bg-blue-100 text-blue-700 animate-pulse';
                                  txt = language === 'tr' ? 'Alternatif Mevcut!' : 'Alternative Ready!';
                                } else if (awaitingAck) {
                                  cls = 'bg-purple-100 text-purple-700 animate-pulse';
                                  txt = language === 'tr' ? 'Yeni Alan Atandı' : 'New Location Assigned';
                                } else if (isApproved) {
                                  cls = 'bg-green-100 text-green-700';
                                  txt = language === 'tr' ? 'Tamamlandı' : 'Completed';
                                } else if (req.status === 'declined_by_customer') {
                                  cls = 'bg-orange-100 text-orange-700';
                                  txt = language === 'tr' ? 'Vazgeçildi' : 'Declined';
                                } else {
                                  cls = 'bg-red-100 text-red-600';
                                  txt = language === 'tr' ? 'Reddedildi' : 'Rejected';
                                }
                                return <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${cls}`}>{txt}</span>;
                              })()}
                              {req.status === 'pending' && (
                                <button
                                  data-testid={`remind-request-${req.id}`}
                                  onClick={() => remindChangeRequest(req.id)}
                                  disabled={remindingId === req.id}
                                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 text-xs font-semibold transition-colors disabled:opacity-50"
                                  title={language === 'tr' ? 'Admini hatırlat (24 saatte 1 kez)' : 'Remind admin (once per 24h)'}
                                >
                                  <Bell className="h-3 w-3" />
                                  {remindingId === req.id ? '...' : (language === 'tr' ? 'Hatırlat' : 'Remind')}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* ── Alternatif Önerileri (awaiting_customer_choice) ── */}
                          {req.status === 'awaiting_customer_choice' && req.alternatives?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-blue-200">
                              <p className="text-xs font-bold text-blue-800 mb-2">
                                {language === 'tr'
                                  ? `Admin size ${req.alternatives.length} alternatif önerdi. Birini seçin veya vazgeçin:`
                                  : `Admin proposed ${req.alternatives.length} alternative(s). Choose one or decline:`}
                              </p>

                              {/* Alternatif kartlar — yan yana mini SVG */}
                              <div className={`grid gap-3 ${req.alternatives.length === 1 ? 'grid-cols-1' : req.alternatives.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3'}`}>
                                {req.alternatives.map((alt, idx) => {
                                  const cells = alt.cells || [];
                                  // North=A, East=B, South=C, West=D
                                  const sideMap = { north: 'A', east: 'B', south: 'C', west: 'D' };
                                  const sidesAbcd = (alt.open_sides || []).map(s => sideMap[s]).filter(Boolean).sort().join(', ');

                                  return (
                                    <div key={alt.id}
                                      className="bg-white border-2 border-orange-200 hover:border-orange-400 rounded-xl overflow-hidden flex flex-col transition-all shadow-sm"
                                      data-testid={`alt-card-${idx}`}>

                                      {/* Mini Kroki (clickable → büyütülmüş modal) */}
                                      {cells.length > 0 && (
                                        <button
                                          type="button"
                                          onClick={() => setAltDetailModal({ alt, idx })}
                                          className="bg-orange-50 hover:bg-orange-100 transition-colors flex items-center justify-center py-3 border-b border-orange-100 group relative w-full"
                                          data-testid={`alt-card-zoom-${idx}`}
                                          title={language === 'tr' ? 'Detaylı görünüm için tıklayın' : 'Click for detailed view'}
                                        >
                                          <StandLayoutVisualizer
                                            cells={cells}
                                            openSides={alt.open_sides || []}
                                            standNo={alt.stand_no || ''}
                                            cellSize={12}
                                            compact
                                          />
                                          <span className="absolute top-1.5 right-1.5 bg-white/90 backdrop-blur-sm text-orange-700 rounded-md p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                            <ZoomIn className="h-3.5 w-3.5" />
                                          </span>
                                        </button>
                                      )}

                                      {/* Stand bilgileri */}
                                      <div className="p-3 flex-1">
                                        <div className="flex items-center justify-between mb-1.5">
                                          <span className="text-xs font-bold text-orange-700">
                                            {language === 'tr' ? `${idx + 1}. Seçenek` : `Option ${idx + 1}`}
                                          </span>
                                          {alt.stand_no && (
                                            <span className="font-mono text-xs bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded font-bold">{alt.stand_no}</span>
                                          )}
                                        </div>
                                        <div className="space-y-1 text-xs text-gray-600">
                                          {alt.hall_name && (
                                            <div className="flex items-center gap-1">
                                              <span className="text-gray-400">Hall:</span>
                                              <span className="font-medium">{alt.hall_name}</span>
                                            </div>
                                          )}
                                          {(alt.area_sqm || alt.area) && (
                                            <div className="flex items-center gap-1">
                                              <span className="text-gray-400">{language === 'tr' ? 'Alan:' : 'Area:'}</span>
                                              <span className="font-medium text-orange-700">{alt.area_sqm || alt.area} m²</span>
                                            </div>
                                          )}
                                          {sidesAbcd && (
                                            <div className="flex items-center gap-1">
                                              <span className="text-gray-400">{language === 'tr' ? 'Açık Kenar:' : 'Open:'}</span>
                                              <span className="font-bold text-emerald-700 tracking-wider">{sidesAbcd}</span>
                                            </div>
                                          )}
                                          {alt.location_note && (
                                            <p className="text-gray-500 italic">{alt.location_note}</p>
                                          )}
                                        </div>
                                      </div>

                                      <div className="px-3 pb-3">
                                        <button
                                          data-testid={`accept-alt-${alt.id}`}
                                          onClick={() => respondToAlternative(req.id, 'accept', alt.id)}
                                          disabled={!!respondingId}
                                          className="w-full py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold transition-colors disabled:opacity-50">
                                          {respondingId === req.id + '_accept' + alt.id ? '...' : (language === 'tr' ? 'Bu Standu Kabul Et' : 'Accept This Stand')}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Vazgeç butonu */}
                              <button
                                data-testid={`decline-all-alternatives-${req.id}`}
                                onClick={() => respondToAlternative(req.id, 'decline')}
                                disabled={!!respondingId}
                                className="mt-3 w-full py-2 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-red-300 hover:text-red-500 text-xs font-semibold transition-colors disabled:opacity-50">
                                {language === 'tr' ? 'Hiçbirini İstemiyorum / Vazgeç' : 'None of these / Decline All'}
                              </button>
                            </div>
                          )}

                          {/* Seçilen alternatif bilgisi */}
                          {req.status === 'approved' && req.selected_alternative && (
                            <div className="mt-2 pt-2 border-t border-green-200">
                              <p className="text-xs text-green-700 font-semibold">
                                {language === 'tr' ? 'Seçilen alternatif:' : 'Selected alternative:'}
                                {req.selected_alternative.stand_no && <span className="font-mono ml-1 bg-green-100 px-1 rounded">{req.selected_alternative.stand_no}</span>}
                                {req.selected_alternative.area && <span className="ml-1">{req.selected_alternative.area}m²</span>}
                                {req.selected_alternative.hall && <span className="ml-1 text-green-600">{req.selected_alternative.hall}</span>}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )} {/* end: myLayout && myLayout.base_width */}

              </CardContent>
            </Card>
          </TabsContent>

          {/* ── MODULAR STANDS TAB (Phase 86) ──
           * supplier_products filtered by category == "Organization".
           * Reuses the same detail-modal flow via addToCart(product).
           * Phase 86AZ.2 — When admin bound a specific package to this
           * customer at assignment time, we filter the catalog to ONLY
           * that bound modular_stand_id (customer can't browse others). */}
          <TabsContent value="modular-stands" className="space-y-6" data-testid="modular-stands-tab-content">
            {/* Phase 86M — when user opens a Modular Stand detail, render the
                shared productDetailJSX here so they STAY on the modular-stands
                tab instead of being switched to "Browse Products". */}
            {productDetailJSX ? productDetailJSX : (() => {
              const boundStandId = myLayout?.assigned_modular_package?.modular_stand_id || null;
              const visibleModularProducts = boundStandId
                ? (modularStandProducts || []).filter(p => p.id === boundStandId)
                : (modularStandProducts || []);
              return (
            <>
            {productsLoading && (
              <div className="grid md:grid-cols-3 gap-6">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="rounded-3xl overflow-hidden shadow-lg bg-white animate-pulse">
                    <div className="h-48 bg-gray-200" />
                    <div className="p-4 space-y-3">
                      <div className="h-5 bg-gray-200 rounded w-2/3" />
                      <div className="h-3 bg-gray-200 rounded w-full" />
                      <div className="h-8 bg-gray-200 rounded-full w-full mt-4" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {boundStandId && !productsLoading && visibleModularProducts.length > 0 && (
              <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-4 flex items-start gap-3" data-testid="assigned-modular-banner">
                <Sparkles className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-bold text-sm text-emerald-900">
                    {language === 'tr' ? 'Size Atanan Modüler Paket' : 'Your Assigned Modular Package'}
                  </p>
                  <p className="text-xs text-emerald-700/80 mt-0.5 leading-relaxed">
                    {language === 'tr'
                      ? 'Solda standınızın özet bilgileri, sağda paket içeriği listelenir.'
                      : 'Stand summary is on the left, package contents are on the right.'}
                  </p>
                </div>
              </div>
            )}

            {/* Phase 86AZ.6 — 2-column layout for the assigned-package view:
                  • LEFT  → Stand summary card (image, name, description, price)
                  • RIGHT → Package Contents (chairs/tables/sockets…)
                When there's NO bound package (catalog browsing), fall back to
                the original 3-column grid. */}
            {boundStandId ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start" data-testid="my-stand-two-col">
                {/* ── LEFT: Stand summary card ── */}
                <div data-testid="my-stand-summary-col">
                  {!productsLoading && visibleModularProducts.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-20 gap-3 text-center rounded-3xl border border-dashed border-gray-200 bg-white">
                      <Package className="h-12 w-12 text-gray-300" />
                      <p className="font-semibold text-gray-500">
                        {language === 'tr' ? 'Atanan modüler paket bulunamadı.' : 'Assigned modular package not found.'}
                      </p>
                    </div>
                  )}
                  {!productsLoading && visibleModularProducts.length > 0 && visibleModularProducts.map(product => {
                    const effectivePrice = getEffectivePrice(product);
                    const pricingSummary = getPricingSummary(product);
                    const coverImg = product.cover_thumb_url || product.image_objects?.[0]?.thumb_url || product.cover_url || product.image_objects?.[0]?.url || null;
                    // Phase 86AZ.7 — Gallery: top 3 extra images shown as
                    // thumbnails under the hero so the customer can review
                    // the assigned stand from multiple angles without leaving
                    // the page.
                    const galleryImgs = (product.image_objects || [])
                      .map(o => o.thumb_url || o.url)
                      .filter(Boolean)
                      .filter(u => u !== coverImg)
                      .slice(0, 4);
                    const title = language === 'tr'
                      ? (product.title_tr || product.title_en || product.title)
                      : (product.title_en || product.title_tr || product.title);
                    const description = language === 'tr'
                      ? (product.description_tr || product.description_en || product.description)
                      : (product.description_en || product.description_tr || product.description);
                    return (
                      <Card key={product.id}
                        className="border-0 rounded-3xl overflow-hidden shadow-lg"
                        data-testid={`modular-stand-card-${product.id}`}
                      >
                        <div className="h-72 overflow-hidden relative bg-white flex items-center justify-center">
                          {coverImg
                            ? <img src={coverImg} alt={title} loading="lazy" decoding="async" className="w-full h-full object-contain p-2" onError={e => { e.target.style.display = 'none'; }} />
                            : <div className="w-full h-full flex items-center justify-center"><Package className="h-16 w-16 text-gray-300" /></div>}
                          {product.product_code && (
                            <div className="absolute top-3 left-3 px-2 py-1 rounded-md bg-white/85 backdrop-blur text-[10px] font-mono font-bold text-gray-700 border border-gray-200 shadow-sm">
                              {product.product_code}
                            </div>
                          )}
                        </div>
                        {galleryImgs.length > 0 && (
                          <div className="px-5 pt-4 grid grid-cols-4 gap-2" data-testid="my-stand-gallery">
                            {galleryImgs.map((g, i) => (
                              <div key={i} className="aspect-square rounded-lg overflow-hidden border border-gray-100 bg-white">
                                <img src={g} alt={`view-${i+1}`} loading="lazy" className="w-full h-full object-cover" />
                              </div>
                            ))}
                          </div>
                        )}
                        <CardContent className="p-5">
                          <div className="mb-2">
                            <span className="inline-block px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-700 text-[10px] font-bold uppercase tracking-wide">
                              {language === 'tr' ? 'Modüler Stand' : 'Modular Stand'}
                            </span>
                            <span className="inline-block ml-2 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wide">
                              {language === 'tr' ? 'Atandı' : 'Assigned'}
                            </span>
                          </div>
                          <h3 className="font-black text-lg text-gray-900 mb-2 leading-tight">{title}</h3>
                          {description && (
                            <p className="text-sm text-gray-600 mb-4 leading-relaxed whitespace-pre-line">{description}</p>
                          )}
                          <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                            {assignedModularPkg?.area_m2 && (
                              <div className="rounded-lg bg-gray-50 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wide font-bold text-gray-400">{language === 'tr' ? 'Alan' : 'Area'}</p>
                                <p className="font-semibold text-gray-800">{assignedModularPkg.area_m2} m²</p>
                              </div>
                            )}
                            {product.dimensions && (
                              <div className="rounded-lg bg-gray-50 px-3 py-2">
                                <p className="text-[10px] uppercase tracking-wide font-bold text-gray-400">{language === 'tr' ? 'Boyutlar' : 'Dimensions'}</p>
                                <p className="font-semibold text-gray-800">{product.dimensions}</p>
                              </div>
                            )}
                          </div>
                          {effectivePrice != null && (
                            <div className="rounded-lg bg-emerald-50 px-4 py-3 border border-emerald-100">
                              <p className="text-[10px] uppercase tracking-wide font-bold text-emerald-700/70">{language === 'tr' ? 'Stand Fiyatı' : 'Stand Price'}</p>
                              <p className="text-xl font-black text-emerald-700">
                                {product.currency || 'USD'} {Number(effectivePrice).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </p>
                              {pricingSummary && (
                                <p className="text-[10px] text-emerald-700/60 mt-0.5">{pricingSummary}</p>
                              )}
                            </div>
                          )}
                          <p className="text-[11px] text-gray-500 mt-3 leading-relaxed text-center">
                            {language === 'tr'
                              ? 'Bu stand size organizasyon tarafından atandı. Ek bir sepet işlemi gerekmez.'
                              : 'This stand has been assigned to you by the organisation. No cart action is required.'}
                          </p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* ── RIGHT: Package Contents ── */}
                <div data-testid="my-stand-contents-col">
                  {assignedModularPkg && Array.isArray(assignedModularPkg.items) && assignedModularPkg.items.length > 0 ? (
                    <div className="rounded-2xl border border-emerald-200 bg-white shadow-sm overflow-hidden" data-testid="assigned-package-contents">
                      <div className="px-5 py-4 border-b border-emerald-100 bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <h3 className="text-sm font-bold text-emerald-900 flex items-center gap-2">
                            <Package className="h-4 w-4" />
                            {language === 'tr' ? 'Paket İçeriği' : 'Package Contents'}
                          </h3>
                          <p className="text-[11px] text-emerald-700/80 mt-0.5 leading-relaxed">
                            {language === 'tr'
                              ? `Standınıza dahil olan tüm kalemler — ${assignedModularPkg.area_m2} m² için tasarlandı.`
                              : `Everything included with your stand — designed for ${assignedModularPkg.area_m2} m².`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-wide font-bold text-emerald-700/60">{language === 'tr' ? 'Paket Toplamı' : 'Package Total'}</p>
                          <p className="text-base font-black text-emerald-700">
                            {assignedModularPkg.currency || 'EUR'} {Number(assignedModularPkg.total_price || 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-100 max-h-[640px] overflow-y-auto">
                        {assignedModularPkg.items.map((item, idx) => {
                          const prod = item.product || {};
                          const itemName = (language === 'tr' ? (prod.name_tr || prod.name) : (prod.name_en || prod.name)) || prod.name || '—';
                          const itemDesc = (language === 'tr' ? (prod.description_tr || prod.description) : (prod.description_en || prod.description)) || '';
                          const itemImg = prod.thumb_url || prod.cover_url || null;
                          const qty = Number(item.qty || 0);
                          const isExtraAllowed = !!item.extra_allowed;
                          return (
                            <div key={item.product_id || idx} className="px-5 py-3 flex items-center gap-3 hover:bg-emerald-50/40 transition-colors">
                              <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                                {itemImg ? (
                                  <img src={itemImg} alt={itemName} className="w-full h-full object-cover" />
                                ) : (
                                  <Package className="h-5 w-5 text-gray-300" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-800 truncate" title={itemName}>{itemName}</p>
                                {itemDesc && (
                                  <p className="text-[11px] text-gray-500 truncate" title={itemDesc}>{itemDesc}</p>
                                )}
                                {isExtraAllowed && (
                                  <span className="inline-block mt-0.5 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                    {language === 'tr' ? 'Ek talep edilebilir' : 'Extra orderable'}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-[11px] uppercase tracking-wide font-bold text-gray-400">{language === 'tr' ? 'Adet' : 'Qty'}</span>
                                <span className="inline-flex items-center justify-center min-w-[2.25rem] h-7 px-2 rounded-full bg-emerald-100 text-emerald-800 text-sm font-bold">
                                  {qty}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                        <p className="text-[11px] text-gray-500 text-center leading-relaxed">
                          {language === 'tr'
                            ? 'Ek talepleriniz için "Ürünler" sekmesinden sepete ekleyebilirsiniz.'
                            : 'For additional items, browse the "Products" tab and add to your cart.'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center" data-testid="assigned-package-loading">
                      <Package className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">
                        {language === 'tr' ? 'Paket içeriği yükleniyor…' : 'Loading package contents…'}
                      </p>
                    </div>
                  )}

                  {/* Phase 86AZ.8 — "Customise your stand" CTA + Extras list.
                      Extras = customer's order items whose product_id is NOT
                      already in the assigned package. Surface them under
                      "Extra" so the user sees exactly what they added on top
                      of the included items. */}
                  {(() => {
                    const pkgProductIds = new Set(
                      (assignedModularPkg?.items || []).map(it => it.product_id).filter(Boolean)
                    );
                    const extras = [];
                    (orders || []).forEach(o => {
                      // Skip cancelled / refunded orders so they don't bloat
                      // the list. Anything else (pending / paid / completed)
                      // is treated as a real extra commitment.
                      const st = (o.status || '').toLowerCase();
                      if (st === 'cancelled' || st === 'refunded') return;
                      (o.items || []).forEach(it => {
                        if (!it.product_id || pkgProductIds.has(it.product_id)) return;
                        extras.push({
                          order_id:    o.id,
                          order_no:    o.order_number || o.id,
                          paid:        (o.payment_status === 'paid' || o.status === 'paid' || o.status === 'completed'),
                          currency:    o.currency || 'USD',
                          product_id:  it.product_id,
                          name:        it.product_name || '—',
                          variant:     it.variant_name || '',
                          qty:         Number(it.quantity || 0),
                          unit_price:  Number(it.unit_price || 0),
                          image_url:   it.image_thumb_url || it.image_url || null,
                        });
                      });
                    });
                    return (
                      <div className="mt-6" data-testid="my-stand-extras-block">
                        {/* CTA — open Browse Products tab */}
                        <button
                          type="button"
                          onClick={() => handleTabChange('products')}
                          data-testid="customize-stand-cta"
                          className="w-full group rounded-2xl border-2 border-dashed border-purple-300 hover:border-purple-500 hover:bg-purple-50/60 transition-all p-5 text-left flex items-start gap-3"
                        >
                          <div className="h-10 w-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-200 transition-colors">
                            <Sparkles className="h-5 w-5 text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm text-purple-900">
                              {language === 'tr'
                                ? 'Standınızı daha fazla özelleştirmek ister misiniz?'
                                : 'Want to customise your stand further?'}
                            </p>
                            <p className="text-[11px] text-purple-700/80 mt-0.5 leading-relaxed">
                              {language === 'tr'
                                ? 'Extra ürünleri görmek ve sipariş vermek için tıklayın. Satın aldıklarınız bu sayfada "Ekstralar" altında listelenir.'
                                : 'Click to browse extras and place an order. Your purchases will appear here under "Extras".'}
                            </p>
                          </div>
                          <span className="text-purple-600 text-xl leading-none group-hover:translate-x-1 transition-transform">→</span>
                        </button>

                        {/* Extras list */}
                        {extras.length > 0 && (
                          <div className="mt-4 rounded-2xl border border-purple-200 bg-white shadow-sm overflow-hidden" data-testid="my-stand-extras-list">
                            <div className="px-5 py-3 border-b border-purple-100 bg-gradient-to-r from-purple-50 to-fuchsia-50 flex items-center justify-between flex-wrap gap-2">
                              <div>
                                <h3 className="text-sm font-bold text-purple-900 flex items-center gap-2">
                                  <Sparkles className="h-4 w-4" />
                                  {language === 'tr' ? 'Ekstralar' : 'Extras'}
                                </h3>
                                <p className="text-[11px] text-purple-700/80 mt-0.5 leading-relaxed">
                                  {language === 'tr'
                                    ? 'Ürünler sekmesinden eklediğiniz extra kalemler.'
                                    : 'Extra items you added from the Products tab.'}
                                </p>
                              </div>
                              <span className="text-[10px] uppercase tracking-wide font-bold text-purple-700/60 bg-white px-2 py-1 rounded-full">
                                {extras.length} {language === 'tr' ? 'kalem' : 'item' + (extras.length > 1 ? 's' : '')}
                              </span>
                            </div>
                            <div className="divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
                              {extras.map((e, i) => (
                                <div key={`${e.order_id}-${e.product_id}-${i}`} className="px-5 py-3 flex items-center gap-3 hover:bg-purple-50/40 transition-colors">
                                  <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                                    {e.image_url ? (
                                      <img src={e.image_url} alt={e.name} className="w-full h-full object-cover" />
                                    ) : (
                                      <Package className="h-5 w-5 text-gray-300" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-800 truncate" title={e.name}>
                                      {e.name}
                                      {e.variant && <span className="text-gray-400 font-normal"> · {e.variant}</span>}
                                    </p>
                                    <p className="text-[10px] text-gray-400 mt-0.5 font-mono">{e.order_no}</p>
                                  </div>
                                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-[11px] uppercase tracking-wide font-bold text-gray-400">{language === 'tr' ? 'Adet' : 'Qty'}</span>
                                      <span className="inline-flex items-center justify-center min-w-[2.25rem] h-7 px-2 rounded-full bg-purple-100 text-purple-800 text-sm font-bold">
                                        {e.qty}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[11px] font-semibold text-gray-700">{e.currency} {Number(e.qty * e.unit_price).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
                                      {e.paid ? (
                                        <Badge className="rounded-full bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 text-[9px] px-1.5 py-0">
                                          {language === 'tr' ? 'Ödendi' : 'Paid'}
                                        </Badge>
                                      ) : (
                                        <Badge className="rounded-full bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100 text-[9px] px-1.5 py-0">
                                          {language === 'tr' ? 'Bekleyen' : 'Pending'}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            ) : (
              <>
                {!productsLoading && visibleModularProducts.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                    <Package className="h-12 w-12 text-gray-300" />
                    <p className="font-semibold text-gray-500">
                      {language === 'tr' ? 'Henüz modüler stand eklenmemiş.' : 'No modular stands added yet.'}
                    </p>
                  </div>
                )}
                {!productsLoading && visibleModularProducts.length > 0 && (
              <div className="grid md:grid-cols-3 gap-6">
                {visibleModularProducts.map(product => {
                  const effectivePrice = getEffectivePrice(product);
                  const pricingSummary = getPricingSummary(product);
                  const availability = product.availability;
                  const coverImg = product.cover_thumb_url || product.image_objects?.[0]?.thumb_url || product.cover_url || product.image_objects?.[0]?.url || null;
                  const availBadgeColor = {
                    in_stock: 'bg-emerald-100 text-emerald-700',
                    low_stock: 'bg-amber-100 text-amber-700',
                    out_of_stock: 'bg-red-100 text-red-700',
                    can_supply_on_request: 'bg-blue-100 text-blue-700',
                    partial_available: 'bg-orange-100 text-orange-700',
                  }[availability?.status] || '';
                  return (
                    <Card key={product.id}
                      className={`border-0 rounded-3xl overflow-hidden cursor-pointer hover:shadow-xl transition-shadow ${
                        product.id === recommendedProductId
                          ? 'shadow-lg ring-2 ring-emerald-400 ring-offset-2'
                          : 'shadow-lg'
                      }`}
                      data-testid={`modular-stand-card-${product.id}`}
                      onClick={() => addToCart(product)}
                    >
                      <div className="h-48 overflow-hidden relative bg-white flex items-center justify-center">
                        {coverImg
                          ? <img src={coverImg} alt={product.title} loading="lazy" decoding="async" className="w-full h-full object-contain p-2" onError={e => { e.target.style.display = 'none'; }} />
                          : <div className="w-full h-full flex items-center justify-center"><Package className="h-16 w-16 text-gray-300" /></div>
                        }
                        {product.id === recommendedProductId && (
                          <span
                            data-testid={`recommended-badge-${product.id}`}
                            className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-md animate-pulse"
                          >
                            <Sparkles className="h-3 w-3" />
                            {language === 'tr' ? `Size Önerilen · ${myLayout?.total_area}m²` : `Recommended · ${myLayout?.total_area}m²`}
                          </span>
                        )}
                        {product.product_code && (
                          <span className="absolute top-2 left-2 text-[10px] font-mono bg-black/50 text-white px-1.5 py-0.5 rounded">
                            {product.product_code}
                          </span>
                        )}
                      </div>
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                          <Badge className="w-fit rounded-full text-[10px] bg-indigo-100 text-indigo-700">
                            {language === 'tr' ? 'Modüler Stand' : 'Modular Stand'}
                          </Badge>
                        </div>
                        <CardTitle className="text-base leading-tight">
                          {language === 'tr'
                            ? (product.title_tr || product.title_en || product.title)
                            : (product.title_en || product.title_tr || product.title)}
                        </CardTitle>
                        {(() => {
                          const desc = language === 'tr'
                            ? (product.description_tr || product.description_en || product.description)
                            : (product.description_en || product.description_tr || product.description);
                          return desc ? <p className="text-xs text-gray-500 line-clamp-2 mt-1">{desc}</p> : null;
                        })()}
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="mb-3">
                          {pricingSummary ? (
                            <>
                              <p className="text-lg font-bold text-emerald-600">{product.currency || 'USD'} {fmt(effectivePrice)}</p>
                              <p className="text-[10px] text-gray-400 line-clamp-1 mt-0.5">{pricingSummary}</p>
                            </>
                          ) : effectivePrice > 0 ? (
                            <p className="text-lg font-bold text-emerald-600">{product.currency || 'USD'} {fmt(effectivePrice)}</p>
                          ) : (
                            <p className="text-sm text-gray-400 italic">{language === 'tr' ? 'Fiyat için iletişime geçin' : 'Contact for pricing'}</p>
                          )}
                        </div>
                        <Button
                          className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-full text-sm"
                          onClick={e => { e.stopPropagation(); addToCart(product); }}
                          data-testid={`modular-view-details-btn-${product.id}`}
                        >
                          {language === 'tr' ? 'Detayları Gör' : 'View Details'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
                )}
              </>
            )}
            </>
            );
            })()}
          </TabsContent>

          <TabsContent value="products" className="space-y-6">

            {/* ── Ürün Detay Sayfası (modal yerine tam sayfa) — Phase 86M:
                 Aynı detail JSX hem bu tabda hem modular-stands tabında
                 paylaşılan productDetailJSX const'undan render olur. ── */}
            {productDetailJSX ? productDetailJSX : (
            <>
            {/* Search bar */}
            <div className="relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <Input
                placeholder={language === 'tr' ? 'Ürün ara...' : 'Search products...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-xl"
                data-testid="product-search-input"
              />
            </div>

            {/* Source filter chips (Phase 86): Tümü / Organization / Tedarikçi Ürünleri */}
            <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
              {[
                { id: 'all',       label: language === 'tr' ? 'Tümü' : 'All' },
                { id: 'organizer', label: 'Organization' },
                { id: 'supplier',  label: language === 'tr' ? 'Tedarikçi Ürünleri' : 'Supplier Products' },
              ].map(chip => (
                <Button
                  key={chip.id}
                  onClick={() => { setProductSource(chip.id); setSelectedCategory(''); }}
                  variant={productSource === chip.id ? 'default' : 'outline'}
                  className="rounded-full whitespace-nowrap shrink-0"
                  data-testid={`product-source-${chip.id}`}
                >
                  {chip.label}
                </Button>
              ))}
            </div>

            {/* Loading State */}
            {productsLoading && (
              <div className="grid md:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="rounded-3xl overflow-hidden shadow-lg bg-white animate-pulse">
                    <div className="h-48 bg-gray-200" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-gray-200 rounded w-1/3" />
                      <div className="h-5 bg-gray-200 rounded w-2/3" />
                      <div className="h-3 bg-gray-200 rounded w-full" />
                      <div className="h-8 bg-gray-200 rounded-full w-full mt-4" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Error State */}
            {!productsLoading && productsError && (
              <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
                <AlertCircle className="h-12 w-12 text-red-400" />
                <p className="text-gray-500">{productsError}</p>
                <Button onClick={() => fetchSupplierProducts(true)} variant="outline" className="gap-2 rounded-full">
                  <RefreshCw className="h-4 w-4" />
                  {language === 'tr' ? 'Tekrar Dene' : 'Retry'}
                </Button>
              </div>
            )}

            {/* Empty State */}
            {!productsLoading && !productsError && filteredProducts.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                <Package className="h-12 w-12 text-gray-300" />
                <p className="font-semibold text-gray-500">
                  {searchTerm || selectedCategory
                    ? (language === 'tr' ? 'Arama kriterlerine uyan ürün bulunamadı.' : 'No products match your search.')
                    : (language === 'tr' ? 'Henüz ürün eklenmemiş.' : 'No products available yet.')
                  }
                </p>
                {(searchTerm || selectedCategory) && (
                  <Button variant="ghost" size="sm" onClick={() => { setSearchTerm(''); setSelectedCategory(''); }}>
                    {language === 'tr' ? 'Filtreleri Temizle' : 'Clear filters'}
                  </Button>
                )}
              </div>
            )}

            {/* Product Grid */}
            {!productsLoading && !productsError && filteredProducts.length > 0 && (
              <div className="grid md:grid-cols-3 gap-6">
                {filteredProducts.map(product => {
                  const isOrganizerExtra = product._source === 'organizer';
                  const effectivePrice = getEffectivePrice(product);
                  const pricingSummary = getPricingSummary(product);
                  const availability = product.availability;
                  const coverImg = product.cover_thumb_url || product.image_objects?.[0]?.thumb_url || product.cover_url || product.image_objects?.[0]?.url || null;
                  const availBadgeColor = {
                    in_stock: 'bg-emerald-100 text-emerald-700',
                    low_stock: 'bg-amber-100 text-amber-700',
                    out_of_stock: 'bg-red-100 text-red-700',
                    can_supply_on_request: 'bg-blue-100 text-blue-700',
                    partial_available: 'bg-orange-100 text-orange-700',
                  }[availability?.status] || '';

                  return (
                    <Card key={product.id}
                      className={`border-0 rounded-3xl overflow-hidden transition-shadow ${
                        isOrganizerExtra
                          ? 'shadow-md opacity-95'
                          : (product.id === recommendedProductId
                              ? 'shadow-lg ring-2 ring-emerald-400 ring-offset-2 cursor-pointer hover:shadow-xl'
                              : 'shadow-lg cursor-pointer hover:shadow-xl')
                      }`}
                      data-testid={`product-card-${product.id}`}
                      onClick={isOrganizerExtra ? undefined : () => addToCart(product)}
                    >
                      <div className="h-48 overflow-hidden relative bg-white flex items-center justify-center">
                        {coverImg
                          ? <img src={coverImg} alt={product.title} loading="lazy" decoding="async" className="w-full h-full object-contain p-2" onError={e => { e.target.style.display = 'none'; }} />
                          : <div className="w-full h-full flex items-center justify-center"><Package className="h-16 w-16 text-gray-300" /></div>
                        }
                        {isOrganizerExtra && (
                          <span className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                            {language === 'tr' ? 'Organizer Ekstra' : 'Organizer Extra'}
                          </span>
                        )}
                        {!isOrganizerExtra && product.id === recommendedProductId && (
                          <span
                            data-testid={`recommended-badge-${product.id}`}
                            className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-md animate-pulse"
                          >
                            <Sparkles className="h-3 w-3" />
                            {language === 'tr' ? `Size Önerilen · ${myLayout?.total_area}m²` : `Recommended · ${myLayout?.total_area}m²`}
                          </span>
                        )}
                        {!isOrganizerExtra && availability?.status && availability.status !== 'untracked' && product.id !== recommendedProductId && (
                          <div className="absolute top-2 right-2 flex flex-col items-end gap-0.5">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${availBadgeColor}`}>
                              {availability.message}
                            </span>
                            {availability.available_qty != null && (
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${availBadgeColor} opacity-80`}>
                                {availability.available_qty} {product.calculation_type === 'carton_based' ? (language === 'tr' ? 'koli' : 'Pack') : (language === 'tr' ? 'adet' : 'pcs')}
                              </span>
                            )}
                          </div>
                        )}
                        {!isOrganizerExtra && product.product_code && (
                          <span className="absolute top-2 left-2 text-[10px] font-mono bg-black/50 text-white px-1.5 py-0.5 rounded">
                            {product.product_code}
                          </span>
                        )}
                      </div>
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                          {product.category && <Badge className="w-fit rounded-full text-[10px]">{translateCategory(product.category)}</Badge>}
                          {!isOrganizerExtra && product.product_type && (
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                              product.product_type === 'rental'   ? 'bg-emerald-100 text-emerald-700' :
                              product.product_type === 'service'  ? 'bg-purple-100 text-purple-700'  :
                              product.product_type === 'for_sale' ? 'bg-blue-100 text-blue-700'      :
                                                                    'bg-gray-100 text-gray-600'
                            }`} data-testid={`product-type-badge-${product.id}`}>
                              {product.product_type === 'rental'   ? (language === 'tr' ? 'Kiralık' : 'Rental') :
                               product.product_type === 'service'  ? (language === 'tr' ? 'Hizmet'  : 'Service') :
                               product.product_type === 'for_sale' ? (language === 'tr' ? 'Satılık' : 'For Sale') :
                               product.product_type}
                            </span>
                          )}
                        </div>
                        <CardTitle className="text-base leading-tight">
                          {language === 'tr'
                            ? (product.title_tr || product.title_en || product.title)
                            : (product.title_en || product.title_tr || product.title)}
                        </CardTitle>
                        {(() => {
                          const desc = language === 'tr'
                            ? (product.description_tr || product.description_en || product.description)
                            : (product.description_en || product.description_tr || product.description);
                          return desc ? <p className="text-xs text-gray-500 line-clamp-2 mt-1">{desc}</p> : null;
                        })()}
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="mb-3">
                          {pricingSummary ? (
                            <>
                              <p className="text-lg font-bold text-emerald-600">{product.currency || 'USD'} {fmt(effectivePrice)}</p>
                              <p className="text-[10px] text-gray-400 line-clamp-1 mt-0.5">{pricingSummary}</p>
                            </>
                          ) : effectivePrice > 0 ? (
                            <p className="text-lg font-bold text-emerald-600">{product.currency || 'USD'} {fmt(effectivePrice)}</p>
                          ) : (
                            <p className="text-sm text-gray-400 italic">{language === 'tr' ? 'Fiyat için iletişime geçin' : 'Contact for pricing'}</p>
                          )}
                          {product.unit && <p className="text-xs text-gray-400">/ {product.unit}</p>}
                        </div>
                        {isOrganizerExtra ? (() => {
                          const rawOrganizer = (organizerExtras || []).find(o => o.id === product.id);
                          const price = Number(rawOrganizer?.extra_price || product.effective_price || 0);
                          // "İncele" button is always available so customer can read
                          // category, description, pricing unit, stock rules, etc.
                          const inspectBtn = (
                            <Button
                              type="button"
                              variant="outline"
                              className="flex-1 rounded-full text-sm border-gray-300 hover:bg-gray-50"
                              onClick={e => { e.stopPropagation(); if (rawOrganizer) setOrganizerDetail(rawOrganizer); }}
                              disabled={!rawOrganizer}
                              data-testid={`organizer-inspect-btn-${product.id}`}
                            >
                              {language === 'tr' ? 'İncele' : 'Preview'}
                            </Button>
                          );
                          // Only show "Add to Cart" when organizer admin has set an
                          // extra_price > 0. Otherwise the product ships free inside
                          // the modular stand package and cannot be bought standalone.
                          if (price <= 0) {
                            return (
                              <div className="flex flex-col gap-2">
                                <p className="text-center text-[11px] text-gray-500 italic py-2 border-t border-gray-100" data-testid={`organizer-free-note-${product.id}`}>
                                  {language === 'tr'
                                    ? 'Modüler stand paketine dahil — ilave fiyat tanımlı değil'
                                    : 'Included in modular stand package — no extra price set'}
                                </p>
                                {inspectBtn}
                              </div>
                            );
                          }
                          return (
                            <div className="flex items-center gap-1.5 flex-nowrap" data-testid={`organizer-actions-${product.id}`}>
                              {/* Phase 86AZ.11 — Quantity stepper inline with
                                  Preview + Add to Cart on a single row.
                                  Compact sizes so all 3 fit on one row even
                                  on the narrow 3-col grid layout. */}
                              <div className="flex items-center gap-0.5 px-1 py-0.5 rounded-full border border-gray-200 bg-gray-50 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={e => { e.stopPropagation(); setQtyFor(product.id, q => q - 1); }}
                                  disabled={(organizerQty[product.id] ?? 1) <= 1}
                                  data-testid={`organizer-qty-dec-${product.id}`}
                                  className="h-6 w-6 rounded-full flex items-center justify-center bg-white border border-gray-200 text-gray-700 font-bold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs"
                                  title={language === 'tr' ? 'Azalt' : 'Decrease'}
                                >−</button>
                                <input
                                  type="number"
                                  min={1}
                                  max={99}
                                  value={organizerQty[product.id] ?? 1}
                                  onClick={e => e.stopPropagation()}
                                  onChange={e => setQtyFor(product.id, () => e.target.value)}
                                  data-testid={`organizer-qty-input-${product.id}`}
                                  className="w-6 text-center bg-transparent border-0 focus:outline-none text-xs font-bold text-gray-800 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                                <button
                                  type="button"
                                  onClick={e => { e.stopPropagation(); setQtyFor(product.id, q => q + 1); }}
                                  disabled={(organizerQty[product.id] ?? 1) >= 99}
                                  data-testid={`organizer-qty-inc-${product.id}`}
                                  className="h-6 w-6 rounded-full flex items-center justify-center bg-white border border-gray-200 text-gray-700 font-bold hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs"
                                  title={language === 'tr' ? 'Artır' : 'Increase'}
                                >+</button>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={e => { e.stopPropagation(); if (rawOrganizer) setOrganizerDetail(rawOrganizer); }}
                                disabled={!rawOrganizer}
                                data-testid={`organizer-inspect-btn-${product.id}`}
                                className="h-9 px-3 rounded-full text-xs flex-shrink-0 border-gray-300 hover:bg-gray-50"
                              >
                                {language === 'tr' ? 'Önizle' : 'Preview'}
                              </Button>
                              <Button
                                className="flex-1 h-9 min-w-0 bg-amber-500 hover:bg-amber-600 rounded-full text-xs px-2"
                                onClick={e => {
                                  e.stopPropagation();
                                  if (!rawOrganizer) return;
                                  const q = Number(organizerQty[product.id] ?? 1);
                                  handleAddOrganizerExtra(rawOrganizer, product, q);
                                }}
                                disabled={!rawOrganizer || addingOrganizerId === product.id}
                                data-testid={`organizer-add-btn-${product.id}`}
                                title={language === 'tr' ? 'Paket dışı ilave alım' : 'Additional purchase outside package'}
                              >
                                {(() => {
                                  const q = Number(organizerQty[product.id] ?? 1);
                                  const total = price * q;
                                  if (addingOrganizerId === product.id) {
                                    return language === 'tr' ? '...' : '...';
                                  }
                                  return language === 'tr'
                                    ? `+ Ekle · ${product.currency || 'EUR'} ${fmt(total)}`
                                    : `+ Add · ${product.currency || 'EUR'} ${fmt(total)}`;
                                })()}
                              </Button>
                            </div>
                          );
                        })() : (
                          <Button
                            className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-full text-sm"
                            onClick={e => { e.stopPropagation(); addToCart(product); }}
                            data-testid={`view-details-btn-${product.id}`}
                          >
                            {language === 'tr' ? 'Detayları Gör' : 'View Details'}
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
            </>) /* end !showProductDetail */}
          </TabsContent>

          {/* ── STAND DESIGN TAB ── */}
          <TabsContent value="design" className="space-y-6">
            <StandDesignSection slug={slug} language={language} onUnreadChange={setStandUnreadCount} myLayout={myLayout} areaApprovalStatus={areaApprovalStatus} />
          </TabsContent>

          <TabsContent value="orders" className="space-y-6">
            <h2 className="text-2xl font-bold">{t('order_history')}</h2>
            {ordersLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin h-10 w-10 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto mb-3"></div>
                <p className="text-gray-500">{language === 'tr' ? 'Siparişler yükleniyor...' : 'Loading orders...'}</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500">{language === 'tr' ? 'Henüz siparişiniz yok' : 'No orders yet'}</p>
              </div>
            ) : (
                <div className="space-y-4">
              {orders.map(order => (
                <Card key={order.id} className="border-0 shadow-lg rounded-3xl">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-bold text-gray-900 font-mono">{order.order_number || order.id}</p>
                          {order.kind === 'service' && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1" data-testid="order-kind-service">
                              <Wrench className="h-3 w-3" />{language === 'tr' ? 'Servis' : 'Service'}
                            </span>
                          )}
                          {order.order_type === 'quote' && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium flex items-center gap-1">
                              <FileText className="h-3 w-3" />{language === 'tr' ? 'Teklif' : 'Quote'}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {new Date(order.created_at).toLocaleDateString()}
                          {order.kind === 'service' && order.stand_no && (
                            <span className="ml-2 text-gray-400">· {order.stand_no}{order.hall_name ? ` · ${order.hall_name}` : ''}</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {(order.payment_status === 'paid' || order.status === 'paid' || order.status === 'completed') && (
                          <Badge className="rounded-full bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100" data-testid="order-paid-badge">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            {language === 'tr' ? 'Ödendi' : 'Paid'}
                          </Badge>
                        )}
                        <Badge className={`rounded-full ${getStatusColor(order.status)}`}>
                          {getStatusLabel(order.status, language)}
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-2 mb-4">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <div>
                            <span>
                              {item.product_name}
                              {item.variant_name
                                ? <span className="text-gray-400 ml-1">({item.variant_name})</span>
                                : item.variant_id
                                  ? <span className="text-gray-400 ml-1">(Orijinal)</span>
                                  : null}
                              {' '}× {item.quantity}
                            </span>
                            {item.pricing_mode_selected === 'daily' && item.number_of_days && (
                              <p className="text-xs text-emerald-600 mt-0.5">
                                {item.number_of_days} gün
                                {item.start_date && ` · ${item.start_date}${item.end_date && item.end_date !== item.start_date ? ` → ${item.end_date}` : ''}`}
                                {item.number_of_people && ` · ${item.number_of_people} kişi`}
                              </p>
                            )}
                            {item.pricing_mode_selected === 'event' && (
                              <p className="text-xs text-amber-600 mt-0.5">
                                Etkinlik · 9–20 Kas 2026
                                {item.number_of_people && ` · ${item.number_of_people} kişi`}
                              </p>
                            )}
                          </div>
                          <span className="font-semibold shrink-0">{order.currency || 'USD'} {fmt(item.quantity * item.unit_price, 2)}</span>
                        </div>
                      ))}
                    </div>
                    {order.brief && (
                      <div className="bg-purple-50 rounded-xl px-3 py-2 mb-3 text-sm text-purple-800">
                        <span className="font-semibold">{language === 'tr' ? 'Brifing: ' : 'Brief: '}</span>{order.brief}
                      </div>
                    )}

                    {/* ── Quote Offer Panel (visible when status = "quoted") ── */}
                    {order.status === 'quoted' && order.quote_offer && (
                      <div className={`mt-1 mb-3 rounded-2xl p-4 border ${
                        isQuoteExpired(order) ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                      }`}>
                        <p className="text-sm font-bold mb-3 flex items-center gap-2">
                          <span className={isQuoteExpired(order) ? 'text-red-700' : 'text-amber-800'}>
                            {language === 'tr'
                              ? (isQuoteExpired(order) ? 'Teklifin Süresi Doldu' : 'Teklifiniz Geldi!')
                              : (isQuoteExpired(order) ? 'Quote Expired' : 'Quote Received!')}
                          </span>
                        </p>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <p className="text-xs text-gray-500">{language === 'tr' ? 'Teklif Fiyatı' : 'Offered Price'}</p>
                            <p className="text-base font-bold text-gray-900">
                              {order.quote_offer.currency} {fmt(order.quote_offer.offered_price)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{language === 'tr' ? 'Teslim Süresi' : 'Lead Time'}</p>
                            <p className="text-base font-bold text-gray-900">
                              {order.quote_offer.lead_time_days} {language === 'tr' ? 'gün' : 'days'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">{language === 'tr' ? 'Geçerlilik' : 'Valid Until'}</p>
                            <p className={`text-sm font-medium ${isQuoteExpired(order) ? 'text-red-600' : 'text-gray-700'}`}>
                              {new Date(order.quote_offer.validity_date).toLocaleDateString()}
                              {isQuoteExpired(order) && (
                                <span className="ml-1 text-xs">({language === 'tr' ? 'Doldu' : 'Expired'})</span>
                              )}
                            </p>
                          </div>
                        </div>
                        {order.quote_offer.notes && (
                          <p className="text-xs text-gray-600 bg-white/60 rounded-xl px-3 py-2 mb-3">
                            {order.quote_offer.notes}
                          </p>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAcceptQuote(order.id)}
                            disabled={isQuoteExpired(order) || quoteActionLoading !== null}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            data-testid={`accept-quote-btn-${order.id}`}
                          >
                            {quoteActionLoading === order.id + '-accept'
                              ? '...'
                              : (language === 'tr' ? 'Kabul Et' : 'Accept')}
                          </button>
                          <button
                            onClick={() => handleRejectQuote(order.id)}
                            disabled={quoteActionLoading !== null}
                            className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            data-testid={`reject-quote-btn-${order.id}`}
                          >
                            {quoteActionLoading === order.id + '-reject'
                              ? '...'
                              : (language === 'tr' ? 'Reddet' : 'Reject')}
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="border-t pt-3 flex justify-between font-bold">
                      <span>{t('total_amount')}:</span>
                      <span className="text-emerald-600">{order.currency || 'USD'} {fmt(order.total_amount || 0)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            )}
          </TabsContent>

          {/* ── Users / Invitations Tab ── */}
          <TabsContent value="users" className="space-y-6">
            {/* Invite Form Modal */}
            {showInviteForm && (
              <InvitationForm
                categories={invitationData.categories}
                zone={userInfo.zone || 'blue_zone'}
                onSubmit={async (...args) => {
                  await invitationData.sendInvitation(...args);
                  completeOnboardingStep('user_invited');
                }}
                onClose={() => setShowInviteForm(false)}
              />
            )}

            {/* Ana Kullanıcı (Main User) — Phase 86AZ.18: For sub-users the
                Main User card must reflect the ACTUAL tenant owner (Murat),
                not the logged-in sub-user. We surface the main user via the
                tenant's profile payload that the dashboard already fetches.
                Sub-users see this section read-only (no Invite button below). */}
            {(() => {
              const userInfo = getUserInfo();
              const isSubUser = userInfo.role === 'sub_user';
              // profileData carries the tenant's main user payload server-side
              // (full_name + email + role=customer_admin). Fallback to userInfo
              // for the main customer_admin themself.
              const mainName  = isSubUser ? (profileData?.main_user_full_name || profileData?.full_name || '—') : (userInfo.full_name || '—');
              // Phase 86AZ.18 — Backend returns `main_user_email` for sub-users
              // (resolved via main_user_id → users.email). Falls back to legacy
              // contact_email only when explicit field is missing.
              const mainEmail = isSubUser
                ? (profileData?.main_user_email || profileData?.email || profileData?.contact_email || '—')
                : (userInfo.email || '—');
              return (
                <Card className="border-0 shadow-lg rounded-3xl">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-xl">{language === 'tr' ? 'Ana Kullanıcı' : 'Main User'}</CardTitle>
                    <p className="text-sm text-gray-400">{language === 'tr' ? 'Hesabı oluşturan kullanıcı' : 'Account owner'}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 bg-emerald-50 rounded-2xl px-5 py-4">
                      <div className="h-11 w-11 rounded-full overflow-hidden bg-emerald-600 flex items-center justify-center flex-shrink-0">
                        {(!isSubUser && avatarUrl) ? (
                          <img src={avatarUrl} alt="avatar" className="h-full w-full object-cover" />
                        ) : (
                          <span className="text-lg font-bold text-white">
                            {(mainName || mainEmail || '?')[0]?.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{mainName}</p>
                        <p className="text-sm text-gray-500 truncate">{mainEmail}</p>
                      </div>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-600 text-white flex-shrink-0">
                        {language === 'tr' ? 'Ana Kullanıcı' : 'Main User'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Invitations Section — hidden for sub-users (cannot manage
                tenant-level invitations). */}
            {getUserInfo().role !== 'sub_user' && (
            <Card className="border-0 shadow-lg rounded-3xl">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                  <CardTitle className="text-xl">{language === 'tr' ? 'Davetler' : 'Invitations'}</CardTitle>
                  <p className="text-sm text-gray-400 mt-0.5">{language === 'tr' ? 'Gönderilen davetleri yönetin' : 'Manage sent invitations'}</p>
                </div>
                <Button
                  data-testid="send-invite-btn"
                  onClick={() => setShowInviteForm(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2"
                >
                  <Plus className="h-4 w-4" />
                  {language === 'tr' ? 'Kullanıcı Davet Et' : 'Invite User'}
                </Button>
              </CardHeader>
              <CardContent>
                {invitationData.loading ? (
                  <div className="text-center py-10">
                    <div className="animate-spin h-8 w-8 border-4 border-emerald-600 border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : (
                  <InvitationList
                    invitations={invitationData.invitations}
                    onResend={invitationData.resendInvitation}
                    onCancel={invitationData.cancelInvitation}
                    loading={invitationData.loading}
                  />
                )}
              </CardContent>
            </Card>
            )}

            {/* Sub-Users Section — visible to both main + sub users. Sub
                users see the team READ-ONLY (no toggle/delete actions). */}
            <Card className="border-0 shadow-lg rounded-3xl">
              <CardHeader>
                <CardTitle className="text-xl">{language === 'tr' ? 'Alt Kullanıcılar' : 'Sub Users'}</CardTitle>
                <p className="text-sm text-gray-400">
                  {getUserInfo().role === 'sub_user'
                    ? (language === 'tr' ? 'Bu hesabın ekip üyeleri' : 'Members of this account')
                    : (language === 'tr' ? 'Kabul edilmiş davetler ile oluşturulan kullanıcılar' : 'Users created from accepted invitations')}
                </p>
              </CardHeader>
              <CardContent>
                <SubUserList
                  subUsers={invitationData.subUsers}
                  onToggle={getUserInfo().role === 'sub_user' ? undefined : invitationData.toggleSubUserStatus}
                  onDelete={getUserInfo().role === 'sub_user' ? undefined : invitationData.deleteSubUser}
                  loading={invitationData.loading}
                />
              </CardContent>
            </Card>
          </TabsContent>


          <TabsContent value="profile" className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {language === 'tr' ? 'Profilim' : 'My Profile'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {language === 'tr' ? 'Hesap ve firma bilgilerinizi buradan yönetin.' : 'Manage your account and company information here.'}
                </p>
              </div>
              {!profileEdit && (
                <Button
                  data-testid="edit-profile-btn"
                  onClick={() => { setProfileForm(profileData || {}); setProfileEdit(true); setProfileMsg(''); }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-5"
                >
                  {language === 'tr' ? 'Firma Bilgilerini Düzenle' : 'Edit Company Info'}
                </Button>
              )}
              {profileEdit && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setProfileEdit(false); setProfileForm(profileData || {}); setProfileMsg(''); }} className="rounded-full px-5">
                    {language === 'tr' ? 'İptal' : 'Cancel'}
                  </Button>
                  <Button
                    data-testid="save-profile-btn"
                    onClick={saveProfile}
                    disabled={profileSaving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-5"
                  >
                    {profileSaving
                      ? <span className="flex items-center gap-2"><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{language === 'tr' ? 'Kaydediliyor...' : 'Saving...'}</span>
                      : (language === 'tr' ? 'Kaydet' : 'Save')}
                  </Button>
                </div>
              )}
            </div>

            {profileMsg && (
              <div className={`rounded-2xl px-4 py-3 text-sm font-medium ${profileMsg.startsWith('Hata') || profileMsg.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                {profileMsg}
              </div>
            )}

            {/* Hesap Bilgileri — Her zaman görünür */}
            {(() => {
              const userInfo = getUserInfo();
              return (
                <Card className="border-0 shadow-lg rounded-3xl">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">{language === 'tr' ? 'Hesap Bilgileri' : 'Account Information'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-5">
                      {/* Avatar Alanı */}
                      <div className="relative flex-shrink-0 group">
                        <div className="h-20 w-20 rounded-full overflow-hidden bg-emerald-100 flex items-center justify-center border-2 border-emerald-200">
                          {avatarLoading ? (
                            <span className="animate-spin h-6 w-6 border-2 border-emerald-600 border-t-transparent rounded-full" />
                          ) : avatarUrl ? (
                            <img src={avatarUrl} alt="avatar" className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-3xl font-bold text-emerald-700">
                              {(userInfo.full_name || userInfo.email || '?')[0]?.toUpperCase()}
                            </span>
                          )}
                        </div>
                        {/* Hover overlay */}
                        <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          onClick={() => avatarInputRef.current?.click()}>
                          <Camera className="h-5 w-5 text-white" />
                        </div>
                        <input
                          ref={avatarInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          className="hidden"
                          onChange={handleAvatarUpload}
                          data-testid="avatar-upload-input"
                        />
                      </div>

                      {/* Kullanıcı Bilgileri */}
                      <div className="flex-1 min-w-0">
                        <div className="grid sm:grid-cols-3 gap-4 mb-3">
                          <div>
                            <Label className="text-xs text-gray-400 uppercase tracking-wide">{language === 'tr' ? 'Ad Soyad' : 'Full Name'}</Label>
                            <p className="font-semibold text-gray-900 mt-0.5">{userInfo.full_name || '—'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-400 uppercase tracking-wide">Email</Label>
                            <p className="font-semibold text-gray-900 mt-0.5 truncate">{userInfo.email || '—'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-400 uppercase tracking-wide">{language === 'tr' ? 'Rol' : 'Role'}</Label>
                            <p className="font-semibold text-gray-900 mt-0.5 capitalize">{userInfo.role?.replace('_', ' ') || '—'}</p>
                          </div>
                        </div>
                        {/* Avatar aksiyon butonları */}
                        <div className="flex items-center gap-2">
                          <button
                            data-testid="change-avatar-btn"
                            onClick={() => avatarInputRef.current?.click()}
                            disabled={avatarLoading}
                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium border border-emerald-200 rounded-full px-3 py-1 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                          >
                            {avatarUrl
                              ? (language === 'tr' ? 'Resmi Değiştir' : 'Change Photo')
                              : (language === 'tr' ? 'Resim Ekle' : 'Add Photo')}
                          </button>
                          {avatarUrl && (
                            <button
                              data-testid="delete-avatar-btn"
                              onClick={handleAvatarDelete}
                              disabled={avatarLoading}
                              className="text-xs text-red-500 hover:text-red-600 font-medium border border-red-200 rounded-full px-3 py-1 hover:bg-red-50 transition-colors disabled:opacity-50"
                            >
                              {language === 'tr' ? 'Resmi Sil' : 'Remove Photo'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Firma Bilgileri başlığı */}
            <div className="flex items-center gap-3 mt-2">
              <h3 className="text-lg font-semibold text-gray-700">{language === 'tr' ? 'Firma Bilgileri' : 'Company Information'}</h3>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Firma bilgisi yok durumu */}
            {!profileEdit && profileData !== null && Object.keys(profileData).length === 0 && (
              <div className="flex items-center gap-4 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Package className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-amber-800 text-sm">
                    {language === 'tr' ? 'Firma bilgileriniz henüz doldurulmamış.' : 'Your company information has not been filled in yet.'}
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    {language === 'tr' ? '"Firma Bilgilerini Düzenle" butonuna tıklayarak ekleyebilirsiniz.' : 'Click "Edit Company Info" to add your details.'}
                  </p>
                </div>
              </div>
            )}

            {/* View Mode */}
            {!profileEdit && profileData && Object.keys(profileData).length > 0 && (
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-0 shadow-lg rounded-3xl">
                  <CardHeader><CardTitle className="text-xl">{language === 'tr' ? 'Firma Bilgileri' : 'Company Information'}</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { label: language === 'tr' ? 'Firma / Organizasyon Adı' : 'Company / Organization Name', val: profileData.company_name },
                      { label: language === 'tr' ? 'Sektör' : 'Industry', val: profileData.industry },
                      { label: language === 'tr' ? 'Kuruluş Yılı' : 'Founded', val: profileData.founded },
                      { label: language === 'tr' ? 'Çalışan Sayısı' : 'Employees', val: profileData.employees },
                      { label: language === 'tr' ? 'Hakkında' : 'About', val: profileData.about },
                    ].map(({ label, val }) => (
                      <div key={label}>
                        <Label className="text-xs text-gray-400 uppercase tracking-wide">{label}</Label>
                        {val ? (
                          <p className="font-medium text-gray-800 mt-0.5">{val}</p>
                        ) : (
                          <p className="font-medium text-gray-300 italic mt-0.5">{language === 'tr' ? '— Belirtilmemiş' : '— Not specified'}</p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg rounded-3xl">
                  <CardHeader><CardTitle className="text-xl">{language === 'tr' ? 'İletişim' : 'Contact'}</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      { label: language === 'tr' ? 'Telefon' : 'Phone', val: profileData.phone },
                      { label: 'Email', val: profileData.contact_email },
                      { label: language === 'tr' ? 'Adres' : 'Address', val: profileData.address },
                      { label: language === 'tr' ? 'Şehir' : 'City', val: profileData.city },
                      { label: language === 'tr' ? 'Ülke' : 'Country', val: profileData.country },
                      { label: language === 'tr' ? 'Web Sitesi' : 'Website', val: profileData.website },
                      { label: language === 'tr' ? 'Vergi No' : 'Tax ID', val: profileData.tax_id },
                    ].map(({ label, val }) => (
                      <div key={label}>
                        <Label className="text-xs text-gray-400 uppercase tracking-wide">{label}</Label>
                        {val ? (
                          <p className="font-medium text-gray-800 mt-0.5">{val}</p>
                        ) : (
                          <p className="font-medium text-gray-300 italic mt-0.5">{language === 'tr' ? '— Belirtilmemiş' : '— Not specified'}</p>
                        )}
                      </div>
                    ))}
                    {(profileData.social_facebook || profileData.social_twitter || profileData.social_linkedin || profileData.social_instagram) && (
                      <div className="flex gap-3 pt-2">
                        {profileData.social_facebook && <a href={profileData.social_facebook} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-emerald-600"><Facebook className="h-5 w-5" /></a>}
                        {profileData.social_twitter && <a href={profileData.social_twitter} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-emerald-600"><Twitter className="h-5 w-5" /></a>}
                        {profileData.social_linkedin && <a href={profileData.social_linkedin} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-emerald-600"><Linkedin className="h-5 w-5" /></a>}
                        {profileData.social_instagram && <a href={profileData.social_instagram} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-emerald-600"><Instagram className="h-5 w-5" /></a>}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Edit Form */}
            {profileEdit && (
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-0 shadow-lg rounded-3xl">
                  <CardHeader><CardTitle className="text-xl">{language === 'tr' ? 'Firma Bilgileri' : 'Company Information'}</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { key: 'company_name', label: language === 'tr' ? 'Firma / Organizasyon Adı' : 'Company / Organization Name', placeholder: language === 'tr' ? 'Firma veya organizasyon adınız...' : 'Your company or organization name...' },
                      { key: 'industry', label: language === 'tr' ? 'Sektör' : 'Industry', placeholder: language === 'tr' ? 'Örn: Etkinlik, Turizm...' : 'e.g. Events, Tourism...' },
                      { key: 'founded', label: language === 'tr' ? 'Kuruluş Yılı' : 'Founded Year', placeholder: '2000' },
                      { key: 'employees', label: language === 'tr' ? 'Çalışan Sayısı' : 'Employees', placeholder: language === 'tr' ? 'Örn: 50+' : 'e.g. 50+' },
                    ].map(({ key, label, placeholder }) => (                      <div key={key}>
                        <Label className="text-sm text-gray-600 mb-1">{label}</Label>
                        <Input
                          data-testid={`profile-${key}`}
                          value={profileForm[key] || ''}
                          onChange={e => setProfileForm(p => ({ ...p, [key]: e.target.value }))}
                          placeholder={placeholder}
                          className="rounded-xl"
                        />
                      </div>
                    ))}
                    <div>
                      <Label className="text-sm text-gray-600 mb-1">{language === 'tr' ? 'Hakkında' : 'About'}</Label>
                      <textarea
                        data-testid="profile-about"
                        value={profileForm.about || ''}
                        onChange={e => setProfileForm(p => ({ ...p, about: e.target.value }))}
                        placeholder={language === 'tr' ? 'Firmanız hakkında kısa bilgi...' : 'Brief description of your company...'}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                        rows={3}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg rounded-3xl">
                  <CardHeader><CardTitle className="text-xl">{language === 'tr' ? 'İletişim & Sosyal Medya' : 'Contact & Social Media'}</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { key: 'phone', label: language === 'tr' ? 'Telefon' : 'Phone', placeholder: '+1 234 567 8900' },
                      { key: 'contact_email', label: 'Email', placeholder: 'info@company.com' },
                      { key: 'address', label: language === 'tr' ? 'Adres' : 'Address', placeholder: language === 'tr' ? 'Tam adres...' : 'Full address...' },
                      { key: 'city', label: language === 'tr' ? 'Şehir' : 'City', placeholder: language === 'tr' ? 'İstanbul' : 'London' },
                      { key: 'country', label: language === 'tr' ? 'Ülke' : 'Country', placeholder: language === 'tr' ? 'Türkiye' : 'United Kingdom' },
                      { key: 'website', label: language === 'tr' ? 'Web Sitesi' : 'Website', placeholder: 'https://www.company.com' },
                    ].map(({ key, label, placeholder }) => (
                      <div key={key}>
                        <Label className="text-sm text-gray-600 mb-1">{label}</Label>
                        <Input
                          data-testid={`profile-${key}`}
                          value={profileForm[key] || ''}
                          onChange={e => setProfileForm(p => ({ ...p, [key]: e.target.value }))}
                          placeholder={placeholder}
                          className="rounded-xl"
                        />
                      </div>
                    ))}
                    <div className="pt-2 space-y-3">
                      <div>
                        <Label className="text-sm text-gray-600 mb-1">{language === 'tr' ? 'Vergi Kimlik No (Tax ID)' : 'Tax ID'}</Label>
                        <Input
                          data-testid="profile-tax_id"
                          value={profileForm.tax_id || ''}
                          onChange={e => setProfileForm(p => ({ ...p, tax_id: e.target.value }))}
                          placeholder={language === 'tr' ? 'Kurumsal fatura için (opsiyonel)' : 'For corporate invoicing (optional)'}
                          className="rounded-xl"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          {language === 'tr' ? 'Kurumsal fatura talep edecekseniz doldurunuz.' : 'Fill in if you require a corporate invoice.'}
                        </p>
                      </div>
                    </div>
                    <div className="pt-2 space-y-3">
                      <Label className="text-sm text-gray-500 uppercase tracking-wide">{language === 'tr' ? 'Sosyal Medya (opsiyonel)' : 'Social Media (optional)'}</Label>
                      {[
                        { key: 'social_facebook', placeholder: 'https://facebook.com/...' },
                        { key: 'social_twitter', placeholder: 'https://twitter.com/...' },
                        { key: 'social_linkedin', placeholder: 'https://linkedin.com/...' },
                        { key: 'social_instagram', placeholder: 'https://instagram.com/...' },
                      ].map(({ key, placeholder }) => (
                        <Input
                          key={key}
                          data-testid={`profile-${key}`}
                          value={profileForm[key] || ''}
                          onChange={e => setProfileForm(p => ({ ...p, [key]: e.target.value }))}
                          placeholder={placeholder}
                          className="rounded-xl text-sm"
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
        )}
      </div>

      {/* Sepet Modal */}
      {showCart && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowCart(false)}>
          <Card className="border-0 shadow-2xl rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-2xl">{language === 'tr' ? 'Sepetim' : 'My Cart'} ({cart.length})</CardTitle>
                <Button onClick={() => setShowCart(false)} variant="ghost" className="text-2xl">✕</Button>
              </div>
            </CardHeader>
            <CardContent>
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">{language === 'tr' ? 'Sepetiniz boş' : 'Your cart is empty'}</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-6">
                    {groupCartForDisplay(cart).map((item) => {
                      // ─── Forklift Grubu ───────────────────────────────────
                      if (item._isForkliftGroup) {
                        return (
                          <div key={item.key} className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
                            <div className="flex items-center gap-4">
                              {item.product_image ? (
                                <img src={item.product_image} alt={item.product_name} className="w-20 h-20 object-cover rounded-xl" />
                              ) : (
                                <div className="w-20 h-20 bg-blue-100 rounded-xl flex items-center justify-center">
                                  <Package className="h-8 w-8 text-blue-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-sm truncate">{item.product_name}</h4>
                                <p className="text-xs text-blue-600 font-semibold mt-0.5">
                                  {item.start_time} – {item.end_time}
                                  <span className="ml-1 font-normal text-gray-500">· {item.total_hours} {language === 'tr' ? 'saat' : 'hrs'}</span>
                                </p>
                                {item.slot_date && <p className="text-[11px] text-gray-400">{item.slot_date}</p>}
                                <p className="text-[11px] text-gray-400 mt-0.5">
                                  {item.currency} {item.unit_price} × {item.total_hours} {language === 'tr' ? 'saat' : 'hrs'} = <span className="font-semibold text-gray-600">{item.currency} {fmt(item.total_price, 0)}</span>
                                </p>
                                {item.expires_at && (
                                  <CartItemCountdown expiresAt={item.expires_at} language={language} />
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Forklift saatleri sabittir — miktarı değiştirilemez */}
                                <span className="text-xs text-blue-500 bg-blue-100 px-2 py-1 rounded-lg font-medium">
                                  {item.total_hours}h
                                </span>
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleRemoveForkliftGroup(item.items)}>🗑</Button>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="font-bold text-emerald-600">{item.currency} {fmt(item.total_price, 0)}</p>
                              </div>
                            </div>
                          </div>
                        );
                      }
                      // ─── Normal Ürün ─────────────────────────────────────
                      const { minQ: cMinQ, maxQ: cMaxQ } = getCartItemLimits(item);
                      const isAtMax = cMaxQ !== null && item.quantity >= cMaxQ;
                      const isCartonItem = (item.calculation_type || '') === 'carton_based';
                      return (
                      <div key={item.id} className="bg-gray-50 rounded-2xl p-4">
                        <div className="flex items-center gap-4">
                          {item.product_image ? (
                            <img src={item.product_image} alt={item.product_name} className="w-20 h-20 object-cover rounded-xl" />
                          ) : (
                            <div className="w-20 h-20 bg-gray-200 rounded-xl flex items-center justify-center">
                              <Package className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm truncate">{item.product_name}</h4>
                            {item.variant_name
                              ? <p className="text-xs text-gray-400 font-medium">{item.variant_name}</p>
                              : item.variant_id
                                ? <p className="text-xs text-gray-400">Orijinal</p>
                                : null}
                            {/* Cart expiry countdown */}
                            {item.expires_at && (
                              <CartItemCountdown expiresAt={item.expires_at} language={language} />
                            )}
                            {item.pricing_mode_selected === 'daily' && (
                              <p className="text-xs text-emerald-600 font-medium">
                                {language === 'tr' ? 'Günlük' : 'Daily'}
                                {item.number_of_days  && ` · ${item.number_of_days} ${language === 'tr' ? 'gün' : 'days'}`}
                                {item.start_date      && ` · ${item.start_date}`}
                                {item.end_date && item.end_date !== item.start_date && ` → ${item.end_date}`}
                                {item.number_of_people && ` · ${item.number_of_people} ${language === 'tr' ? 'kişi' : 'ppl'}`}
                              </p>
                            )}
                            {/* Fiyat dökümü — Günlük */}
                            {item.pricing_mode_selected === 'daily' && item.unit_price > 0 && (
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {item.currency || 'USD'} {item.unit_price}
                                {item.number_of_people ? ` × ${item.number_of_people} ${language === 'tr' ? 'kişi' : 'ppl'}` : ''}
                                {item.number_of_days   ? ` × ${item.number_of_days} ${language === 'tr' ? 'gün' : 'days'}` : ''}
                                {item.quantity > 1     ? ` × ${item.quantity}` : ''}
                                {' = '}
                                <span className="font-semibold text-gray-600">{item.currency || 'USD'} {fmt(item.line_total || 0, 0)}</span>
                              </p>
                            )}
                            {item.pricing_mode_selected === 'event' && (
                              <p className="text-xs text-amber-600 font-medium">
                                {language === 'tr' ? 'Etkinlik' : 'Event'}
                                {item.number_of_people && ` · ${item.number_of_people} ${language === 'tr' ? 'kişi' : 'ppl'}`}
                                {item.quantity > 1    && ` · ${item.quantity} adet`}
                              </p>
                            )}
                            {/* Fiyat dökümü — Etkinlik */}
                            {item.pricing_mode_selected === 'event' && item.unit_price > 0 && (
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {item.currency || 'USD'} {fmt(item.unit_price, 0)}
                                {item.number_of_people ? ` × ${item.number_of_people} ${language === 'tr' ? 'kişi' : 'ppl'}` : ''}
                                {item.quantity > 1     ? ` × ${item.quantity}` : ''}
                                {' = '}
                                <span className="font-semibold text-gray-600">{item.currency || 'USD'} {fmt(item.line_total || 0, 0)}</span>
                              </p>
                            )}
                            {!item.pricing_mode_selected && (
                              <p className="text-xs text-gray-500">{item.currency || 'USD'} {item.unit_price} × {item.quantity}</p>
                            )}
                            {/* Customization badge + preview */}
                            {(item.is_customized || item.customization_notes) && (
                              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50/70 px-2.5 py-1.5">
                                <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                                  <Wand2 className="h-3 w-3" />
                                  {language === 'tr' ? 'Özel Talep' : 'Custom Request'}
                                </p>
                                {item.customization_notes && (
                                  <div
                                    className="text-[11px] text-amber-900/80 mt-0.5 line-clamp-3 prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: item.customization_notes }}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm" variant="outline"
                              data-testid={`cart-qty-minus-${item.id}`}
                              disabled={item.quantity <= cMinQ}
                              onClick={() => {
                                if (item.quantity > cMinQ) handleUpdateCartQty(item, item.quantity - 1);
                              }}
                            >−</Button>
                            <span className="w-8 text-center text-sm font-bold" data-testid={`cart-qty-${item.id}`}>{item.quantity}</span>
                            <Button
                              size="sm" variant="outline"
                              data-testid={`cart-qty-plus-${item.id}`}
                              disabled={isAtMax}
                              title={isAtMax ? (language === 'tr' ? `Maks: ${cMaxQ} ${isCartonItem ? 'koli' : 'adet'}` : `Max: ${cMaxQ}`) : undefined}
                              onClick={() => handleUpdateCartQty(item, item.quantity + 1)}
                            >+</Button>
                            <Button size="sm" variant="ghost" className="text-red-600" data-testid={`cart-remove-${item.id}`} onClick={() => handleRemoveFromCart(item.id)}>🗑</Button>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-bold text-emerald-600" data-testid={`cart-line-total-${item.id}`}>
                              {item.currency || 'USD'} {fmt(item.line_total || item.unit_price * item.quantity || 0, 0)}
                            </p>
                            {cMaxQ !== null && (
                              <p className="text-[10px] text-gray-400">
                                {language === 'tr' ? `Maks: ${cMaxQ} ${isCartonItem ? 'koli' : 'adet'}` : `Max: ${cMaxQ}`}
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Kırmızı uyarı: max stoka ulaşıldı */}
                        {isAtMax && (
                          <div className="mt-2 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-1.5">
                            <span className="text-red-500 text-sm">⚠</span>
                            <p className="text-xs text-red-600 font-semibold" data-testid={`cart-max-warning-${item.id}`}>
                              {language === 'tr'
                                ? `Maksimum sipariş miktarına ulaştınız (${cMaxQ} ${isCartonItem ? 'koli' : 'adet'})`
                                : `Maximum order quantity reached (${cMaxQ} ${isCartonItem ? 'Pack' : 'pcs'})`}
                            </p>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  
                  <div className="border-t pt-4">
                    {/* Para birimi bazlı toplamlar */}
                    {(() => {
                      const byCurrency = cart.reduce((acc, item) => {
                        const cur = item.currency || 'USD';
                        const amt = item.line_total || (item.unit_price * item.quantity) || 0;
                        acc[cur] = (acc[cur] || 0) + amt;
                        return acc;
                      }, {});
                      const currencies = Object.keys(byCurrency);
                      return (
                        <div className="space-y-2 mb-6">
                          {currencies.map(cur => (
                            <div key={cur} className="flex justify-between items-center">
                              <span className="text-base font-semibold text-gray-600">
                                {language === 'tr' ? 'Toplam' : 'Total'} ({cur}):
                              </span>
                              <span className="text-xl font-bold text-emerald-600" data-testid={`cart-total-${cur}`}>
                                {cur} {fmt(byCurrency[cur], 0)}
                              </span>
                            </div>
                          ))}
                          {currencies.length > 1 && (
                            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-1.5 mt-1">
                              {language === 'tr'
                                ? 'Sepetinizdeki ürünler farklı para birimlerine ait. Ödeme sırasında döviz kuru uygulanacaktır.'
                                : 'Items in your cart use different currencies. Exchange rate will apply at checkout.'}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    <Button onClick={() => { setShowCart(false); setShowCheckout(true); }} className="w-full bg-emerald-600 rounded-full py-6 text-lg">
                      {language === 'tr' ? 'Ödemeye Geç' : 'Proceed to Checkout'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Checkout — CustomerCheckout bileşeni */}
      {showCheckout && (
        <CustomerCheckout
          cart={cart}
          language={language}
          profileData={profileData}
          userInfo={getUserInfo()}
          tenantSlug={slug}
          supplierTenantId="quattro-stand"
          onClose={() => setShowCheckout(false)}
          onSuccess={async (order) => {
            await fetchOrders();
            setCart([]);
            completeOnboardingStep('first_order');
            setTimeout(() => {
              setShowCheckout(false);
              navigate(`/musteri-admin/${slug}/orders`);
            }, 3000);
          }}
        />
      )}

      {/* Quote Request Modal */}
      {showQuoteModal && selectedProduct && (
        <QuoteRequestModal
          product={selectedProduct}
          language={language}
          supplierTenantId="quattro-stand"
          onClose={() => { setShowQuoteModal(false); setShowProductDetail(false); }}
        />
      )}

      {/* Phase 86E — Organizer Extra Preview Modal (centered).
          Opened by the "İncele / Preview" button on organizer extra cards. */}
      {organizerDetail && (() => {
        const o = organizerDetail;
        const name  = (language === 'tr' ? (o.name_tr || o.name_en) : (o.name_en || o.name_tr)) || o.name || '';
        const desc  = (language === 'tr' ? (o.description_tr || o.description_en) : (o.description_en || o.description_tr)) || '';
        const cat   = (language === 'tr' ? (o.category_tr || o.category_en) : (o.category_en || o.category_tr)) || '';
        const subc  = (language === 'tr' ? (o.sub_category_tr || o.sub_category_en) : (o.sub_category_en || o.sub_category_tr)) || '';
        const price = Number(o.extra_price || 0);
        const inStock = o.track_stock === true;
        const imgUrl  = o.cover_url || o.thumb_url || null;
        return (
          <div
            className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="organizer-detail-modal"
            onClick={() => setOrganizerDetail(null)}
          >
            <div
              className="bg-white rounded-3xl w-full max-w-lg max-h-[92vh] overflow-hidden shadow-2xl flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              {/* Header with image */}
              <div className="relative bg-gray-50 h-64 flex items-center justify-center">
                {imgUrl
                  ? <img src={imgUrl} alt={name} className="max-h-full max-w-full object-contain p-4" />
                  : <Package className="h-16 w-16 text-gray-300" />
                }
                <button
                  onClick={() => setOrganizerDetail(null)}
                  data-testid="organizer-detail-close-btn"
                  className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-gray-600 shadow"
                >
                  <X className="h-4 w-4" />
                </button>
                <span className="absolute top-3 left-3 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                  {language === 'tr' ? 'Organizer Ekstra' : 'Organizer Extra'}
                </span>
              </div>
              {/* Body */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {cat  && <Badge className="rounded-full text-[10px]">{cat}</Badge>}
                    {subc && <Badge className="rounded-full text-[10px] bg-gray-100 text-gray-700">{subc}</Badge>}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 leading-tight">{name}</h3>
                </div>
                {desc && (
                  <div>
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      {language === 'tr' ? 'Açıklama' : 'Description'}
                    </p>
                    <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{desc}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      {language === 'tr' ? 'Birim' : 'Unit'}
                    </p>
                    <p className="text-sm font-semibold text-gray-900">{o.unit || (language === 'tr' ? 'adet' : 'pcs')}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      {language === 'tr' ? 'İlave Fiyat' : 'Extra Price'}
                    </p>
                    <p className={`text-sm font-semibold ${price > 0 ? 'text-emerald-600' : 'text-gray-400 italic'}`}>
                      {price > 0 ? `${o.currency || 'EUR'} ${fmt(price)}` : (language === 'tr' ? 'Pakete dahil' : 'Included in package')}
                    </p>
                  </div>
                  {price > 0 && (
                    <>
                      <div>
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          {language === 'tr' ? 'Min. Adet' : 'Min Qty'}
                        </p>
                        <p className="text-sm font-semibold text-gray-900">{o.extra_min_qty || 1}</p>
                      </div>
                      {o.extra_max_qty != null && (
                        <div>
                          <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            {language === 'tr' ? 'Max. Adet' : 'Max Qty'}
                          </p>
                          <p className="text-sm font-semibold text-gray-900">{o.extra_max_qty}</p>
                        </div>
                      )}
                    </>
                  )}
                  {inStock && (
                    <div>
                      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                        {language === 'tr' ? 'Stokta' : 'In Stock'}
                      </p>
                      <p className="text-sm font-semibold text-gray-900">{o.stock_qty ?? 0}</p>
                    </div>
                  )}
                </div>
                {/* Phase 86F: banner only shown when customer has paid for
                    a stand AND this organizer product is a member of their
                    paid package's items list. */}
                {hasAnyPaidStand && paidPackageProductIds.has(o.id) && (
                  <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="organizer-included-banner">
                    {language === 'tr'
                      ? 'Bu ürün modüler stand paketinize dahildir. İlave adet almak isterseniz aşağıdaki butonu kullanabilirsiniz.'
                      : 'This product is included in your modular stand package. Use the button below to purchase additional units.'}
                  </p>
                )}
              </div>
              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  onClick={() => setOrganizerDetail(null)}
                  className="rounded-full"
                >
                  {language === 'tr' ? 'Kapat' : 'Close'}
                </Button>
                {price > 0 && (
                  <Button
                    className="bg-amber-500 hover:bg-amber-600 text-white rounded-full"
                    disabled={addingOrganizerId === o.id}
                    onClick={() => {
                      handleAddOrganizerExtra(o, { cover_thumb_url: o.thumb_url });
                      setOrganizerDetail(null);
                    }}
                    data-testid="organizer-detail-add-btn"
                  >
                    {addingOrganizerId === o.id
                      ? (language === 'tr' ? 'Ekleniyor...' : 'Adding...')
                      : (language === 'tr' ? `+ Sepete Ekle (${o.currency || 'EUR'} ${fmt(price)})` : `+ Add to Cart (${o.currency || 'EUR'} ${fmt(price)})`)
                    }
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Phase 86C — Stand Payment Lock Modal (centered).
          Shown when the user clicks a locked "Customize Booth" button. */}
      {showStandPaymentLockModal && (
        <div
          className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="stand-payment-lock-modal"
          onClick={() => setShowStandPaymentLockModal(false)}
        >
          <div
            className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-6 py-6 text-center">
              <div className="h-14 w-14 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center mx-auto mb-4">
                <Wand2 className="h-7 w-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {language === 'tr' ? 'Stand Özelleştirme Kilitli' : 'Booth Customization Locked'}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {language === 'tr'
                  ? 'Stand ödemesi yapıldıktan sonra aktif olacaktır.'
                  : 'Will be active after stand payment is made.'}
              </p>
              <p className="text-[12px] text-gray-400 mt-2">
                {language === 'tr'
                  ? 'Önce bu standı sepete ekleyin ve ödemenizi tamamlayın.'
                  : 'Add this stand to the cart and complete payment first.'}
              </p>
            </div>
            <div className="px-6 pb-6 flex justify-center">
              <Button
                onClick={() => setShowStandPaymentLockModal(false)}
                data-testid="stand-payment-lock-close-btn"
                className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-2 rounded-full"
              >
                {language === 'tr' ? 'Anladım' : 'Got it'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Customize Booth / Note for Supplier Modal — rich-text custom request attached to cart item */}
      {showCustomizeModal && (detailProduct || selectedProduct) && (() => {
        const _p = detailProduct || selectedProduct;
        const isPlatformProduct =
          (_p?.tenant_id === 'summitflow-platform') ||
          (_p?.supplier_id === 'summitflow-platform');
        const modalTitle = isPlatformProduct
          ? (language === 'tr' ? 'Standı Özelleştir'       : 'Customize Booth')
          : (language === 'tr' ? 'Tedarikçiye Not Bırak'   : 'Note for Supplier');
        const modalSubtitle = isPlatformProduct
          ? (language === 'tr'
              ? 'Bu stand için özel taleplerinizi aşağıya yazın. İsteğiniz sepete eklenecek ve tedarikçiyle paylaşılacaktır.'
              : 'Describe any special requirements for this booth. Your request will be added to the cart and shared with the supplier.')
          : (language === 'tr'
              ? 'Bu ürün için tedarikçiye iletmek istediğiniz notu aşağıya yazın. Talebiniz sepete eklenecek ve siparişinizle birlikte paylaşılacaktır.'
              : 'Write a note to the supplier about this product. It will be attached to your cart item and shared with the order.');
        const editorLabel = isPlatformProduct
          ? (language === 'tr' ? 'Özelleştirme Talepleriniz'              : 'Your customization requests')
          : (language === 'tr' ? 'Tedarikçiye Notunuz'                    : 'Your note to the supplier');
        const editorPh = isPlatformProduct
          ? (language === 'tr'
              ? 'Ör: Logo konumu, renk teması, mobilya/ekran ekleme, sponsor alanları, LED ekran boyutu, materyal tercihi...'
              : 'e.g. Logo position, color theme, extra furniture/screens, sponsor areas, LED screen size, material preference...')
          : (language === 'tr'
              ? 'Ör: Teslim tarihi, özel renk/boyut talebi, paketleme tercihi, montaj ihtiyacı, teslim adresi detayları...'
              : 'e.g. Delivery date, special color/size request, packaging preference, setup needs, delivery address details...');
        const submitLabel = isPlatformProduct
          ? (language === 'tr' ? 'Özelleştirmeyi Sepete Ekle' : 'Add Customization to Cart')
          : (language === 'tr' ? 'Notu Sepete Ekle'           : 'Add Note to Cart');
        const successMsg = isPlatformProduct
          ? (language === 'tr' ? 'Özelleştirme talebiniz sepete eklendi!' : 'Your customization has been added to the cart!')
          : (language === 'tr' ? 'Notunuz sepete eklendi!'                : 'Your note has been added to the cart!');

        return (
        <div
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !customizeSubmitting && setShowCustomizeModal(false)}
          data-testid="customize-booth-modal"
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-start justify-between"
              style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Wand2 className="h-5 w-5 text-white" />
                  <h2 className="text-lg font-bold text-white">{modalTitle}</h2>
                </div>
                <p className="text-xs text-white/80 max-w-md">{modalSubtitle}</p>
              </div>
              <button
                onClick={() => !customizeSubmitting && setShowCustomizeModal(false)}
                className="text-white/80 hover:text-white p-1 rounded-full shrink-0"
                data-testid="customize-close-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* Ürün özeti */}
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-2xl p-3">
                <Package className="h-5 w-5 text-amber-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-900 truncate">
                    {language === 'tr'
                      ? (_p.title_tr || _p.title_en || _p.title)
                      : (_p.title_en || _p.title_tr || _p.title)}
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    {_p.currency || 'USD'} {fmt(selectedLines?.[0]?.line_total || _p.event_price || _p.daily_price || 0, 0)}
                  </p>
                </div>
              </div>

              {/* Rich text editor */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">
                  {editorLabel}
                </label>
                <RichTextEditor
                  value={customizeNotes}
                  onChange={setCustomizeNotes}
                  placeholder={editorPh}
                  minHeight={200}
                />
                <p className="mt-1.5 text-[11px] text-gray-400">
                  {language === 'tr'
                    ? 'İpucu: Listeler, kalın/italik metin ve bağlantılar kullanabilirsiniz.'
                    : 'Tip: You can use lists, bold/italic text and links.'}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-3 bg-gray-50">
              <Button
                variant="outline"
                onClick={() => !customizeSubmitting && setShowCustomizeModal(false)}
                className="rounded-full px-5"
                data-testid="customize-cancel-btn"
              >
                {language === 'tr' ? 'Vazgeç' : 'Cancel'}
              </Button>
              <Button
                disabled={customizeSubmitting || !customizeNotes || customizeNotes.replace(/<[^>]*>/g, '').trim().length < 3}
                onClick={async () => {
                  if (!_p) return;
                  setCustomizeSubmitting(true);
                  try {
                    let lines = selectedLines && selectedLines.length > 0
                      ? selectedLines
                      : [{
                          _key: 'customize',
                          pricing_mode_selected: _p.event_price ? 'event' : 'daily',
                          calculation_type: _p.calculation_type || 'flat',
                          quantity: 1,
                          unit_price: parseFloat(_p.event_price || _p.daily_price || _p.effective_price || 0),
                          line_total: parseFloat(_p.event_price || _p.daily_price || _p.effective_price || 0),
                          currency: _p.currency || 'USD',
                          start_date: eventConfig?.event_start || null,
                          end_date:   eventConfig?.event_end   || null,
                          number_of_days: eventConfig?.event_days || null,
                        }];
                    lines = lines.map(l => ({ ...l, customization_notes: customizeNotes, is_customized: true }));
                    await handleAddLinesToCart(_p, lines);
                    toast.success(successMsg);
                    setShowCustomizeModal(false);
                    setShowProductDetail(false);
                    setCustomizeNotes('');
                  } catch (e) {
                    toast.error(language === 'tr' ? 'Bir hata oluştu.' : 'Something went wrong.');
                  } finally {
                    setCustomizeSubmitting(false);
                  }
                }}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-full py-5 text-sm font-semibold gap-2 flex items-center justify-center"
                data-testid="customize-submit-btn"
              >
                {customizeSubmitting
                  ? <><Loader2 className="h-4 w-4 animate-spin" />{language === 'tr' ? 'Ekleniyor...' : 'Adding...'}</>
                  : <><ShoppingCart className="h-4 w-4" />{submitLabel}</>}
              </Button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Success Modal */}
      {showSuccess && (        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="border-0 shadow-2xl rounded-3xl max-w-md w-full">
            <CardContent className="pt-12 pb-8 text-center">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold mb-4">{language === 'tr' ? 'Odeme Basarili!' : 'Payment Successful!'}</h2>
              <p className="text-gray-600 mb-8">{language === 'tr' ? 'Siparisинiz alindi.' : 'Order received.'}</p>
              <Button onClick={() => setShowSuccess(false)} className="w-full bg-emerald-600 rounded-full py-6">OK</Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─── Lightbox ─────────────────────────────────────────── */}
      {lightboxOpen && lightboxImgs.length > 0 && (
        <div
          className="fixed inset-0 bg-black/95 z-[70] flex flex-col items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Close */}
          <button onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2.5 transition-colors z-10">
            ✕
          </button>
          {/* Counter */}
          <span className="absolute top-5 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {lightboxIdx + 1} / {lightboxImgs.length}
          </span>
          {/* Prev */}
          {lightboxIdx > 0 && (
            <button
              onClick={e => { e.stopPropagation(); setLightboxIdx(i => i - 1); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 text-2xl leading-none transition-colors">
              ‹
            </button>
          )}
          {/* Main image */}
          <div onClick={e => e.stopPropagation()} className="flex items-center justify-center w-full px-16" style={{ maxHeight: '80vh' }}>
            <img
              src={lightboxImgs[lightboxIdx]?.url}
              alt=""
              className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl transition-opacity duration-200"
            />
          </div>
          {/* Next */}
          {lightboxIdx < lightboxImgs.length - 1 && (
            <button
              onClick={e => { e.stopPropagation(); setLightboxIdx(i => i + 1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 text-2xl leading-none transition-colors">
              ›
            </button>
          )}
          {/* Thumbnail strip */}
          {lightboxImgs.length > 1 && (
            <div className="absolute bottom-5 flex gap-2 overflow-x-auto max-w-[90vw] px-2" onClick={e => e.stopPropagation()}>
              {lightboxImgs.map((img, i) => (
                <img key={i}
                  src={img.thumb_url || img.url}
                  alt=""
                  onClick={() => setLightboxIdx(i)}
                  className={`w-14 h-14 shrink-0 object-cover rounded-lg cursor-pointer border-2 transition-all ${i === lightboxIdx ? 'border-white scale-110 shadow-lg' : 'border-white/20 opacity-50 hover:opacity-80'}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Alternatif Stand Detay Modal — büyütülmüş kroki */}
      {altDetailModal && (
        <div
          className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setAltDetailModal(null)}
          data-testid="alt-detail-modal-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="alt-detail-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-orange-50 sticky top-0">
              <div>
                <h3 className="text-base font-bold text-orange-800">
                  {language === 'tr' ? `${altDetailModal.idx + 1}. Seçenek` : `Option ${altDetailModal.idx + 1}`}
                  {altDetailModal.alt.stand_no && (
                    <span className="ml-2 font-mono bg-orange-200 text-orange-900 px-2 py-0.5 rounded text-xs">{altDetailModal.alt.stand_no}</span>
                  )}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {language === 'tr' ? 'Stand Görünümü' : 'Stand Visualization'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAltDetailModal(null)}
                className="p-1.5 rounded-md hover:bg-orange-100 text-gray-500 hover:text-gray-700 transition-colors"
                data-testid="alt-detail-modal-close"
                aria-label={language === 'tr' ? 'Kapat' : 'Close'}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Visualizer */}
            <div className="p-6">
              <div className="flex items-center justify-center bg-gray-50 rounded-xl p-4 min-h-[320px]">
                <StandLayoutVisualizer
                  cells={altDetailModal.alt.cells || []}
                  openSides={altDetailModal.alt.open_sides || []}
                  standNo={altDetailModal.alt.stand_no || ''}
                  areaSqm={altDetailModal.alt.area_sqm || altDetailModal.alt.area || null}
                  cellSize={32}
                  padding={50}
                />
              </div>

              {/* Side legend (A/B/C/D + open/closed) */}
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center font-bold text-[11px]">A</div>
                  <span>{language === 'tr' ? 'A Kenarı' : 'Side A'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-[11px]">B</div>
                  <span>{language === 'tr' ? 'B Kenarı' : 'Side B'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold text-[11px]">C</div>
                  <span>{language === 'tr' ? 'C Kenarı' : 'Side C'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-[11px]">D</div>
                  <span>{language === 'tr' ? 'D Kenarı' : 'Side D'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-1 bg-green-500 rounded"></div>
                  <span>{language === 'tr' ? 'Açık Kenar' : 'Open Side'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-1 bg-red-500 rounded"></div>
                  <span>{language === 'tr' ? 'Kapalı Kenar' : 'Closed Side'}</span>
                </div>
              </div>

              {/* Quick info row */}
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                {altDetailModal.alt.hall_name && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <p className="text-gray-400 text-[10px] uppercase tracking-wide">Hall</p>
                    <p className="font-bold text-gray-800">{altDetailModal.alt.hall_name}</p>
                  </div>
                )}
                {(altDetailModal.alt.area_sqm || altDetailModal.alt.area) && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <p className="text-gray-400 text-[10px] uppercase tracking-wide">{language === 'tr' ? 'Alan' : 'Area'}</p>
                    <p className="font-bold text-orange-700">{altDetailModal.alt.area_sqm || altDetailModal.alt.area} m²</p>
                  </div>
                )}
                {altDetailModal.alt.open_sides?.length > 0 && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <p className="text-gray-400 text-[10px] uppercase tracking-wide">{language === 'tr' ? 'Açık Kenarlar' : 'Open Sides'}</p>
                    <p className="font-bold text-emerald-700 tracking-wider">
                      {(altDetailModal.alt.open_sides || []).map(s => ({north:'A',east:'B',south:'C',west:'D'}[s])).filter(Boolean).sort().join(', ')}
                    </p>
                  </div>
                )}
              </div>

              {altDetailModal.alt.location_note && (
                <p className="mt-4 text-xs text-gray-600 italic bg-amber-50 border-l-4 border-amber-300 px-3 py-2 rounded">
                  {altDetailModal.alt.location_note}
                </p>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2 sticky bottom-0">
              <button
                type="button"
                onClick={() => setAltDetailModal(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                data-testid="alt-detail-modal-cancel"
              >
                {language === 'tr' ? 'Kapat' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 86AA — Stand Area Approval Confirmation Modal.
          Customer must explicitly acknowledge the irreversible nature
          of the approval (locks contract pipeline + freezes change
          requests) before we POST /approve-area. Replaces the legacy
          window.confirm() shim that the user reported as ugly. */}
      {approveAreaModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          data-testid="approve-area-modal"
          onClick={() => { if (!areaApproving) setApproveAreaModalOpen(false); }}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
              <div className="h-12 w-12 rounded-2xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base">
                  {language === 'tr' ? 'Emin misiniz?' : 'Are you sure?'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {language === 'tr' ? 'Bu işlem geri alınamaz' : 'This action cannot be undone'}
                </p>
              </div>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm text-gray-700 leading-relaxed">
              <p>
                {language === 'tr'
                  ? 'Stand alanınızı onayladığınızda:'
                  : 'When you approve your stand area:'}
              </p>
              <ul className="space-y-2 text-sm pl-1">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>
                    {language === 'tr'
                      ? 'Stand konumunuz kesinleşir ve sözleşme süreci başlar.'
                      : 'Your stand location becomes final and the contract process begins.'}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <span>
                    {language === 'tr'
                      ? 'Bu noktadan sonra artık değişiklik talebi gönderemezsiniz.'
                      : 'You will no longer be able to submit any change requests after this.'}
                  </span>
                </li>
              </ul>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex gap-2 justify-end">
              <button
                onClick={() => setApproveAreaModalOpen(false)}
                disabled={areaApproving}
                data-testid="approve-area-cancel-btn"
                className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                {language === 'tr' ? 'Vazgeç' : 'Cancel'}
              </button>
              <button
                onClick={handleApproveArea}
                disabled={areaApproving}
                data-testid="approve-area-confirm-btn"
                className="px-4 py-2 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-60 flex items-center gap-2"
                style={{background: 'linear-gradient(135deg, #d97706, #b45309)'}}
              >
                {areaApproving ? (
                  <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> {language === 'tr' ? 'Onaylanıyor...' : 'Approving...'}</>
                ) : (
                  <><CheckCircle className="h-4 w-4" /> {language === 'tr' ? 'Evet, Onaylıyorum' : 'Yes, I Approve'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Onboarding Modal */}
      <OnboardingModal
        show={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        steps={onboardingSteps}
        storageKey="customer_dashboard_onboarding"
        language={language}
      />
    </div>
  );
};

export default CustomerDashboard;

// ─────────────────────────────────────────────────────────────
// Stand Design Section (embedded in CustomerDashboard)
// ─────────────────────────────────────────────────────────────
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Simple language detector (TR chars heuristic) ────────────
function detectLang(text = '') {
  if (/[çğışöüÇĞİŞÖÜ]/.test(text)) return 'tr';
  if (/\b(merhaba|teşekkür|lütfen|tamam|evet|hayır|bu|bir|ve|için|ile|nasıl)\b/i.test(text)) return 'tr';
  return 'unknown';
}

// ── Date divider formatter ────────────────────────────────────
function formatMsgDate(iso, tr) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return tr ? 'Bugün' : 'Today';
  if (d.toDateString() === yesterday.toDateString()) return tr ? 'Dün' : 'Yesterday';
  return d.toLocaleDateString(tr ? 'tr-TR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ── Business hours helper — fetches from API, falls back to local ─────────────
let _bhCache = null;
async function fetchBusinessHours() {
  if (_bhCache) return _bhCache;
  try {
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const r = await fetch(`${BACKEND}/api/ultra-admin/business-hours/public`);
    _bhCache = await r.json();
    setTimeout(() => { _bhCache = null; }, 60000); // refresh every 60s
    return _bhCache;
  } catch {
    return { timezone: 'Europe/Istanbul', start_hour: 9, end_hour: 18, working_days: [1,2,3,4,5],
      tr_message: 'Hafta içi 09:00–18:00 saatleri arasında (Türkiye saatiyle) yanıt verilmektedir.',
      en_message: 'Our team replies on weekdays between 09:00–18:00 (Turkey Time, UTC+3).',
      team_name_tr: 'COP 31 Destek Ekibi', team_name_en: 'COP 31 Support Team' };
  }
}

function calcStatus(bh) {
  try {
    const now = new Date();
    const ist = new Date(now.toLocaleString('en-US', { timeZone: bh.timezone || 'Europe/Istanbul' }));
    const h = ist.getHours();
    const d = ist.getDay() === 0 ? 7 : ist.getDay();
    const isWorkDay = (bh.working_days || [1,2,3,4,5]).includes(d);
    const isWorkHour = h >= (bh.start_hour ?? 9) && h < (bh.end_hour ?? 18);
    const timeStr = ist.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    return { online: isWorkDay && isWorkHour, timeStr };
  } catch {
    return { online: false, timeStr: '' };
  }
}

// ── Chat Header with live business hours ──────────────────────────
function ChatHeader({ tr }) {
  const [bh, setBh] = useState(null);
  useEffect(() => { fetchBusinessHours().then(setBh); }, []);

  const status = bh ? calcStatus(bh) : { online: false, timeStr: '' };
  const teamName = bh ? (tr ? bh.team_name_tr : bh.team_name_en) : (tr ? 'COP 31 Destek Ekibi' : 'COP 31 Support Team');
  const startH = bh ? String(bh.start_hour).padStart(2,'0') + ':00' : '09:00';
  const endH   = bh ? String(bh.end_hour).padStart(2,'0') + ':00' : '18:00';

  return (
    <div className="px-5 py-4 border-b border-gray-100 bg-white">
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shadow-sm">
            <span className="text-white text-sm font-bold">SF</span>
          </div>
          <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${status.online ? 'bg-emerald-400' : 'bg-gray-300'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm">{teamName}</p>
          <p className={`text-xs ${status.online ? 'text-emerald-600' : 'text-gray-400'}`}>
            {status.online
              ? (tr ? `Çevrimiçi — şu an ${status.timeStr} (İstanbul)` : `Online — ${status.timeStr} Istanbul time`)
              : (tr
                  ? `Çevrimdışı — Hafta içi ${startH}–${endH} arası yanıt verilir`
                  : `Offline — Replies on weekdays ${startH}–${endH} (Istanbul)`)}
          </p>
        </div>
        <div className={`hidden sm:flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${status.online ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${status.online ? 'bg-emerald-500' : 'bg-gray-400'}`} />
          {status.online ? (tr ? 'Aktif' : 'Active') : (tr ? 'Mesai Dışı' : 'Off Hours')}
        </div>
      </div>
    </div>
  );
}

// ── Welcome system message — from API ────────────────────────────────────────
function WelcomeMessage({ tr }) {
  const [bh, setBh] = useState(null);
  useEffect(() => { fetchBusinessHours().then(setBh); }, []);

  const teamName = bh ? (tr ? bh.team_name_tr : bh.team_name_en) : (tr ? 'COP 31 Destek Ekibi' : 'COP 31 Support Team');
  const msgText  = bh ? (tr ? bh.tr_message : bh.en_message) : '';
  const startH   = bh ? String(bh.start_hour).padStart(2,'0') + ':00' : '09:00';
  const endH     = bh ? String(bh.end_hour).padStart(2,'0') + ':00' : '18:00';
  const tz       = bh?.timezone === 'Europe/Istanbul' ? (tr ? 'Türkiye Saatiyle' : 'Turkey Time (UTC+3)') : (bh?.timezone || 'UTC');

  return (
    <div className="flex justify-center mb-3">
      <div className="bg-orange-50 border border-orange-100 rounded-2xl px-4 py-3 max-w-[90%] text-center">
        <p className="text-xs font-semibold text-orange-700 mb-1">{teamName}</p>
        {msgText && <p className="text-xs text-gray-600 leading-relaxed">{msgText}</p>}
        <p className="text-xs text-orange-400 mt-1.5 font-medium">
          {tr ? `Hafta içi ${startH}–${endH} · ${tz}` : `Mon–Fri ${startH}–${endH} · ${tz}`}
        </p>
      </div>
    </div>
  );
}

function StandDesignSection({ slug, language, onUnreadChange, myLayout: parentLayout, areaApprovalStatus }) {
  const tr = language === 'tr';
  const token = localStorage.getItem('cop31_token');
  const headers = { Authorization: `Bearer ${token}` };
  const navigate = useNavigate();
  const [showStandsCatalog, setShowStandsCatalog] = useState(false);
  const [editingAreaRequest, setEditingAreaRequest] = useState(false);
  const [areaReqForm, setAreaReqForm] = useState({ stand_area: '', stand_design_type: '', stand_layout_type: '', stand_request_notes: '' });
  const [savingAreaReq, setSavingAreaReq] = useState(false);
  const [availableAreas, setAvailableAreas] = useState([25, 50, 100, 150]);
  const [availableCustomAreas, setAvailableCustomAreas] = useState([50, 100, 150, 200, 300]);
  const [designPdfFile, setDesignPdfFile] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [requestSuccessModal, setRequestSuccessModal] = useState(null); // null | { submittedAt, designType, area, layout }
  const [noteModalOpen, setNoteModalOpen] = useState(null); // null | { text, updatedAt }
  const [editingNote, setEditingNote] = useState(false);
  const [draftNote, setDraftNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    apiClient.get(`/ultra-admin/stand-options`)
      .then(r => {
        if (r.data?.modular_areas?.length) setAvailableAreas(r.data.modular_areas);
        if (r.data?.custom_areas?.length)  setAvailableCustomAreas(r.data.custom_areas);
      })
      .catch(() => {});
  }, []);
  const myLayout = parentLayout;

  const [designs, setDesigns] = useState([]);
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [messages, setMessages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [standInfo, setStandInfo] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [msgFile, setMsgFile] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const msgFileRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pollDesignsRef = useRef(null);
  const pollMessagesRef = useRef(null);
  const selectedDesignRef = useRef(null); // track current open design for polling

  const [notes, setNotes] = useState('');
  const [designFiles, setDesignFiles] = useState([]);
  const designFileRef = useRef(null);
  // Phase 86AZ.27 — Technical files bucket (DWG/PDF/PPT) tracked separately
  // from creative/visual design files. Same 5-file / 100 MB per-file gate.
  const [technicalFiles, setTechnicalFiles] = useState([]);
  const technicalFileRef = useRef(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [fileActionLoading, setFileActionLoading] = useState(null);
  const [onboardingProfile, setOnboardingProfile] = useState(null);

  // Derived request status: 'none' | 'pending' | 'under_review' | 'allocated'
  const requestStatus = (() => {
    if (myLayout?.stand_no) return 'allocated';
    const submitted = onboardingProfile?.stand_request_submitted_at;
    if (!submitted) return 'none';
    return onboardingProfile?.stand_request_status || 'pending';
  })();
  const formattedSubmittedAt = onboardingProfile?.stand_request_submitted_at
    ? new Date(onboardingProfile.stand_request_submitted_at).toLocaleString(tr ? 'tr-TR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;
  const formattedReviewedAt = onboardingProfile?.stand_request_reviewed_at
    ? new Date(onboardingProfile.stand_request_reviewed_at).toLocaleString(tr ? 'tr-TR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  // Modular package state (shown when user selected modular + area matches a defined package)
  const [modularPackage, setModularPackage] = useState(null);
  const [packageExtras, setPackageExtras] = useState({}); // {product_id: extraQty (fazladan ilave adet)}
  const [addingPackage, setAddingPackage] = useState(false);
  const replaceRefs = useRef({});

  const handleDeleteFile = async (filename, fileType = 'design') => {
    if (!window.confirm(tr ? 'Bu dosyayı silmek istediğinize emin misiniz?' : 'Delete this file?')) return;
    setFileActionLoading(filename);
    try {
      const r = await apiClient.delete(`/stand-designs/my/${selectedDesignRef.current?.id}/files/${filename}?file_type=${fileType}`);
      const field = fileType === 'technical' ? 'technical_files' : 'design_files';
      setSelectedDesign(prev => ({ ...prev, [field]: r.data[field] }));
    } catch {}
    finally { setFileActionLoading(null); }
  };

  const handleReplaceFile = async (filename, newFile, fileType = 'design') => {
    if (!newFile) return;
    setFileActionLoading(filename);
    try {
      const fd = new FormData();
      fd.append('file', newFile);
      fd.append('replace_filename', filename);
      fd.append('file_type', fileType);
      const r = await apiClient.post(`/stand-designs/my/${selectedDesignRef.current?.id}/files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const field = fileType === 'technical' ? 'technical_files' : 'design_files';
      setSelectedDesign(prev => ({ ...prev, [field]: r.data[field] }));
    } catch (e) { alert(e.response?.data?.detail || 'Hata'); }
    finally { setFileActionLoading(null); }
  };

  // Phase 86AZ — Add a brand-new design file (no replacement). The backend
  // assigns the next version number automatically. `fileType` selects which
  // bucket (design vs technical) the file lands in.
  const addFileRef = useRef(null);
  const [addFileLoading, setAddFileLoading] = useState(false);
  // Phase 86AZ.28 — Which file bucket is currently visible in the detail
  // panel. Controls both rendering AND which bucket new uploads go to.
  const [filesTab, setFilesTab] = useState('design');
  // Phase 86AZ.34 — Müşteri detay sayfası sekmesi: Stand Brief | Sohbet
  const [designDetailTab, setDesignDetailTab] = useState('info');
  const handleAddFile = async (newFile, fileType = 'design') => {
    if (!newFile) return;
    setAddFileLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', newFile);
      fd.append('file_type', fileType);
      const r = await apiClient.post(`/stand-designs/my/${selectedDesignRef.current?.id}/files`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const field = fileType === 'technical' ? 'technical_files' : 'design_files';
      setSelectedDesign(prev => ({ ...prev, [field]: r.data[field] }));
    } catch (e) { alert(e.response?.data?.detail || 'Hata'); }
    finally { setAddFileLoading(false); }
  };

  const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  // ── Poll designs list every 8s to refresh unread counts ───────────────
  const refreshDesigns = async () => {
    try {
      const r = await apiClient.get(`/stand-designs/my`);
      setDesigns(r.data || []);
      const total = (r.data || []).reduce((s, d) => s + (d.unread_count || 0), 0);
      onUnreadChange?.(total);
      if (selectedDesignRef.current) {
        const updated = (r.data || []).find(d => d.id === selectedDesignRef.current.id);
        if (updated) setSelectedDesign(prev => ({ ...prev, unread_count: updated.unread_count }));
      }
    } catch {}
  };

  // ── Poll messages every 4s when design is open ───────────────────────
  const refreshMessages = async (designId) => {
    try {
      const r = await apiClient.get(`/stand-designs/my/${designId}/messages`);
      const incoming = r.data || [];
      setMessages(prev => {
        if (prev.length === incoming.length) return prev; // no change
        return incoming;
      });
    } catch {}
  };

  // ── Mark messages as read ─────────────────────────────────────────────
  const markRead = async (designId) => {
    try {
      await apiClient.post(`/stand-designs/my/${designId}/messages/mark-read`, {});
      setDesigns(prev => {
        const updated = prev.map(d => d.id === designId ? { ...d, unread_count: 0 } : d);
        const total = updated.reduce((s, d) => s + (d.unread_count || 0), 0);
        onUnreadChange?.(total);
        return updated;
      });
    } catch {}
  };

  useEffect(() => {
    fetchDesigns();
    fetchStandInfo();
    fetchOnboardingProfile();
    // Start polling designs list
    pollDesignsRef.current = setInterval(refreshDesigns, 8000);
    return () => { clearInterval(pollDesignsRef.current); clearInterval(pollMessagesRef.current); };
  }, []);



  useEffect(() => {
    selectedDesignRef.current = selectedDesign;
    clearInterval(pollMessagesRef.current);
    if (selectedDesign) {
      fetchMessages(selectedDesign.id);
      markRead(selectedDesign.id);
      // Start polling messages every 4s
      pollMessagesRef.current = setInterval(() => {
        if (selectedDesignRef.current) refreshMessages(selectedDesignRef.current.id);
      }, 4000);
    }
    return () => clearInterval(pollMessagesRef.current);
  }, [selectedDesign?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchStandInfo = async () => {
    try {
      const r = await apiClient.get(`/customer/my-layout`);
      setStandInfo(r.data);
    } catch {}
  };

  const fetchOnboardingProfile = async () => {
    try {
      const r = await apiClient.get(`/customer/profile`);
      setOnboardingProfile(r.data || null);
      // Modular paket seçtiyse ilgili m² paketini de fetch et
      if ((r.data?.stand_design_type === 'modular') && r.data?.stand_area) {
        try {
          const pkgRes = await apiClient.get(`/customer/modular-package`, {
            headers,
            params: { area_m2: r.data.stand_area },
          });
          setModularPackage(pkgRes.data?.package || null);
        } catch {
          setModularPackage(null);
        }
      } else {
        setModularPackage(null);
      }
    } catch {}
  };

  // Ekstra miktarlarını yönet
  const setExtra = (productId, qty) => {
    const q = Math.max(0, Number(qty) || 0);
    setPackageExtras(prev => {
      const n = { ...prev };
      if (q === 0) delete n[productId];
      else n[productId] = q;
      return n;
    });
  };

  // Paketi (ve varsa ekstraları) sepete ekle
  const handleAddPackageToCart = async () => {
    if (!modularPackage) return;
    setAddingPackage(true);
    try {
      const lines = [];
      // Ana paket kalemi — tek line, toplu fiyat
      const pkgLocalizedName = (tr ? (modularPackage.name_tr || modularPackage.name) : (modularPackage.name_en || modularPackage.name)) || modularPackage.name;
      lines.push({
        product_id:         `modular-package-${modularPackage.id}`,
        product_name:       `${pkgLocalizedName} (${modularPackage.area_m2} m²)`,
        product_image:      null,
        calculation_type:   'flat',
        unit_price:         Number(modularPackage.total_price || 0),
        line_total:         Number(modularPackage.total_price || 0),
        currency:           modularPackage.currency || 'EUR',
        quantity:           1,
        supplier_tenant_id: 'organizer',
        notes:              tr ? `COP 31 Modüler Stand Paketi · ${modularPackage.area_m2} m²` : `COP 31 Modular Stand Package · ${modularPackage.area_m2} m²`,
      });
      // Her bir ekstra için ayrı line
      for (const item of (modularPackage.items || [])) {
        const extraQ = Number(packageExtras[item.product_id] || 0);
        if (extraQ <= 0 || !item.extra_allowed) continue;
        const price = Number(item.product?.extra_price || 0);
        const cur   = item.product?.currency || 'EUR';
        const itemLocalized = (tr ? (item.product?.name_tr || item.product?.name) : (item.product?.name_en || item.product?.name)) || '—';
        lines.push({
          product_id:         `organizer-extra-${item.product_id}`,
          product_name:       `${itemLocalized} (${tr ? 'Ekstra' : 'Extra'})`,
          product_image:      item.product?.thumb_url || null,
          calculation_type:   'quantity_based',
          unit_price:         price,
          line_total:         price * extraQ,
          currency:           cur,
          quantity:           extraQ,
          supplier_tenant_id: 'organizer',
          notes:              tr ? `Modüler paket ekstra kalemi (${modularPackage.area_m2} m²)` : `Modular package extra item (${modularPackage.area_m2} m²)`,
        });
      }
      await apiClient.post(`/customer/cart/add-lines`, { lines });
      toast.success(tr ? 'Paket sepete eklendi.' : 'Package added to cart.');
      setPackageExtras({});
      // Cart badge'i yenilensin (main component'in fetchCart fonksiyonu global scope'ta değil;
      // sayfa yenilemesi yerine window event ile tetikle)
      window.dispatchEvent(new CustomEvent('cop31:cart-refresh'));
    } catch (err) {
      toast.error(err.response?.data?.detail || (tr ? 'Sepete eklenemedi.' : 'Failed to add to cart.'));
    } finally {
      setAddingPackage(false);
    }
  };

  const fetchDesigns = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/stand-designs/my`);
      setDesigns(r.data || []);
      const total = (r.data || []).reduce((s, d) => s + (d.unread_count || 0), 0);
      onUnreadChange?.(total);
    } catch {}
    finally { setLoading(false); }
  };

  const fetchMessages = async (designId) => {
    try {
      const r = await apiClient.get(`/stand-designs/my/${designId}/messages`);
      setMessages(r.data || []);
    } catch {}
  };

  const sendMessage = async () => {
    if (!msgText.trim() && !msgFile) return;
    setMsgLoading(true);
    try {
      const fd = new FormData();
      fd.append('message', msgText);
      if (msgFile) fd.append('attachment', msgFile);
      const r = await apiClient.post(`/stand-designs/my/${selectedDesignRef.current.id}/messages`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessages(prev => [...prev, r.data]);
      setMsgText(''); setMsgFile(null);
    } catch {}
    finally { setMsgLoading(false); }
  };

  const handleSubmit = async () => {
    if (designFiles.length === 0) {
      setSubmitError(tr ? 'Lütfen en az bir tasarım dosyası yükleyin.' : 'Please upload at least one design file.');
      return;
    }
    // Phase 86AF — Notes alanı için sert üst sınır. Backend bazı kayıtlarda
    // çok uzun HTML payload'ları işlerken zaman aşımına uğruyordu (kullanıcı
    // raporu: "Submitting…" ekranında takılıyor). Kullanıcının istediği gibi
    // 5000 karakterlik plaintext sınırı koyuyoruz; bu hem UX hem güvenli
    // bir backstop sağlıyor.
    const NOTES_PLAIN_MAX = 5000;
    const plainNotes = (notes || '').replace(/<[^>]+>/g, '').trim();
    if (plainNotes.length > NOTES_PLAIN_MAX) {
      setSubmitError(tr
        ? `Notlar en fazla ${NOTES_PLAIN_MAX} karakter olabilir (şu an ${plainNotes.length}).`
        : `Notes can be at most ${NOTES_PLAIN_MAX} characters (currently ${plainNotes.length}).`);
      return;
    }
    setSubmitLoading(true); setSubmitError('');
    try {
      const fd = new FormData();
      // Auto-generated title from stand info
      const autoTitle = standInfo?.stand_no
        ? `Stand ${standInfo.stand_no} – ${tr ? 'Anahtar Teslim Stand Üretim Talebi' : 'Turnkey Stand Production Request'}`
        : (tr ? 'Stand Üretim Talebi' : 'Stand Production Request');
      fd.append('title', autoTitle);
      fd.append('description', notes);
      // Stand info from server (not from user input)
      // Phase 86AG — Backend `/customer/my-layout` sometimes returns the
      // hall/area at the top level (`hall`, `total_area`) and sometimes
      // nested under `layout.*`. Try both shapes so the design submission
      // never ends up with empty Hall / Area on the admin detail page.
      const hallName  = standInfo?.hall_name  || standInfo?.hall || standInfo?.layout?.hall_name || '';
      const standNoF  = standInfo?.stand_no   || standInfo?.layout?.stand_no  || '';
      const areaVal   = standInfo?.total_area || standInfo?.layout?.total_area || null;
      if (hallName) fd.append('hall', hallName);
      if (standNoF) fd.append('stand_no', standNoF);
      if (areaVal)  fd.append('stand_area', areaVal);
      designFiles.forEach(f => fd.append('design_files', f));
      // Phase 86AZ.27 — Send technical files in their own bucket so the
      // backend stores them under `technical_files` (independent versioning).
      technicalFiles.forEach(f => fd.append('technical_files', f));
      const r = await apiClient.post(`/stand-designs`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90_000,
      });
      setDesigns(prev => [r.data, ...prev]);
      setShowForm(false);
      setNotes('');
      setDesignFiles([]);
      setTechnicalFiles([]);
    } catch (e) {
      setSubmitError(e.response?.data?.detail || (tr ? 'Bir hata oluştu.' : 'An error occurred.'));
    } finally { setSubmitLoading(false); }
  };

  const DESIGN_STATUS_MAP = {
    submitted:    { label: tr ? 'Gönderildi' : 'Submitted',    color: 'bg-blue-100 text-blue-700' },
    under_review: { label: tr ? 'İnceleniyor' : 'Under Review', color: 'bg-purple-100 text-purple-700' },
    rfq_sent:     { label: tr ? 'Teklif Alınıyor' : 'RFQ Sent', color: 'bg-amber-100 text-amber-700' },
    bid_received: { label: tr ? 'Teklif Geldi' : 'Bid Received', color: 'bg-orange-100 text-orange-700' },
    awarded:      { label: tr ? 'Yüklenici Belirlendi' : 'Awarded', color: 'bg-emerald-100 text-emerald-700' },
  };

  if (loading) return (
    <div className="flex justify-center py-12">
      <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full" />
    </div>
  );

  if (selectedDesign) return (
    <div className="space-y-4">
      {/* Header */}
      <button onClick={() => setSelectedDesign(null)} className="flex items-center gap-2 text-sm text-orange-600 hover:underline font-medium">
        ← {tr ? 'Taleplerime Dön' : 'Back to Requests'}
      </button>

      <div className="flex items-center justify-between bg-white rounded-2xl border border-gray-100 px-6 py-4">
        <div>
          <h2 className="font-bold text-gray-800 text-base">{selectedDesign.title}</h2>
          {selectedDesign.description && selectedDesign.description !== '<p></p>' && (
            <div
              className="text-xs text-gray-500 mt-1 max-w-xl
                [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_p]:my-0.5
                [&_strong]:font-bold [&_em]:italic [&_s]:line-through"
              dangerouslySetInnerHTML={{ __html: selectedDesign.description }}
            />
          )}
        </div>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ml-4 ${DESIGN_STATUS_MAP[selectedDesign.status]?.color || 'bg-gray-100 text-gray-600'}`}>
          {DESIGN_STATUS_MAP[selectedDesign.status]?.label || selectedDesign.status}
        </span>
      </div>

      {/* Phase 86AZ.34 — Sekme: Stand Brief (dosyalar) | Sohbet */}
      <div className="flex gap-2 border-b border-gray-200 pb-0 overflow-x-auto">
        {[
          { id: 'info', label: tr ? 'Stand Brief' : 'Stand Brief' },
          { id: 'chat', label: tr ? 'Sohbet' : 'Chat',
            badge: (selectedDesign?.unread_count || 0) > 0 ? selectedDesign.unread_count : null },
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            data-testid={`customer-design-tab-${tab.id}`}
            onClick={() => setDesignDetailTab(tab.id)}
            className={`relative px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex-shrink-0 flex items-center gap-1.5 ${
              designDetailTab === tab.id ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.badge && (
              <span className="h-4 min-w-4 px-1 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* BRIEF/FILES TAB */}
      {designDetailTab === 'info' && (
      <div className="space-y-4">

        {/* Files (full width) */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          {/* Tab switcher */}
          <div className="flex items-center gap-2 mb-4 border-b border-gray-100 -mx-5 px-5">
            {[
              { key: 'design',    labelTr: 'Tasarım Dosyaları',  labelEn: 'Design Files',    count: selectedDesign.design_files?.length || 0,    theme: 'orange' },
              { key: 'technical', labelTr: 'Teknik Dosyalar',    labelEn: 'Technical Files', count: selectedDesign.technical_files?.length || 0, theme: 'blue' },
            ].map(t => {
              const active = filesTab === t.key;
              const themeCls = t.theme === 'orange'
                ? (active ? 'text-orange-600 border-orange-500' : 'text-gray-400 hover:text-orange-600 border-transparent')
                : (active ? 'text-blue-600 border-blue-500' : 'text-gray-400 hover:text-blue-600 border-transparent');
              const chipCls = t.theme === 'orange'
                ? (active ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500')
                : (active ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500');
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`design-files-tab-${t.key}`}
                  onClick={() => setFilesTab(t.key)}
                  className={`flex items-center gap-2 px-3 pb-2.5 -mb-px border-b-2 text-sm font-semibold transition-colors ${themeCls}`}
                >
                  <Paperclip className="h-3.5 w-3.5" />
                  {tr ? t.labelTr : t.labelEn}
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-5 px-1.5 rounded-full text-[10px] font-bold ${chipCls}`}>
                    {t.count}/5
                  </span>
                </button>
              );
            })}
          </div>

          {(() => {
            const currentField = filesTab === 'technical' ? 'technical_files' : 'design_files';
            const currentCounter = filesTab === 'technical' ? 'next_technical_version_no' : 'next_version_no';
            const isDesign = filesTab === 'design';
            const theme = isDesign
              ? { txt: 'text-orange-400', txtStrong: 'text-orange-500', border: 'border-orange-200', borderHover: 'hover:border-orange-400', bgHover: 'hover:bg-orange-50', badge: 'bg-orange-500', spinner: 'border-orange-400', addEmptyText: tr ? 'Tasarım dosyası ekle (PDF/PPT/PPTX)' : 'Add design file (PDF/PPT/PPTX)' }
              : { txt: 'text-blue-400',   txtStrong: 'text-blue-500',   border: 'border-blue-200',   borderHover: 'hover:border-blue-400',   bgHover: 'hover:bg-blue-50',   badge: 'bg-blue-500',   spinner: 'border-blue-400',   addEmptyText: tr ? 'Teknik dosya ekle (DWG/PDF/PPT)' : 'Add technical file (DWG/PDF/PPT)' };
            const files = selectedDesign[currentField] || [];
            const canAdd = files.length < 5;
            const sorted = [...files].sort((a, b) => (a.version || 0) - (b.version || 0));
            // Display version: file.version (set by backend) or fallback to index+1 for legacy.
            const renderVerBadge = (f, idx) => {
              const v = f.version || (idx + 1);
              return (
                <span
                  data-testid={`design-file-version-${v}`}
                  title={f.uploaded_at ? new Date(f.uploaded_at).toLocaleString(tr ? 'tr-TR' : 'en-US') : ''}
                  className={`absolute top-1.5 right-1.5 text-[10px] font-extrabold text-white ${theme.badge} px-1.5 py-0.5 rounded-md shadow-sm tracking-wide`}
                >
                  V{v}
                </span>
              );
            };

            // Hidden input for "Add new file" — sends to whichever bucket is active.
            const AddInput = (
              <input
                ref={addFileRef}
                type="file" className="hidden" accept=".pdf,.ppt,.pptx"
                onChange={e => { handleAddFile(e.target.files?.[0], filesTab); e.target.value = ''; }}
              />
            );

            if (sorted.length === 0) {
              return (
                <div>
                  {AddInput}
                  <button
                    type="button"
                    data-testid="design-file-add-empty-btn"
                    disabled={!canAdd || addFileLoading}
                    onClick={() => addFileRef.current?.click()}
                    className={`w-full border-2 border-dashed ${theme.border} ${theme.borderHover} ${theme.bgHover} rounded-2xl py-7 text-center transition-colors disabled:opacity-40`}
                  >
                    {addFileLoading ? (
                      <span className={`inline-block animate-spin h-5 w-5 border-2 ${theme.spinner} border-t-transparent rounded-full`} />
                    ) : (
                      <>
                        <p className={`text-2xl ${theme.txt} font-bold`}>+</p>
                        <p className="text-xs text-gray-500 mt-1">{theme.addEmptyText}</p>
                        <p className="text-[10px] text-gray-300 mt-0.5">{tr ? 'İlki V1 olarak eklenir' : 'First upload becomes V1'}</p>
                      </>
                    )}
                  </button>
                </div>
              );
            }

            return (
              <>
                {AddInput}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {sorted.map((f, idx) => {
                const isPdf = f.filename?.endsWith('.pdf') || f.original_name?.endsWith('.pdf');
                const fileUrl = `${window.location.origin}/api/uploads/${f.filename}`;
                const isActing = fileActionLoading === f.filename;
                return (
                  <div key={f.filename} className={`relative group border border-gray-200 rounded-xl p-2.5 bg-gray-50 ${isDesign ? 'hover:border-orange-300 hover:bg-orange-50' : 'hover:border-blue-300 hover:bg-blue-50'} transition-all`}>
                    {renderVerBadge(f, idx)}
                    {/* Hidden replace input */}
                    <input
                      ref={el => replaceRefs.current[f.filename] = el}
                      type="file" className="hidden" accept=".pdf,.ppt,.pptx"
                      onChange={e => { handleReplaceFile(f.filename, e.target.files?.[0], filesTab); e.target.value = ''; }}
                    />
                    {/* File icon + name */}
                    <div className="flex flex-col gap-1 mb-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${isPdf ? 'bg-red-100 text-red-600' : (isDesign ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600')}`}>
                        {isPdf ? 'PDF' : 'PPT'}
                      </div>
                      <p className="text-xs text-gray-700 font-medium leading-tight line-clamp-2 break-all">{f.original_name}</p>
                      {f.size && <p className="text-xs text-gray-400">{fmtSize(f.size)}</p>}
                    </div>
                    {/* Actions */}
                    {isActing ? (
                      <div className="flex justify-center py-1">
                        <span className={`animate-spin h-4 w-4 border-2 ${theme.spinner} border-t-transparent rounded-full`} />
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 mt-auto">
                        {/* Preview */}
                        <button
                          onClick={() => isPdf ? setPreviewFile({url: fileUrl, name: f.original_name}) : window.open(fileUrl, '_blank')}
                          title={tr ? 'Önizle' : 'Preview'}
                          className={`flex-1 flex items-center justify-center h-6 rounded-lg bg-white border border-gray-200 ${isDesign ? 'hover:bg-orange-50 hover:border-orange-300' : 'hover:bg-blue-50 hover:border-blue-300'} transition-colors`}
                        >
                          <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                        </button>
                        {/* Replace */}
                        <button
                          onClick={() => replaceRefs.current[f.filename]?.click()}
                          title={tr ? 'Değiştir' : 'Replace'}
                          className="flex-1 flex items-center justify-center h-6 rounded-lg bg-white border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        >
                          <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => handleDeleteFile(f.filename, filesTab)}
                          title={tr ? 'Sil' : 'Delete'}
                          className="flex-1 flex items-center justify-center h-6 rounded-lg bg-white border border-gray-200 hover:bg-red-50 hover:border-red-300 transition-colors"
                        >
                          <svg className="h-3 w-3 text-gray-400 hover:text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              {canAdd && (
                <button
                  type="button"
                  data-testid="design-file-add-btn"
                  disabled={addFileLoading}
                  onClick={() => addFileRef.current?.click()}
                  className={`border-2 border-dashed ${theme.border} ${theme.borderHover} ${theme.bgHover} rounded-xl p-2.5 flex flex-col items-center justify-center min-h-[120px] transition-colors disabled:opacity-40`}
                >
                  {addFileLoading ? (
                    <span className={`inline-block animate-spin h-5 w-5 border-2 ${theme.spinner} border-t-transparent rounded-full`} />
                  ) : (
                    <>
                      <p className={`text-2xl ${theme.txt} font-bold`}>+</p>
                      <p className="text-[10px] text-gray-500 mt-0.5">{tr ? 'Yeni Dosya' : 'Add File'}</p>
                      <p className={`text-[10px] ${theme.txtStrong} font-bold mt-0.5`}>V{(selectedDesign[currentCounter] || sorted.reduce((m,f)=>Math.max(m,f.version||0),0)) + 1}</p>
                    </>
                  )}
                </button>
              )}
            </div>
              </>
            );
          })()}
        </div>

        {/* Phase 86AZ.34 — End of Brief/Files tab */}
        </div>
      )}

      {/* CHAT TAB */}
      {designDetailTab === 'chat' && (
        <div className="bg-white rounded-2xl border border-gray-100 flex flex-col overflow-hidden max-w-2xl" style={{minHeight: 420}}>

          {/* Chat Header */}
          <ChatHeader tr={tr} />

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1 bg-gray-50/40" style={{maxHeight: 320}}>
            {/* System welcome message — always shown first */}
            <WelcomeMessage tr={tr} />

            {messages.length === 0 && (
              <div className="flex justify-center py-4">
                <span className="text-xs text-gray-400 bg-white border border-gray-100 rounded-full px-3 py-1">
                  {tr ? 'Henüz mesaj yok — ilk mesajı siz gönderin.' : 'No messages yet — send the first one.'}
                </span>
              </div>
            )}

            {messages.map((msg, idx) => {
              const isMe = msg.sender_role === 'customer_admin';
              const prevMsg = messages[idx - 1];
              const showDateDivider = !prevMsg || new Date(msg.created_at).toDateString() !== new Date(prevMsg.created_at).toDateString();
              const msgLang = detectLang(msg.message);
              const useTr = msgLang === 'tr' || (msgLang === 'unknown' && tr);
              return (
                <React.Fragment key={msg.id}>
                  {showDateDivider && (
                    <div className="flex items-center gap-3 py-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-gray-400 font-medium whitespace-nowrap">
                        {formatMsgDate(msg.created_at, useTr)}
                      </span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                  )}
                  <div className={`flex items-end gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                    {!isMe && (
                      <div className="h-7 w-7 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center flex-shrink-0 mb-0.5 shadow-sm">
                        <span className="text-white text-xs font-bold">SF</span>
                      </div>
                    )}
                    <div className={`max-w-[78%] group`}>
                      {!isMe && <p className="text-xs font-semibold text-orange-600 mb-1 ml-1">{msg.sender_name || 'COP 31'}</p>}
                      <div className={`px-4 py-2.5 rounded-2xl shadow-sm text-sm leading-relaxed
                        ${isMe
                          ? 'bg-orange-500 text-white rounded-br-none'
                          : 'bg-white border border-gray-100 text-gray-800 rounded-bl-none'}`}>
                        <p>{msg.message}</p>
                        {msg.attachment && (() => {
                          const fileUrl = `${window.location.origin}/api/uploads/${msg.attachment.filename}`;
                          const ext = ((msg.attachment.original_name || msg.attachment.filename || '').split('.').pop() || '').toLowerCase();
                          const isImage = ['png','jpg','jpeg','gif','webp','svg','bmp','heic','heif'].includes(ext);
                          const isPdf = ext === 'pdf';
                          const isPpt = ['ppt','pptx'].includes(ext);
                          // Phase 86AI — Inline preview for chat attachments.
                          //   image  → tıklanınca tam ekran modal
                          //   pdf/ppt → tıklanınca iframe modal
                          //   diğer  → indirme linki (excel, word, zip…)
                          if (isImage) {
                            return (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); setPreviewFile({ url: fileUrl, name: msg.attachment.original_name, type: 'image' }); }}
                                className="block mt-2 rounded-xl overflow-hidden border border-white/30 hover:opacity-90 transition-opacity"
                                data-testid={`chat-img-${msg.id || ''}`}
                              >
                                <img src={fileUrl} alt={msg.attachment.original_name} className="max-h-48 w-auto object-cover" />
                              </button>
                            );
                          }
                          if (isPdf || isPpt) {
                            return (
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); setPreviewFile({ url: fileUrl, name: msg.attachment.original_name, type: isPdf ? 'pdf' : 'ppt' }); }}
                                className={`flex items-center gap-1.5 mt-2 text-xs rounded-lg px-2 py-1.5 ${isMe ? 'bg-orange-400/50 text-orange-100 hover:bg-orange-400' : 'bg-orange-50 text-orange-600 hover:bg-orange-100'} transition-colors`}
                                data-testid={`chat-doc-${msg.id || ''}`}
                              >
                                <FileText className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{msg.attachment.original_name}</span>
                                <span className="text-[10px] uppercase opacity-70">{ext}</span>
                              </button>
                            );
                          }
                          return (
                            <a href={fileUrl} download target="_blank" rel="noreferrer"
                              className={`flex items-center gap-1.5 mt-2 text-xs rounded-lg px-2 py-1.5 ${isMe ? 'bg-orange-400/50 text-orange-100 hover:bg-orange-400' : 'bg-orange-50 text-orange-600 hover:bg-orange-100'} transition-colors`}>
                              <Paperclip className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{msg.attachment.original_name}</span>
                              <span className="text-[10px] uppercase opacity-70">{ext || 'file'}</span>
                            </a>
                          );
                        })()}
                      </div>
                      <div className={`flex items-center gap-1 mt-1 px-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <span className="text-xs text-gray-400">
                          {new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isMe && (
                          <svg className="h-3 w-3 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="px-4 py-3 border-t border-gray-100 bg-white">
            {msgFile && (
              <div className="flex items-center gap-2 mb-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-xl">
                <Paperclip className="h-3 w-3 text-orange-500" />
                <span className="text-xs flex-1 truncate text-gray-700">{msgFile.name}</span>
                <button onClick={() => setMsgFile(null)} className="text-gray-400 hover:text-red-500"><X className="h-3 w-3" /></button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input ref={msgFileRef} type="file" className="hidden"
                onChange={e => { setMsgFile(e.target.files?.[0] || null); e.target.value = ''; }} />
              <button onClick={() => msgFileRef.current?.click()}
                className="h-9 w-9 flex items-center justify-center rounded-xl text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-colors flex-shrink-0">
                <Paperclip className="h-4 w-4" />
              </button>
              <input data-testid="design-msg-input" type="text" value={msgText}
                onChange={e => setMsgText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:bg-white transition-all"
                placeholder={tr ? 'Mesajınızı yazın...' : 'Type your message...'} />
              <button data-testid="design-send-msg" onClick={sendMessage} disabled={msgLoading}
                className="h-9 w-9 flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white rounded-xl flex-shrink-0 transition-colors shadow-sm disabled:opacity-50">
                {msgLoading
                  ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 86AI — File Preview Modal (image / pdf / ppt). */}
      {previewFile && (() => {
        // Type fallback: design brief PDF'leri eski kullanım (type yok) → uzantıdan çıkar.
        let resolvedType = previewFile.type;
        if (!resolvedType) {
          const ext = ((previewFile.name || previewFile.url || '').split('.').pop() || '').toLowerCase();
          if (['png','jpg','jpeg','gif','webp','svg','bmp','heic','heif'].includes(ext)) resolvedType = 'image';
          else if (ext === 'pdf') resolvedType = 'pdf';
          else if (['ppt','pptx'].includes(ext)) resolvedType = 'ppt';
          else resolvedType = 'pdf'; // güvenli default → iframe
        }
        return (
          <div
            className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setPreviewFile(null)}
            data-testid="chat-preview-modal"
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPreviewFile(null); }}
              className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur-md"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="absolute top-4 left-4 max-w-[60%] truncate text-white text-sm font-semibold bg-black/40 px-3 py-1.5 rounded-full">
              {previewFile.name}
            </div>
            {resolvedType === 'image' ? (
              <img
                src={previewFile.url}
                alt={previewFile.name}
                className="max-h-[90vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            ) : resolvedType === 'pdf' ? (
              <iframe
                src={previewFile.url}
                title={previewFile.name}
                className="bg-white rounded-2xl shadow-2xl"
                style={{ width: 'min(95vw, 1200px)', height: '90vh' }}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              // ppt/pptx — Google docs embedded viewer
              <iframe
                src={`https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(previewFile.url)}`}
                title={previewFile.name}
                className="bg-white rounded-2xl shadow-2xl"
                style={{ width: 'min(95vw, 1200px)', height: '90vh' }}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <a
              href={previewFile.url}
              download
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="absolute bottom-6 right-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 hover:bg-white/30 text-white text-xs font-semibold backdrop-blur-md"
            >
              <Paperclip className="h-3.5 w-3.5" />
              {tr ? 'İndir' : 'Download'}
            </a>
          </div>
        );
      })()}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Success Modal — shown right after a stand area request is submitted */}
      {requestSuccessModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="stand-request-success-modal">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[85vh]">
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 px-6 py-6 text-white text-center flex-shrink-0">
              <div className="h-14 w-14 mx-auto rounded-full bg-white/20 backdrop-blur flex items-center justify-center mb-3">
                <CheckCircle2 className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-bold text-xl mb-1">
                {tr ? 'Talebiniz Alındı!' : 'Request Submitted!'}
              </h3>
              <p className="text-emerald-50 text-sm">
                {tr
                  ? 'Organizatör en kısa sürede talebinizi inceleyecek ve size yer ataması yapacaktır.'
                  : 'The organizer will review your request and allocate your stand shortly.'}
              </p>
            </div>
            <div className="px-6 py-5 space-y-3 overflow-y-auto flex-1">
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-medium">{tr ? 'Tasarım' : 'Design'}</span>
                  <span className="font-bold text-gray-900">
                    {requestSuccessModal.designType === 'modular' ? (tr ? 'Modüler' : 'Modular') : (tr ? 'Özel Tasarım' : 'Custom Design')}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-medium">{tr ? 'Alan' : 'Area'}</span>
                  <span className="font-bold text-gray-900">{requestSuccessModal.area} m²</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-medium">{tr ? 'Stand Tipi' : 'Layout'}</span>
                  <span className="font-bold text-gray-900 capitalize">{requestSuccessModal.layout}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                  <span className="text-gray-500 font-medium">{tr ? 'Talep Tarihi' : 'Submitted'}</span>
                  <span className="font-bold text-gray-900">{requestSuccessModal.submittedAt}</span>
                </div>
              </div>
              {requestSuccessModal.notes && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 max-h-40 overflow-y-auto">
                  <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider mb-1 sticky top-0 bg-indigo-50 pb-1">
                    {tr ? 'Eklenen Not' : 'Your Note'}
                  </p>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{requestSuccessModal.notes}</p>
                </div>
              )}
            </div>
            <div className="px-6 pb-5 flex-shrink-0">
              <button
                onClick={() => setRequestSuccessModal(null)}
                data-testid="success-modal-close"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl text-sm shadow-md shadow-indigo-200 transition-colors"
              >
                {tr ? 'Tamam' : 'Got it'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Phase 86AA — STRAY MODAL REMOVED FROM WRONG COMPONENT.
          See CustomerDashboard component for the actual modal. */}

      {/* Note Modal — view + edit + resubmit the customer note */}
      {noteModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="note-modal" onClick={() => { if (!savingNote) { setNoteModalOpen(null); setEditingNote(false); }}}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-indigo-600" />
                <h3 className="font-bold text-gray-900 text-base">
                  {editingNote ? (tr ? 'Notunuzu Düzenle' : 'Edit Your Note') : (tr ? 'Stand Talebi Notunuz' : 'Your Stand Request Note')}
                </h3>
              </div>
              <button
                onClick={() => { if (!savingNote) { setNoteModalOpen(null); setEditingNote(false); }}}
                disabled={savingNote}
                className="text-gray-400 hover:text-gray-700"
                data-testid="note-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {!editingNote ? (
                <>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed" data-testid="note-modal-text">
                    {noteModalOpen.text || (tr ? '(Boş not)' : '(Empty note)')}
                  </p>
                  {noteModalOpen.updatedAt && (
                    <p className="text-[11px] text-gray-400 mt-3">
                      {tr ? 'Son güncelleme:' : 'Last updated:'}{' '}
                      {new Date(noteModalOpen.updatedAt).toLocaleString(tr ? 'tr-TR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <textarea
                    value={draftNote}
                    onChange={(e) => setDraftNote(e.target.value.slice(0, 1000))}
                    rows={10}
                    placeholder={tr ? 'Notunuzu buraya yazın...' : 'Write your note here...'}
                    className="w-full text-sm rounded-xl border border-gray-200 px-3 py-3 focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 resize-y min-h-[220px]"
                    data-testid="note-modal-textarea"
                  />
                  <p className="text-[10px] text-gray-400 mt-1 text-right">{draftNote.length} / 1000</p>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-2 bg-gray-50">
              {!editingNote ? (
                <>
                  <button
                    onClick={() => { setNoteModalOpen(null); setEditingNote(false); }}
                    className="text-sm font-bold text-gray-600 hover:text-gray-800 px-4 py-2"
                  >
                    {tr ? 'Kapat' : 'Close'}
                  </button>
                  <button
                    onClick={() => {
                      setDraftNote(noteModalOpen.text || '');
                      setEditingNote(true);
                    }}
                    data-testid="note-modal-edit-btn"
                    className="text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl shadow-md shadow-indigo-200 transition-colors inline-flex items-center gap-1.5"
                  >
                    <FileText className="h-4 w-4" />
                    {tr ? 'Notu Düzenle' : 'Edit Note'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setEditingNote(false)}
                    disabled={savingNote}
                    className="text-sm font-bold text-gray-600 hover:text-gray-800 px-4 py-2 disabled:opacity-50"
                  >
                    {tr ? 'İptal' : 'Cancel'}
                  </button>
                  <button
                    onClick={async () => {
                      setSavingNote(true);
                      try {
                        await apiClient.put(`/customer/stand-request/notes`, { stand_request_notes: draftNote });
                        toast.success(tr ? 'Notunuz güncellendi ve organizatöre iletildi.' : 'Your note was updated and sent to the organizer.');
                        // Refresh profile so the summary reflects the new text + updated_at
                        fetchOnboardingProfile && fetchOnboardingProfile();
                        setNoteModalOpen({ text: draftNote, updatedAt: new Date().toISOString() });
                        setEditingNote(false);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || (tr ? 'Not güncellenemedi.' : 'Failed to update note.'));
                      } finally {
                        setSavingNote(false);
                      }
                    }}
                    disabled={savingNote}
                    data-testid="note-modal-save-btn"
                    className="text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl shadow-md shadow-indigo-200 disabled:opacity-60 inline-flex items-center gap-1.5"
                  >
                    {savingNote ? (tr ? 'Kaydediliyor...' : 'Saving...') : (
                      <>
                        <Send className="h-4 w-4" />
                        {tr ? 'Notu Gönder' : 'Send Note'}
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Hammer className="h-6 w-6 text-orange-500" />
            {tr ? 'Stand Üretim Talepleri' : 'Stand Production Requests'}
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            {tr ? 'Tasarım dosyanızı yükleyin, yüklenicilerden fiyat teklifi alın.' : 'Upload your design files and receive price quotes from contractors.'}
          </p>
        </div>
        {standInfo?.stand_no && (
          <button data-testid="new-design-btn" onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors">
            <Plus className="h-4 w-4" />
            {tr ? 'Özel Stand Talep Et' : 'Request a Custom Stand'}
          </button>
        )}
      </div>

      {/* ── Approved-Stand Info OR Onboarding Stand Preferences ──
          Phase 86AD — Once the area is approved, the onboarding-time
          "what would you like" preferences are no longer relevant; instead
          show the actual allocated stand details (number, hall, area, open
          sides, approval date) so the customer always sees what they have. */}
      {((areaApprovalStatus === 'approved') || (myLayout && myLayout.stand_no)) ? (
        <div
          className="bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-50 border border-emerald-200 rounded-2xl p-5"
          data-testid="approved-stand-summary"
        >
          {(() => {
            // Phase 86AX — When the customer has explicitly clicked "I Approve
            // My Stand Area" the status flips from "ALLOCATED" to "APPROVED"
            // everywhere in the customer-facing dashboard.
            const isApproved = (areaApprovalStatus === 'approved') || (parentLayout && parentLayout.area_approval_status === 'approved');
            const titleText = isApproved
              ? (tr ? 'Onaylı Stand Bilgileriniz' : 'Your Approved Stand')
              : (tr ? 'Standınız Tahsis Edildi' : 'Your Stand Allocated');
            const descText = isApproved
              ? (tr ? 'Stand alanınız onaylandı; aşağıda kesinleşmiş bilgileri görüyorsunuz.' : 'Your stand area is approved; the finalised details are shown below.')
              : (tr ? 'Süper Yönetici tarafından standınız tahsis edildi; onaylamak için stand alanınızı görüntüleyin.' : 'Your stand has been allocated by the Super Admin; review your stand area to confirm.');
            const badgeText = isApproved ? (tr ? 'ONAYLANDI' : 'APPROVED') : (tr ? 'TAHSİS EDİLDİ' : 'ALLOCATED');
            return (
          <div className="flex items-start justify-between mb-4 gap-3">
            <div>
              <p className="text-xs font-bold text-emerald-700 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                {titleText}
              </p>
              <p className="text-xs text-gray-500">{descText}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                data-testid="review-stand-area-btn"
                onClick={() => navigate(`/musteri-admin/${slug}/layout`)}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-white border border-emerald-300 hover:bg-emerald-100 hover:border-emerald-400 px-3 py-1.5 rounded-full transition-colors shadow-sm"
              >
                {tr ? 'Stand Alanını Görüntüle' : 'Review Your Stand Area'}
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>
              </button>
              <span className="text-[10px] font-bold bg-emerald-600 text-white px-2.5 py-1 rounded-full tracking-wider">{badgeText}</span>
            </div>
          </div>
            );
          })()}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-xl p-3 border border-emerald-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                {tr ? 'Stand No' : 'Stand No'}
              </p>
              <p className="font-bold text-emerald-800 text-sm">
                {(myLayout && myLayout.stand_no) || '—'}
              </p>
            </div>
            <div className="bg-white rounded-xl p-3 border border-emerald-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                {tr ? 'Salon' : 'Hall'}
              </p>
              <p className="font-bold text-gray-800 text-sm truncate">
                {(myLayout && (myLayout.hall_name || myLayout.hall)) || '—'}
              </p>
            </div>
            <div className="bg-white rounded-xl p-3 border border-emerald-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                {tr ? 'Alan' : 'Area'}
              </p>
              <p className="font-bold text-gray-800 text-sm">
                {(() => {
                  // Backend response is flattened (`total_area` at root) but
                  // older clients may also store nested `layout.total_area`.
                  // Try both before falling back to "—".
                  const a = (myLayout && (myLayout.total_area || myLayout?.layout?.total_area)) || 0;
                  return a ? `${a} m²` : '—';
                })()}
              </p>
            </div>
            <div className="bg-white rounded-xl p-3 border border-emerald-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                {tr ? 'Tasarım' : 'Design'}
              </p>
              <p className="font-bold text-gray-800 text-sm capitalize">
                {onboardingProfile?.stand_design_type === 'modular'
                  ? (tr ? 'Modüler' : 'Modular')
                  : onboardingProfile?.stand_design_type === 'custom'
                    ? (tr ? 'Özel' : 'Custom')
                    : '—'}
              </p>
            </div>
          </div>
        </div>
      ) : onboardingProfile && (onboardingProfile.stand_design_type || onboardingProfile.stand_area || onboardingProfile.stand_shape) ? (
        <div
          className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50 border border-blue-200 rounded-2xl p-5"
          data-testid="onboarding-stand-summary"
        >
          <div className="flex items-start justify-between mb-4 gap-3">
            <div>
              <p className="text-xs font-bold text-blue-700 uppercase tracking-widest mb-1 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-600 inline-block" />
                {tr ? 'Onboarding Stand Tercihleriniz' : 'Your Onboarding Stand Preferences'}
              </p>
              <p className="text-xs text-gray-500">
                {tr
                  ? 'Onboarding sırasında seçtiğiniz stand tercihleri aşağıda özetlenmiştir (sadece görüntüleme).'
                  : 'A read-only summary of the stand preferences you selected during onboarding.'}
              </p>
            </div>
            <span className="text-[10px] font-bold bg-white text-blue-700 border border-blue-200 px-2.5 py-1 rounded-full tracking-wider">
              {tr ? 'SALT OKUNUR' : 'READ-ONLY'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Stand Design Type */}
            <div className="bg-white rounded-xl p-3 border border-blue-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                {tr ? 'Tasarım Türü' : 'Design Type'}
              </p>
              <p className="font-bold text-gray-800 text-sm capitalize">
                {onboardingProfile.stand_design_type === 'modular'
                  ? (tr ? 'Modüler Hazır' : 'Modular')
                  : onboardingProfile.stand_design_type === 'custom'
                    ? (tr ? 'Custom Design' : 'Custom Design')
                    : '—'}
              </p>
            </div>
            {/* Stand Area */}
            <div className="bg-white rounded-xl p-3 border border-blue-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                {tr ? 'Talep Edilen Alan' : 'Requested Area'}
              </p>
              <p className="font-bold text-gray-800 text-sm">
                {onboardingProfile.stand_area ? `${onboardingProfile.stand_area} m²` : '—'}
              </p>
            </div>
            {/* Stand Shape */}
            <div className="bg-white rounded-xl p-3 border border-blue-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                {tr ? 'Stand Tipi' : 'Stand Type'}
              </p>
              <p className="font-bold text-gray-800 text-sm capitalize">
                {(() => {
                  const s = onboardingProfile.stand_shape;
                  if (!s) return '—';
                  const map = tr
                    ? { island: 'Ada', peninsula: 'Yarımada', linear: 'Linear', corner: 'Köşe' }
                    : { island: 'Island', peninsula: 'Peninsula', linear: 'Linear', corner: 'Corner' };
                  return map[s] || s;
                })()}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Stand Request Form (only shown when area NOT yet approved) ──
          Phase 86AD — Once the area is approved, the new "Your Approved
          Stand" card above is the sole source of truth; the redundant
          mini-success card was removed to declutter the page. */}
      {!((areaApprovalStatus === 'approved') || (myLayout && myLayout.stand_no)) && (
      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="stand-request-form-card">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
              <Package className="h-5 w-5 text-indigo-600" />
              {tr ? 'Stand Alanı Talebi' : 'Stand Area Request'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {tr
                ? 'Onboarding tercihiniz aşağıda — istediğiniz zaman değiştirebilir ve organizatörden yer ataması isteyebilirsiniz.'
                : 'Your onboarding preference is shown below — change it any time and request allocation from the organizer.'}
            </p>
          </div>
          {!editingAreaRequest && requestStatus === 'none' && (
            <button
              onClick={() => {
                setAreaReqForm({
                  stand_area: onboardingProfile?.stand_area || '',
                  stand_design_type: onboardingProfile?.stand_design_type || '',
                  stand_layout_type: onboardingProfile?.stand_layout_type || '',
                  stand_request_notes: onboardingProfile?.stand_request_notes || '',
                });
                setDesignPdfFile(null);
                setEditingAreaRequest(true);
              }}
              data-testid="edit-stand-request-btn"
              className="text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-4 py-2 shadow-md shadow-indigo-200"
            >
              {tr ? 'Talep Et' : 'Request'}
            </button>
          )}
          {!editingAreaRequest && requestStatus === 'pending' && (
            <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 inline-flex items-center gap-1.5" data-testid="stand-request-status-pending">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
              {tr ? 'Talep Gönderildi' : 'Request Submitted'}
            </span>
          )}
          {!editingAreaRequest && requestStatus === 'under_review' && (
            <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 inline-flex items-center gap-1.5" data-testid="stand-request-status-review">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
              {tr ? 'İnceleniyor' : 'Under Review'}
            </span>
          )}
          {!editingAreaRequest && requestStatus === 'allocated' && (
            <span className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1.5" data-testid="stand-request-status-allocated">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {tr ? 'Atandı' : 'Allocated'}
            </span>
          )}
        </div>

        {/* Submitted request summary (visible whenever there's a submission) */}
        {!editingAreaRequest && requestStatus !== 'none' && formattedSubmittedAt && (
          <div className={`rounded-xl p-4 mb-4 border ${
            requestStatus === 'allocated' ? 'bg-emerald-50 border-emerald-200' :
            requestStatus === 'under_review' ? 'bg-blue-50 border-blue-200' :
            'bg-amber-50 border-amber-200'
          }`} data-testid="submitted-request-summary">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
              <div>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                  {tr ? 'Tasarım' : 'Design'}
                </p>
                <p className="font-bold text-gray-900 text-sm">
                  {onboardingProfile?.stand_design_type === 'modular' ? (tr ? 'Modüler' : 'Modular')
                    : onboardingProfile?.stand_design_type === 'custom' ? (tr ? 'Özel Tasarım' : 'Custom Design')
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                  {tr ? 'Alan' : 'Area'}
                </p>
                <p className="font-bold text-gray-900 text-sm">
                  {onboardingProfile?.stand_area ? `${onboardingProfile.stand_area} m²` : '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                  {tr ? 'Stand Tipi' : 'Layout'}
                </p>
                <p className="font-bold text-gray-900 text-sm capitalize">
                  {onboardingProfile?.stand_layout_type || '—'}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                  {tr ? 'Talep Tarihi' : 'Submitted'}
                </p>
                <p className="font-bold text-gray-900 text-sm">
                  {formattedSubmittedAt}
                </p>
              </div>
            </div>

            {/* Customer note — opens modal so long notes don't blow up the page */}
            {onboardingProfile?.stand_request_notes && (
              <button
                type="button"
                onClick={() => setNoteModalOpen({
                  text: onboardingProfile.stand_request_notes,
                  updatedAt: onboardingProfile.stand_request_notes_updated_at,
                })}
                data-testid="open-note-modal-btn"
                className="w-full flex items-center justify-between bg-white/60 hover:bg-white border border-gray-200 hover:border-indigo-300 rounded-lg p-3 mb-3 transition-colors group"
              >
                <div className="flex items-center gap-2 min-w-0 text-left">
                  <MessageSquare className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-0.5">
                      {tr ? 'Notunuz' : 'Your Note'}
                    </p>
                    <p className="text-sm text-gray-800 truncate">{onboardingProfile.stand_request_notes}</p>
                  </div>
                </div>
                <span className="text-xs font-bold text-indigo-600 group-hover:text-indigo-800 flex-shrink-0 ml-2">
                  {tr ? 'Tam Notu Gör' : 'View Full Note'} →
                </span>
              </button>
            )}

            {/* Status timeline */}
            <div className="flex items-center gap-2 text-xs pt-3 border-t border-gray-200/60">
              <Step done={true}                                            label={tr ? 'Gönderildi' : 'Submitted'}    sub={formattedSubmittedAt} />
              <Connector active={requestStatus !== 'pending'} />
              <Step done={requestStatus === 'under_review' || requestStatus === 'allocated'} label={tr ? 'İnceleniyor' : 'Under Review'} sub={formattedReviewedAt || (tr ? 'Bekleniyor' : 'Pending')} pulse={requestStatus === 'under_review'} />
              <Connector active={requestStatus === 'allocated'} />
              <Step done={requestStatus === 'allocated'}                   label={tr ? 'Atandı' : 'Allocated'}          sub={requestStatus === 'allocated' ? (myLayout?.hall_name ? `${myLayout.hall_name} · #${myLayout.stand_no}` : `#${myLayout?.stand_no}`) : (tr ? 'Bekleniyor' : 'Pending')} />
            </div>
          </div>
        )}

        {editingAreaRequest ? (
          <div className="space-y-5">
            {/* STEP 1: Design type */}
            <div>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                {tr ? '1. Tasarım Tipi' : '1. Choose Your Stand Construction Type'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'modular', tr: 'Modüler Hazır Stand', en: 'Modular Ready Stand', desc_tr: 'Hazır paketlerden seçin', desc_en: 'Pick from ready packages' },
                  { id: 'custom',  tr: 'Özel Tasarım',        en: 'Custom Design',       desc_tr: 'Kendi tasarımınızı yükleyin', desc_en: 'Upload your own design' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setAreaReqForm(f => ({ ...f, stand_design_type: opt.id, stand_area: '' }))}
                    data-testid={`area-request-design-${opt.id}`}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${areaReqForm.stand_design_type === opt.id ? 'bg-indigo-50 border-indigo-500 ring-2 ring-indigo-200' : 'bg-white border-gray-200 hover:border-indigo-300'}`}
                  >
                    <p className="font-bold text-gray-900">{tr ? opt.tr : opt.en}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{tr ? opt.desc_tr : opt.desc_en}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* STEP 2: Area (m² pills) — depends on design type */}
            {areaReqForm.stand_design_type && (
              <div>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                  {tr ? '2. Alan (m²)' : '2. Area (m²)'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {(areaReqForm.stand_design_type === 'modular' ? availableAreas : availableCustomAreas).map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setAreaReqForm(f => ({ ...f, stand_area: String(m) }))}
                      data-testid={`area-pill-${m}`}
                      className={`px-4 py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${String(areaReqForm.stand_area) === String(m) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'}`}
                    >
                      {m} m²
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 3: Stand Layout Type */}
            {areaReqForm.stand_design_type && areaReqForm.stand_area && (
              <div>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                  {tr ? '3. Stand Tipi' : '3. Stand Layout'}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { id: 'island',    tr: 'Ada', en: 'Island', emoji: '◽',
                      desc_tr: '4 tarafı açık', desc_en: '4 sides open' },
                    { id: 'peninsula', tr: 'Yarımada', en: 'Peninsula', emoji: '◧',
                      desc_tr: '3 tarafı açık', desc_en: '3 sides open' },
                    { id: 'corner',    tr: 'Köşe', en: 'Corner', emoji: '◩',
                      desc_tr: '2 tarafı açık', desc_en: '2 sides open' },
                    { id: 'linear',    tr: 'Lineer', en: 'Linear', emoji: '▬',
                      desc_tr: '1 tarafı açık', desc_en: '1 side open' },
                  ].map(opt => {
                    const active = areaReqForm.stand_layout_type === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setAreaReqForm(f => ({ ...f, stand_layout_type: opt.id }))}
                        data-testid={`layout-pill-${opt.id}`}
                        className={`py-3 rounded-xl text-sm font-bold border-2 transition-all ${active ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300'}`}
                      >
                        <span className="block text-lg">{opt.emoji}</span>
                        {tr ? opt.tr : opt.en}
                        <span className={`block mt-0.5 text-[10px] font-semibold ${active ? 'text-white/80' : 'text-red-500'}`}>
                          {tr ? opt.desc_tr : opt.desc_en}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP 4: Optional notes — location preferences, special requests, etc. */}
            {areaReqForm.stand_design_type && areaReqForm.stand_area && areaReqForm.stand_layout_type && (
              <div>
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                  {tr ? '4. Not (İsteğe Bağlı)' : '4. Notes (Optional)'}
                </p>
                <textarea
                  value={areaReqForm.stand_request_notes || ''}
                  onChange={(e) => setAreaReqForm(f => ({ ...f, stand_request_notes: e.target.value.slice(0, 1000) }))}
                  rows={8}
                  placeholder={tr
                    ? 'Yer seçimine ilişkin özel talepleriniz (örn. "Ana giriş yakını tercih edilir", "X firmasının yanına yerleştirin", "Köşe yer mümkünse")...'
                    : 'Special requests about stand location (e.g. "Prefer near main entrance", "Place next to company X", "Corner spot if possible")...'}
                  className="w-full text-sm rounded-xl border border-gray-200 px-3 py-3 focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 resize-y min-h-[180px]"
                  data-testid="stand-request-notes-input"
                />
                <p className="text-[10px] text-gray-400 mt-1 text-right">
                  {(areaReqForm.stand_request_notes || '').length} / 1000
                </p>
              </div>
            )}

            {/* STEP 5 (custom) PDF upload is deferred — user first submits area
                request; after admin allocates a stand, a separate upload card
                appears below the allocation banner. */}

            <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
              <button
                onClick={async () => {
                  if (!areaReqForm.stand_design_type) { toast.error(tr ? 'Tasarım tipini seçin.' : 'Select design type.'); return; }
                  if (!areaReqForm.stand_area) { toast.error(tr ? 'Alan seçin.' : 'Select area.'); return; }
                  if (!areaReqForm.stand_layout_type) { toast.error(tr ? 'Stand tipini seçin.' : 'Select stand layout.'); return; }
                  setSavingAreaReq(true);
                  try {
                    await apiClient.put(`/customer/stand-request`, areaReqForm);
                    window.dispatchEvent(new CustomEvent('cop31:onboarding-refresh'));
                    setEditingAreaRequest(false);
                    setDesignPdfFile(null);
                    setRequestSuccessModal({
                      submittedAt: new Date().toLocaleString(tr ? 'tr-TR' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
                      designType: areaReqForm.stand_design_type,
                      area: areaReqForm.stand_area,
                      layout: areaReqForm.stand_layout_type,
                      notes: areaReqForm.stand_request_notes,
                    });
                    // Refresh profile so summary card picks up the new submitted_at + status
                    fetchOnboardingProfile && fetchOnboardingProfile();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || (tr ? 'Talep gönderilemedi.' : 'Failed to submit.'));
                  } finally {
                    setSavingAreaReq(false);
                  }
                }}
                disabled={savingAreaReq}
                data-testid="submit-stand-request-btn"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl text-sm shadow-md shadow-indigo-200 disabled:opacity-60"
              >
                {savingAreaReq
                  ? (tr ? 'Gönderiliyor...' : 'Submitting...')
                  : (tr ? 'Alan Talebi Gönder' : 'Submit Area Request')}
              </button>
              <button
                onClick={() => { setEditingAreaRequest(false); setDesignPdfFile(null); }}
                disabled={savingAreaReq}
                className="px-4 py-3 rounded-xl text-sm font-semibold bg-white border border-gray-200 hover:bg-gray-50 text-gray-700"
              >
                {tr ? 'Vazgeç' : 'Cancel'}
              </button>
            </div>
          </div>
        ) : requestStatus === 'none' ? (
          <div className="text-center py-6 px-4 rounded-xl bg-gray-50 border border-dashed border-gray-200" data-testid="no-request-yet">
            <Package className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm font-bold text-gray-700">
              {tr ? 'Henüz Talep Göndermediniz' : 'No Request Submitted Yet'}
            </p>
            <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
              {tr
                ? 'Modüler veya özel tasarım stand alanı talebinizi göndermek için yukarıdaki "Talep Et" butonunu kullanın.'
                : 'Use the "Request" button above to submit your modular or custom-design stand area request.'}
            </p>
          </div>
        ) : null}

        {/* Allocation status pill — emerald only on success; pending state is now
            in the timeline above, so we hide the duplicate amber banner. */}
        {myLayout?.stand_no && (
          <div className="mt-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-start gap-2" data-testid="allocation-status-assigned">
              <div className="h-2 w-2 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold text-emerald-900">
                  {tr ? '🎉 Tebrikler — Stand Yeriniz Atandı!' : '🎉 Congratulations — Your Stand is Allocated!'}
                </p>
                <p className="text-xs text-emerald-700">
                  {myLayout.hall_name ? `${myLayout.hall_name} · ` : ''}Stand #{myLayout.stand_no}{myLayout.total_area ? ` · ${myLayout.total_area} m²` : ''}
                </p>
                <p className="text-xs text-emerald-700 mt-1">
                  {tr ? 'Artık stand siparişlerinizi oluşturabilir ve sepete ekleyebilirsiniz.' : 'You may now place stand orders and add items to your cart.'}
                </p>
                <button
                  type="button"
                  onClick={() => navigate(`/musteri-admin/${slug}/layout`)}
                  data-testid="view-stand-layout-btn"
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-900 bg-white hover:bg-emerald-100 border border-emerald-300 rounded-lg px-3 py-1.5 transition-colors"
                >
                  <Search className="h-3.5 w-3.5" />
                  {tr ? 'Stand Görünümünü Aç' : 'View Stand Layout'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Post-allocation Custom Design PDF upload */}
        {myLayout?.stand_no && onboardingProfile?.stand_design_type === 'custom' && (
          <div className="mt-4 bg-indigo-50/60 border border-indigo-200 rounded-xl p-4" data-testid="custom-design-pdf-upload-card">
            <div className="flex items-start gap-2 mb-3">
              <Package className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-indigo-900">
                  {tr ? 'Özel Tasarım Dosyasını Yükle' : 'Upload Custom Design File'}
                </p>
                <p className="text-xs text-indigo-700/80 mt-0.5">
                  {onboardingProfile?.custom_design_pdf_name
                    ? (tr
                        ? `Yüklenen dosya: ${onboardingProfile.custom_design_pdf_name}. Yeni bir dosya yükleyerek değiştirebilirsiniz.`
                        : `Uploaded file: ${onboardingProfile.custom_design_pdf_name}. Upload a new file to replace it.`)
                    : (tr
                        ? 'Hazır tasarımınızı PDF olarak yükleyin (max 25MB). Yalnızca tasarımı yapılmış stand için fiyatlandırma sunuyoruz.'
                        : 'Upload your pre-made design as PDF (max 25MB). We only provide pricing for ready-designed stands.')}
                </p>
              </div>
            </div>
            <label className="block border-2 border-dashed border-indigo-300 hover:border-indigo-500 rounded-xl px-4 py-5 text-center cursor-pointer bg-white hover:bg-indigo-50 transition-colors">
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 25 * 1024 * 1024) {
                    toast.error(tr ? 'Dosya 25MB\'ı aşamaz.' : 'File exceeds 25MB.');
                    return;
                  }
                  try {
                    setUploadingPdf(true);
                    const fd = new FormData();
                    fd.append('file', f);
                    await apiClient.post(`/customer/stand-request/design-pdf`, fd, {
                      headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    window.dispatchEvent(new CustomEvent('cop31:onboarding-refresh'));
                    toast.success(tr ? 'Tasarım dosyanız yüklendi.' : 'Your design file was uploaded.');
                  } catch (err) {
                    toast.error(err.response?.data?.detail || (tr ? 'Yükleme başarısız.' : 'Upload failed.'));
                  } finally {
                    setUploadingPdf(false);
                  }
                }}
                className="hidden"
                data-testid="post-allocation-design-pdf-input"
                disabled={uploadingPdf}
              />
              <Package className="h-7 w-7 text-indigo-500 mx-auto mb-1" />
              <p className="text-sm font-semibold text-indigo-700">
                {uploadingPdf
                  ? (tr ? 'Yükleniyor...' : 'Uploading...')
                  : onboardingProfile?.custom_design_pdf_name
                    ? (tr ? 'Farklı Bir PDF Yükle' : 'Replace with Another PDF')
                    : (tr ? 'PDF Seç veya Sürükle' : 'Select or drop PDF')}
              </p>
            </label>
          </div>
        )}
      </div>
      )}

      {/* ── Modular Stand Package (only when user chose modular + admin defined it) ──
          Phase 86AD — "Browse All Modular Stands" button removed; the
          dedicated "Modular Stands" tab now owns that browsing flow. The
          inline catalog below stays available only if it was already toggled
          open. */}

      {onboardingProfile?.stand_design_type === 'modular' && showStandsCatalog && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="modular-stands-inline-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-gray-900 text-base">
                {tr ? 'Modüler Stand Seçenekleri' : 'Modular Stand Options'}
              </h3>
              <p className="text-xs text-gray-500">
                {tr
                  ? `Onboarding'de seçtiğiniz ${onboardingProfile.stand_area || '—'} m² boyut "Size Tavsiye" olarak işaretlenmiştir.`
                  : `The ${onboardingProfile.stand_area || '—'} m² option you picked in onboarding is marked "Recommended".`}
              </p>
            </div>
          </div>
          <ModularStandsCatalog
            tr={tr}
            recommendedArea={onboardingProfile?.stand_area}
          />
        </div>
      )}


      {/* No stand assigned warning */}
      {!standInfo?.stand_no && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800 text-sm">
              {tr ? 'Stand atamanız henüz yapılmamış' : 'No stand assigned yet'}
            </p>
            <p className="text-amber-600 text-xs mt-1">
              {tr ? 'Fiyat talebi oluşturabilmek için önce stand düzeni atanması gerekiyor. Lütfen organizatörle iletişime geçin.' : 'A stand layout must be assigned before you can request a quote. Please contact the organizer.'}
            </p>
          </div>
        </div>
      )}

      {/* New Request Form */}
      {showForm && standInfo && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
          <h3 className="font-bold text-gray-800 flex items-center gap-2">
            <Upload className="h-5 w-5 text-orange-500" />
            {tr ? 'Fiyat Talebi Oluştur' : 'Create Quote Request'}
          </h3>

          {/* Phase 86AE — "Stand Info (Auto-filled)" sub-card removed: the
              same Hall / Stand No / Area details are already shown in the
              dominant "Your Approved Stand" card right above this form, so
              repeating them was clutter. */}

          {/* Design File Upload */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
              {tr ? 'Tasarım Dosyaları' : 'Design Files'}
              <span className="text-red-500">*</span>
            </label>
            <input ref={designFileRef} type="file" className="hidden"
              accept=".pdf,.ppt,.pptx"
              multiple
              onChange={e => {
                const picked = Array.from(e.target.files || []);
                if (!picked.length) return;
                const allowed = ['.pdf', '.ppt', '.pptx'];
                let errors = [];
                const valid = picked.filter(f => {
                  const ext = '.' + f.name.split('.').pop().toLowerCase();
                  if (!allowed.includes(ext)) { errors.push(`${f.name}: Sadece PDF ve PPT/PPTX yüklenebilir.`); return false; }
                  if (f.size > 100 * 1024 * 1024) { errors.push(`${f.name}: 100 MB sınırını aşıyor.`); return false; }
                  return true;
                });
                if (errors.length) setSubmitError(errors[0]);
                else setSubmitError('');
                setDesignFiles(prev => {
                  const combined = [...prev, ...valid];
                  return combined.slice(0, 5);
                });
                e.target.value = '';
              }} />
            {designFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-xl">
                <Paperclip className="h-4 w-4 text-orange-500 flex-shrink-0" />
                <span className="text-sm flex-1 truncate text-gray-700">{f.name}</span>
                <span className="text-xs text-gray-400">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                <button onClick={() => setDesignFiles(p => p.filter((_, j) => j !== i))} className="ml-1">
                  <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
                </button>
              </div>
            ))}
            {designFiles.length < 5 && (
              <button
                data-testid="design-browse-files"
                type="button"
                onClick={() => designFileRef.current?.click()}
                className="w-full flex items-center justify-center gap-3 p-4 border-2 border-dashed border-orange-300 rounded-xl text-orange-600 hover:bg-orange-50 text-sm font-medium transition-colors"
              >
                <Upload className="h-4 w-4" />
                <span>{tr ? 'Dosya Seç' : 'Browse Files'}</span>
                <span className="text-xs text-gray-400 font-normal">
                  {tr ? '(PDF, PPT — maks. 100 MB, 5 dosya)' : '(PDF, PPT — max 100 MB, 5 files)'}
                </span>
              </button>
            )}
          </div>

          {/* Phase 86AZ.27 — Technical Files (DWG/PDF/PPT). Tracked
              independently from Design Files so production and creative
              briefs can be audited separately. Optional (not required). */}
          <div className="space-y-2" data-testid="technical-files-section">
            <label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
              {tr ? 'Teknik Dosyalar' : 'Technical Files'}
              <span className="text-[10px] font-normal text-gray-400 ml-1">
                {tr ? '(opsiyonel)' : '(optional)'}
              </span>
            </label>
            <input ref={technicalFileRef} type="file" className="hidden"
              accept=".pdf,.ppt,.pptx"
              multiple
              onChange={e => {
                const picked = Array.from(e.target.files || []);
                if (!picked.length) return;
                const allowed = ['.pdf', '.ppt', '.pptx'];
                let errors = [];
                const valid = picked.filter(f => {
                  const ext = '.' + f.name.split('.').pop().toLowerCase();
                  if (!allowed.includes(ext)) { errors.push(`${f.name}: ${tr ? 'Sadece PDF ve PPT/PPTX yüklenebilir.' : 'Only PDF / PPT / PPTX allowed.'}`); return false; }
                  if (f.size > 100 * 1024 * 1024) { errors.push(`${f.name}: 100 MB ${tr ? 'sınırını aşıyor' : 'limit exceeded'}.`); return false; }
                  return true;
                });
                if (errors.length) setSubmitError(errors[0]);
                else setSubmitError('');
                setTechnicalFiles(prev => {
                  const combined = [...prev, ...valid];
                  return combined.slice(0, 5);
                });
                e.target.value = '';
              }} />
            {technicalFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl">
                <Paperclip className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <span className="text-sm flex-1 truncate text-gray-700">{f.name}</span>
                <span className="text-xs text-gray-400">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                <button onClick={() => setTechnicalFiles(p => p.filter((_, j) => j !== i))} className="ml-1">
                  <X className="h-4 w-4 text-gray-400 hover:text-red-500" />
                </button>
              </div>
            ))}
            {technicalFiles.length < 5 && (
              <button
                data-testid="technical-browse-files"
                type="button"
                onClick={() => technicalFileRef.current?.click()}
                className="w-full flex items-center justify-center gap-3 p-4 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 hover:bg-blue-50 text-sm font-medium transition-colors"
              >
                <Upload className="h-4 w-4" />
                <span>{tr ? 'Teknik Dosya Seç' : 'Browse Technical Files'}</span>
                <span className="text-xs text-gray-400 font-normal">
                  {tr ? '(DWG/PDF/PPT — maks. 100 MB, 5 dosya)' : '(DWG/PDF/PPT — max 100 MB, 5 files)'}
                </span>
              </button>
            )}
            <p className="text-[11px] text-gray-400 leading-relaxed">
              {tr
                ? 'Mühendislik çizimleri, teknik şartname veya üretim detaylarını içeren dosyalar buraya yüklenir. Tasarım dosyalarından bağımsız takip edilir.'
                : 'Engineering drawings, technical specs or production details go here. Tracked independently from design files.'}
            </p>
          </div>

          {/* Notes — Phase 86AE: minHeight bumped + resize-y enabled so the
              user can stretch the editor when describing complex briefs.
              Phase 86AF: live character counter (plaintext, max 5000). */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">{tr ? 'Notlar / Özel İstekler' : 'Notes / Special Requests'}</label>
              {(() => {
                const plain = (notes || '').replace(/<[^>]+>/g, '').length;
                const over = plain > 5000;
                return (
                  <span className={`text-[11px] font-mono ${over ? 'text-red-600 font-bold' : 'text-gray-400'}`} data-testid="notes-char-counter">
                    {plain.toLocaleString()} / 5,000
                  </span>
                );
              })()}
            </div>
            <div className="rich-text-resizable">
              <RichTextEditor
                value={notes}
                onChange={setNotes}
                placeholder={tr ? 'Tasarım hakkında özel isteklerinizi, malzeme tercihlerinizi veya teknik detayları buraya yazın...' : 'Describe special requests, material preferences or technical details...'}
                minHeight={280}
              />
            </div>
          </div>

          {submitError && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 px-4 py-2.5 rounded-xl border border-red-200">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />{submitError}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button onClick={() => { setShowForm(false); setSubmitError(''); setNotes(''); setDesignFiles([]); setTechnicalFiles([]); }}
              className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:border-orange-300 transition-colors">
              {tr ? 'İptal' : 'Cancel'}
            </button>
            <button data-testid="design-submit-btn" onClick={handleSubmit} disabled={submitLoading}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
              {submitLoading
                ? <><span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{tr ? 'Gönderiliyor...' : 'Submitting...'}</>
                : <><Send className="h-4 w-4" />{tr ? 'Fiyat Talebini Gönder' : 'Send Quote Request'}</>}
            </button>
          </div>
        </div>
      )}

      {/* Designs List */}
      {designs.length === 0 && !showForm ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <Hammer className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">{tr ? 'Henüz fiyat talebi oluşturmadınız.' : 'No quote requests yet.'}</p>
          {standInfo?.stand_no && (
            <p className="text-gray-400 text-sm mt-1">{tr ? '"Fiyat Talebi Oluştur" butonu ile başlayın.' : 'Click "Request a Quote" to get started.'}</p>
          )}
        </div>
      ) : designs.map(d => (
        <div key={d.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-orange-200 transition-colors cursor-pointer"
          onClick={() => setSelectedDesign(d)}>
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-800 truncate">{d.title}</p>
                {d.unread_count > 0 && (
                  <span className="flex-shrink-0 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 bg-orange-500 text-white text-xs font-bold rounded-full animate-pulse">
                    {d.unread_count}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {d.hall && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Hall: {d.hall}</span>}
                {d.stand_no && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Stand: {d.stand_no}</span>}
                {d.stand_area && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{d.stand_area} m²</span>}
                {d.design_files?.length > 0 && (
                  <span className="text-xs bg-orange-50 text-orange-600 px-2 py-0.5 rounded-full">
                    {d.design_files.length} {tr ? 'tasarım' : 'design'}
                  </span>
                )}
                {d.technical_files?.length > 0 && (
                  <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                    {d.technical_files.length} {tr ? 'teknik' : 'technical'}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 ml-3">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${DESIGN_STATUS_MAP[d.status]?.color || 'bg-gray-100 text-gray-600'}`}>
                {DESIGN_STATUS_MAP[d.status]?.label || d.status}
              </span>
              <button data-testid={`design-open-${d.id}`}
                className="bg-orange-50 hover:bg-orange-100 text-orange-600 border border-orange-200 rounded-xl px-3 py-1.5 text-xs font-medium transition-colors flex-shrink-0">
                {tr ? 'Aç' : 'Open'}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Helper components for the Stand Request status timeline ──
function Step({ done, label, sub, pulse }) {
  return (
    <div className="flex flex-col items-center text-center flex-1 min-w-0">
      <div className={`h-7 w-7 rounded-full flex items-center justify-center mb-1 ${
        done ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-400'
      } ${pulse ? 'ring-4 ring-blue-200 animate-pulse' : ''}`}>
        {done ? <Check className="h-3.5 w-3.5" /> : <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />}
      </div>
      <p className={`text-[10px] font-bold uppercase tracking-wider ${done ? 'text-gray-800' : 'text-gray-400'} truncate w-full`}>{label}</p>
      {sub && <p className="text-[9px] text-gray-400 truncate w-full leading-tight">{sub}</p>}
    </div>
  );
}

function Connector({ active }) {
  return <div className={`h-0.5 flex-1 mx-1 ${active ? 'bg-emerald-500' : 'bg-gray-200'} self-start mt-3.5`} />;
}
